// DispatchDeck - Pre-Trip (DVIR) Module
// Vehicle inspections with defect tracking

import {
  db,
  storage,
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  limit,
  onSnapshot,
  Timestamp,
  ref,
  uploadBytes,
  getDownloadURL,
  functions,      // TASK-9691 — completeDvirRepairSignoff callable
  httpsCallable,
} from '../firebase-config.js?v=1868';
// runTransaction is not re-exported from firebase-config.js — import it directly
// so this module doesn't force a firebase-config cache-buster rebump. Used for
// the zero-floored deployedQty decrement (TASK-1255).
import { runTransaction, serverTimestamp } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
import { processPhoto, generateThumbnail, validateImageFile } from '../utils/imageProcessor.js?v=1955';
import { toDrawablePhoto, photoErrorMessage, createPhotoPrepTracker, settlePhotoPrepBeforeSave } from '../utils/heicConvert.js?v=3';
import { STARTER_LIMITS } from '../config/planLimits.js?v=1763';
import * as photoQueue from '../utils/photoQueue.js?v=1810';
import { initJSA, getJSATabHTML, cleanupJSA } from './jsa.js?v=10024004'; // TASK-9581 today-first order (jsaListWindow.js 9581002) · TASK-9581 JSA archive collapsed + last-day window + crew/job filters (jsaListWindow.js 9581001) · TASK-10024 non-filer self sign-in on a filed template sheet (signJsaCrewLine template branch) · integ/2113: no crew-lead proxy signing (Chris 10/22) · TASK-10024 crew Sign on a filed Standard JSA via signJsaCrewLine + edit-form signatures routed through it + split save-refused toast · TASK-10011 startForJob (office + Job Board) waits for first jobs/templates snapshots, late job prefill, prefill day crew, return to office Job Details · TASK-9996 JSA hospital search fixed (valid Places types, GPS+text via searchText, errors shown) ·TASK-9714 JSA link signers on print + driver signing link + pad/list fixes ·TASK-9548 slice 2 labeled job picker groups ·TASK-9511 JSA template default + phone footer menu + Cancel back to the job · P0 vendors-not-customers (JSA customer picker) · TASK-9548 s3 grouped customer picker · TASK-9694 S6a crew customer reads → customerDirectory
// TASK-9741 — side-effect module: attaches window.InspectionTemplates (base DOT
// lists + per type / per unit checklist resolution). Generated from
// functions/src/shared/inspectionTemplates.js by scripts/build-shared.cjs.
import '../shared/inspection-templates.js?v=10135001'; // TASK-10135 unitMode add/replace, item phase, conflicts
import '../utils/recordDate.js?v=9923'; // TASK-9923 DVIR date = company calendar, never UTC

// ============================================
// PRETRIP STATE
// ============================================
const PretripState = {
  inspections: [],          // Live subscription: pretrips with createdAt >= 30 days ago
  olderInspections: [],     // Loaded on demand via "Load older" button (one-shot reads)
  _olderLoading: false,
  _olderExhausted: false,   // true once a load returns fewer docs than the page size
  _olderPageSize: 50,
  // TASK-4712 — bulk select. ARCHIVE ONLY, deliberately: see bulkSetArchived().
  _selectMode: false,
  _selected: new Set(),
  _bulkBusy: false,
  people: [],
  trucks: [],
  trailers: [],
  assets: [],
  returnInspections: [],  // Pending return inspections for rental assets
  unsubscribers: [],
  editingId: null,
  searchQuery: '',
  activeReturnInspection: null,  // Currently detected return inspection for selected asset
  pendingPhotos: [],  // { id, blob, thumbBlob, thumbUrl, isDefectPhoto }
  // TASK-9397 COD — bumped on every inspection-form open (showNewInspection)
  // and close (closeModal). A Save that waited on photo conversion aborts
  // when it changed, so it never persists a different form opened meanwhile.
  inspectionFormGen: 0,
  pendingFluids: [],  // { fluidType, amount, unit, oilBrand?, oilWeight? }
  _pretripsLoaded: false,
  // TASK-9372 — a New Inspection opened from a Job Board job stamps these on
  // the record (null otherwise). Cleared on standalone New, on cancel/close and
  // after save, never left armed for the next inspection.
  prefillJobId: null,
  prefillJobDayId: null,
  _prefillSeq: 0,
  // TASK-9372 — docs loaded by id for the detail view (a crewmate's inspection
  // opened from the job modal is outside this viewer's driverId-scoped list).
  // Read-only lookup cache; never merged into the list arrays. Scoped to ONE
  // tenant + viewer (`key`); `gen` bumps on every reset so an in-flight
  // getDoc from before a reset / session change is dropped when it lands.
  _byIdCache: { key: '', gen: 0, docs: {} },
  _byIdReq: 0,
  // TASK-9405 — repair state of the maintenance tasks each mechanic-signed
  // defect DVIR spawned (source:'pretrip', sourceId === dvir.id). One live
  // listener per such DVIR, never a tenant-wide read. dvirId →
  // { state: 'loading' | 'ok' | 'error', open, total }. Read by
  // dvirDefectClosure(); anything but 'ok' keeps the DVIR OPEN (fail closed).
  _linkedTasks: new Map(),
  _linkedTaskUnsubs: new Map(),
  // TASK-9405 (COD) — bumped by the view cleanup. Async work that started
  // before a teardown (a Load-older page in flight) checks it before it starts
  // listeners again, so leaving the view cannot leave listeners running.
  _viewGen: 0,
  _refreshTimer: null
};

// TASK-9372 (COD) — the identity the by-id cache belongs to. A different
// tenant OR a different signed-in viewer (PIN person, member uid) = a
// different session, and the cache resets before it is read or written.
function _byIdCacheKey() {
  const a = window.AppState || {};
  return `${a.tenant || ''}|${a.driverPersonId || a.user?.uid || a.memberAccess?.uid || ''}`;
}
function _resetByIdCache() {
  const gen = (PretripState._byIdCache?.gen || 0) + 1;
  PretripState._byIdCache = { key: _byIdCacheKey(), gen, docs: {} };
}
function _byIdCacheDocs() {
  if (!PretripState._byIdCache || PretripState._byIdCache.key !== _byIdCacheKey()) _resetByIdCache();
  return PretripState._byIdCache.docs;
}

// TASK-9372 — every read-only "find this inspection" path: the loaded list
// first, then a doc fetched by id for the detail view (this tenant only).
function _findInspectionById(inspectionId) {
  const live = getAllInspections().find(i => i.id === inspectionId);
  if (live) return live;
  const cached = _byIdCacheDocs()[inspectionId];
  return cached && cached._tenant === (window.AppState?.tenant || 'demo') ? cached : null;
}

// Combined live + on-demand-loaded list used by render / search / counts. Older
// pages are appended after live docs in createdAt-desc order — both are sorted
// desc by createdAt so the concatenation stays ordered.
function getAllInspections() {
  return PretripState.inspections.concat(PretripState.olderInspections);
}

// TASK-9659 — this unit's meter rows for the shared plausibility check
// (window.PmStatus.pmCheckReading). null = could not read them (skip the check).
async function getMeterRows(tenantId, truckId) {
  if (!tenantId || !truckId) return null;
  try {
    const snap = await getDocs(query(collection(db, 'tenants', tenantId, 'metersLog'), where('truckId', '==', truckId)));
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.warn('[Pretrip] meter rows unreadable; plausibility check skipped:', error?.message || error);
    return null;
  }
}

// ============================================
// INITIALIZE PRETRIP VIEW
// ============================================
export function initPretrip(container, tenantId) {
  console.log('[Pretrip] Initializing for tenant:', tenantId);
  _resetByIdCache(); // TASK-9372 — never carry a by-id doc across a view/session

  // Guard against null tenant
  if (!tenantId) {
    console.error('[Pretrip] No tenant ID provided');
    container.innerHTML = `
      <div class="empty-state">
        <div class="empty-state-icon">⚠️</div>
        <div class="empty-state-title">Account Setup Issue</div>
        <div class="empty-state-text">
          Unable to load Inspections. Please try logging out and back in.
        </div>
        <button class="btn btn-primary" onclick="window.handleLogout()">
          <span class="material-icons">logout</span> Log Out
        </button>
      </div>
    `;
    return () => {};
  }

  PretripState._pretripsLoaded = false;

  const isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || (window.AppState?.driverModePin === true);
  const isFounderPublicAccess = window.AppState?.isPublicAccess && window.AppState?.tenant === 'trippin-trucking';
  // TASK-932 — the embedded Job Board tab was founder-tenant-only. Now any office
  // session holding the jobboard module gets it (checkModuleAccess is the nav gate).
  const officeJobBoardTab = !isDriverMode &&
    typeof window.checkModuleAccess === 'function' && window.checkModuleAccess('jobboard', true) === true;

  if (isDriverMode && !isFounderPublicAccess) {
    // Driver mode: DVIR + JSA tabs (no Job Board).
    // TASK-377 2026-06-04 — removed the top "Daily Jobs" back-link; the generic
    // bottom-nav (now reliably shown on Inspections via app.js doNavigateToView)
    // is the canonical way back, so the redundant top button is gone.
    // TASK-437 — with the optional Safety + PPE tabs the bar can hold 4 items;
    // widen max-width to 460px (still phone-safe; roster-tabs flex-wraps if
    // narrower) so 4 short tabs don't crush. 2-3 tabs still center fine.
    container.innerHTML = `
      <div class="dvir-tab-bar roster-tabs" style="margin: 12px auto 20px; max-width: 460px;">
        <button class="roster-tab active" id="dvir-tab-inspections" onclick="window.PretripModule.switchDvirTab('inspections')">
          <span class="material-icons">checklist</span>
          <span>Inspections</span>
        </button>
        <button class="roster-tab" id="dvir-tab-jsa" onclick="window.PretripModule.switchDvirTab('jsa')">
          <span class="material-icons">health_and_safety</span>
          <span>JSA</span>
        </button>
        ${window.AppState?.settings?.safetyMeetingSignInEnabled === true ? `
        <!-- TASK-342 Slice B/D revisit — Safety Meeting tab. Tap routes
             through PretripModule.openSafetyMeetingSignIn which delegates to
             the existing modal builder in jobboard.js. No panel content
             needed; the tab is a button-style entry into the signature pad. -->
        <button class="roster-tab" id="dvir-tab-safety" onclick="window.PretripModule.openSafetyMeetingSignIn()">
          <span class="material-icons">how_to_reg</span>
          <span>Safety</span>
        </button>` : ''}
        ${window.AppState?.settings?.safetyEquipmentDriverEnabled === true ? `
        <!-- TASK-437 Slice 2 — PPE sign-off tab. Like the Safety tab, this is a
             button-style entry into a modal (jobboard.js openPpeModal) — no panel
             content. Identity-locked to the PIN driver; lets them sign off on
             admin-issued gear + self-log items they grabbed off the shelf. -->
        <button class="roster-tab" id="dvir-tab-ppe" onclick="window.PretripModule.openPpe()">
          <span class="material-icons">shield</span>
          <span>PPE</span>
        </button>` : ''}
      </div>
      <div id="inspections-panel" class="dvir-tab-panel active">
        ${getPretripHTML(true)}
      </div>
      ${getJSATabHTML()}
    `;
  } else if (officeJobBoardTab) {
    // Office session with the jobboard module: DVIR + JSA + Job Board tabs
    container.innerHTML = `
      <div class="dvir-tab-bar roster-tabs" style="margin: 12px auto 20px; max-width: 500px;">
        <button class="roster-tab active" id="dvir-tab-inspections" onclick="window.PretripModule.switchDvirTab('inspections')">
          <span class="material-icons">checklist</span>
          <span>Inspections</span>
        </button>
        <button class="roster-tab" id="dvir-tab-jsa" onclick="window.PretripModule.switchDvirTab('jsa')">
          <span class="material-icons">health_and_safety</span>
          <span>JSA</span>
        </button>
        ${window.AppState?.settings?.safetyMeetingSignInEnabled === true ? `
        <!-- TASK-342 Slice B/D revisit — Safety Meeting tab. Tap routes
             through PretripModule.openSafetyMeetingSignIn which delegates to
             the existing modal builder in jobboard.js. No panel content
             needed; the tab is a button-style entry into the signature pad. -->
        <button class="roster-tab" id="dvir-tab-safety" onclick="window.PretripModule.openSafetyMeetingSignIn()">
          <span class="material-icons">how_to_reg</span>
          <span>Safety</span>
        </button>` : ''}
        <button class="roster-tab" id="dvir-tab-jobboard" onclick="window.PretripModule.switchDvirTab('jobboard')">
          <span class="material-icons">assignment</span>
          <span>Job Board</span>
        </button>
      </div>
      <div id="inspections-panel" class="dvir-tab-panel active">
        ${getPretripHTML()}
      </div>
      ${getJSATabHTML()}
      <div id="jobboard-panel" class="dvir-tab-panel">
        <div class="loading-screen"><div class="loading-spinner"></div><p>Loading Job Board...</p></div>
      </div>
    `;
  } else if (isFounderPublicAccess) {
    // Founder public access: DVIR + full JSA tab (with history + PDF downloads)
    container.innerHTML = `
      <div class="dvir-tab-bar roster-tabs" style="margin: 12px auto 20px; max-width: 400px;">
        <button class="roster-tab active" id="dvir-tab-inspections" onclick="window.PretripModule.switchDvirTab('inspections')">
          <span class="material-icons">checklist</span>
          <span>Inspections</span>
        </button>
        <button class="roster-tab" id="dvir-tab-jsa" onclick="window.PretripModule.switchDvirTab('jsa')">
          <span class="material-icons">health_and_safety</span>
          <span>JSA</span>
        </button>
        ${window.AppState?.settings?.safetyMeetingSignInEnabled === true ? `
        <!-- TASK-342 Slice B/D revisit — Safety Meeting tab. Tap routes
             through PretripModule.openSafetyMeetingSignIn which delegates to
             the existing modal builder in jobboard.js. No panel content
             needed; the tab is a button-style entry into the signature pad. -->
        <button class="roster-tab" id="dvir-tab-safety" onclick="window.PretripModule.openSafetyMeetingSignIn()">
          <span class="material-icons">how_to_reg</span>
          <span>Safety</span>
        </button>` : ''}
      </div>
      <div id="inspections-panel" class="dvir-tab-panel active">
        ${getPretripHTML()}
      </div>
      ${getJSATabHTML()}
    `;
  } else {
    // Full view: wrap DVIR + JSA in tab panels
    container.innerHTML = `
      <div class="dvir-tab-bar roster-tabs" style="margin: 12px auto 20px; max-width: 400px;">
        <button class="roster-tab active" id="dvir-tab-inspections" onclick="window.PretripModule.switchDvirTab('inspections')">
          <span class="material-icons">checklist</span>
          <span>Inspections</span>
        </button>
        <button class="roster-tab" id="dvir-tab-jsa" onclick="window.PretripModule.switchDvirTab('jsa')">
          <span class="material-icons">health_and_safety</span>
          <span>JSA</span>
        </button>
        ${window.AppState?.settings?.safetyMeetingSignInEnabled === true ? `
        <!-- TASK-342 Slice B/D revisit — Safety Meeting tab. Tap routes
             through PretripModule.openSafetyMeetingSignIn which delegates to
             the existing modal builder in jobboard.js. No panel content
             needed; the tab is a button-style entry into the signature pad. -->
        <button class="roster-tab" id="dvir-tab-safety" onclick="window.PretripModule.openSafetyMeetingSignIn()">
          <span class="material-icons">how_to_reg</span>
          <span>Safety</span>
        </button>` : ''}
      </div>
      <div id="inspections-panel" class="dvir-tab-panel active">
        ${getPretripHTML()}
      </div>
      ${getJSATabHTML()}
    `;
  }

  // Set up modal handlers
  setupModals();

  // Set up tutorial event listeners
  setupTutorialEvents();

  // Subscribe to real-time data
  subscribeToData(tenantId);

  // TASK-9658 S1 — record deep link fallback if the list never delivers.
  if (_dvirFocusFallbackTimer) { clearTimeout(_dvirFocusFallbackTimer); _dvirFocusFallbackTimer = null; }
  if (window.RecordLinks?.hasPendingFocus?.('pretrip')) {
    _dvirFocusFallbackTimer = setTimeout(() => { _dvirFocusFallbackTimer = null; drainPretripRecordFocus(); }, 8000);
  }

  // Initialize JSA module (all modes — drivers need JSA too)
  initJSA(container, tenantId);

  // Process offline photo queue when online
  const onOnline = () => processOfflineQueue(tenantId);
  window.addEventListener('online', onOnline);
  if (navigator.onLine) processOfflineQueue(tenantId);

  // Return cleanup function
  return () => {
    if (_dvirFocusFallbackTimer) { clearTimeout(_dvirFocusFallbackTimer); _dvirFocusFallbackTimer = null; } // TASK-9658
    _cancelTplOpen(); // TASK-10135 — leaving Inspections cancels a pending Unit Checklists pointer
    PretripState.unsubscribers.forEach(unsub => unsub());
    PretripState.unsubscribers = [];
    _resetByIdCache(); // TASK-9372
    PretripState._viewGen++; // TASK-9405 — in-flight Load older must not restart listeners
    _stopLinkedTaskWatchers(); // TASK-9405
    // Reset the on-demand-loaded older pages so next view-open starts fresh
    // and the "Load older" button is available again.
    PretripState.olderInspections = [];
    PretripState._olderLoading = false;
    PretripState._olderExhausted = false;
    ['dvir-defects','maintenance-notes','dvir-oil-brand','unknown-driver-name']
      .forEach(id => window.SmartAssistant?.unregister(id));
    cleanupJSA();
    if (PretripState._jobBoardCleanup) {
      PretripState._jobBoardCleanup();
      PretripState._jobBoardCleanup = null;
    }
    PretripState._jobBoardLoaded = false;
    window.removeEventListener('online', onOnline);
  };
}

// ============================================
// TUTORIAL EVENT HANDLERS
// ============================================
let tutorialEventsDispatched = {
  driver: false,
  asset: false,
  defects: false,
  confirm: false
};

function resetTutorialEvents() {
  tutorialEventsDispatched = {
    driver: false,
    asset: false,
    defects: false,
    confirm: false
  };
}

function setupTutorialEvents() {
  // Reset flags when modal opens
  const modal = document.getElementById('modal-inspection');
  if (modal) {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          if (!modal.classList.contains('hidden')) {
            resetTutorialEvents();
          }
        }
      });
    });
    observer.observe(modal, { attributes: true });
  }

  // Driver selection
  const driverSelect = document.getElementById('inspection-driver');
  if (driverSelect) {
    driverSelect.addEventListener('change', () => {
      if (driverSelect.value && !tutorialEventsDispatched.driver) {
        tutorialEventsDispatched.driver = true;
        window.dispatchEvent(new CustomEvent('pretrip:driver-selected'));
      }
    });
  }

  // Truck/Asset selection — also check for pending 3-sig DVIRs
  const truckSelect = document.getElementById('inspection-truck');
  if (truckSelect) {
    truckSelect.addEventListener('change', () => {
      if (truckSelect.value && !tutorialEventsDispatched.asset) {
        tutorialEventsDispatched.asset = true;
        window.dispatchEvent(new CustomEvent('pretrip:asset-selected'));
      }
      // Check if selected vehicle needs 3rd signature acknowledgment
      if (truckSelect.value) {
        checkVehicle3SigOnSelect(truckSelect.value);
      }
    });
  }

  // Safety defects input
  const defectsTextarea = document.getElementById('dvir-defects');
  if (defectsTextarea) {
    defectsTextarea.addEventListener('input', () => {
      if (defectsTextarea.value.trim().length > 5 && !tutorialEventsDispatched.defects) {
        tutorialEventsDispatched.defects = true;
        window.dispatchEvent(new CustomEvent('pretrip:defects-entered'));
      }
    });
  }

  // Confirmation checkbox
  const confirmCheckbox = document.getElementById('dvir-confirm');
  if (confirmCheckbox) {
    confirmCheckbox.addEventListener('change', () => {
      if (confirmCheckbox.checked && !tutorialEventsDispatched.confirm) {
        tutorialEventsDispatched.confirm = true;
        window.dispatchEvent(new CustomEvent('pretrip:confirmation-checked'));
      }
    });
  }
}

// ============================================
// MODAL HTML HELPERS (shared between driver and dispatcher views)
// ============================================
function getInspectionModalHTML() {
  return `
    <!-- New Inspection Modal -->
    <div class="modal hidden" id="modal-inspection">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-inspection-title">New Pre-Trip Inspection</h3>
          <button class="btn-icon" onclick="window.PretripModule.closeModal('inspection')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-inspection" class="modal-form-scrollable" novalidate onsubmit="window.PretripModule.saveInspection(event)">
          <div class="modal-form-scroll">
          <input type="hidden" id="inspection-id">

          <!-- Driver & Asset Selection -->
          <div class="form-row">
            <div class="form-group">
              <label for="inspection-driver">Driver *</label>
              <select id="inspection-driver" required>
                <option value="">Select driver</option>
              </select>
            </div>
            <div class="form-group">
              <label for="inspection-asset-type">Asset Type *</label>
              <select id="inspection-asset-type" required onchange="window.PretripModule.onAssetTypeChange()">
                <option value="truck">Vehicle</option>
                <option value="trailer">Trailer</option>
                <option value="equipment">Equipment</option>
              </select>
            </div>
            <div class="form-group">
              <label for="inspection-truck" id="label-inspection-asset">Vehicle *</label>
              <select id="inspection-truck" required onchange="window.PretripModule.onAssetSelected?.()">
                <option value="">Select vehicle</option>
              </select>
              <input type="search" id="inspection-asset-filter" class="hidden" placeholder="Search equipment" aria-label="Search equipment" autocomplete="off" enterkeyhint="search" style="margin-top:6px;min-height:44px;" oninput="window.PretripModule.onAssetFilterInput?.()" onkeydown="if (event.key === 'Enter') { event.preventDefault(); this.blur(); }">
            </div>
          </div>

          <!-- Return Inspection Section (shown when asset has pending return) -->
          <div id="return-inspection-section" class="hidden" style="background:linear-gradient(135deg,rgba(59,130,246,0.06),rgba(6,182,212,0.06));border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:var(--spacing-sm);">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">assignment_return</span>
              <span style="font-weight:700;font-size:0.9rem;color:var(--text-primary);">Return Inspection</span>
              <span id="ri-expected-badge" style="font-size:0.75rem;color:var(--text-secondary);margin-left:auto;"></span>
            </div>
            <div class="form-row">
              <div class="form-group" id="ri-qty-group">
                <label for="ri-qty-returned">Qty Returned *</label>
                <input type="number" id="ri-qty-returned" min="0" placeholder="0" oninput="window.PretripModule.checkReturnQtyWarning?.()">
              </div>
              <div class="form-group">
                <label for="ri-receiver">Received By *</label>
                <input type="text" id="ri-receiver" placeholder="Name of person receiving">
              </div>
              <div class="form-group">
                <label for="ri-yard">Returned To Yard</label>
                <select id="ri-yard">
                  <option value="">Select yard</option>
                </select>
              </div>
            </div>
            <div id="ri-qty-warning" class="hidden" style="padding:6px 10px;border-radius:var(--radius-sm);font-size:0.78rem;font-weight:600;margin-bottom:var(--spacing-xs);"></div>
            <div class="form-group">
              <label for="ri-condition">Condition Notes</label>
              <textarea id="ri-condition" rows="2" placeholder="Note any damage, missing parts, wear, etc."></textarea>
            </div>
            <div class="form-group hidden" id="ri-hours-group">
              <label for="ri-hours-reading">Current Hours Reading</label>
              <input type="number" id="ri-hours-reading" step="0.1" min="0" placeholder="Hour meter reading">
            </div>
          </div>

          <!-- Previous inspections on this vehicle (driver mode only) -->
          <div id="truck-recent-inspections" class="hidden"></div>

          <div class="form-row">
            <div class="form-group">
              <label for="inspection-type">Inspection Type *</label>
              <select id="inspection-type" required onchange="window.PretripModule.onInspectionTypeChange()">
                <option value="pre">Pre-Trip</option>
                <option value="post">Post-Trip</option>
              </select>
            </div>
            <div class="form-group">
              <label for="inspection-date">Date</label>
              <input type="date" id="inspection-date">
            </div>
            <div class="form-group">
              <label for="inspection-mileage">Mileage</label>
              <input type="number" id="inspection-mileage" placeholder="Current odometer">
            </div>
            <div class="form-group">
              <label for="inspection-hours">Hours</label>
              <input type="number" id="inspection-hours" placeholder="Current hour meter">
            </div>
          </div>

          <!-- Collapsible Checkbox System -->
          <div class="checklist-toggle-section">
            <label class="checklist-toggle">
              <input type="checkbox" id="use-checkbox-system" onchange="window.PretripModule.toggleChecklistSystem()">
              <span class="toggle-label">Use Checkbox System</span>
              <span class="toggle-hint">(Optional)</span>
            </label>
          </div>

          <div class="inspection-checklist collapsed" id="checklist-section">
            <p class="checklist-hint">✓ Checked = Item OK  |  ☐ Unchecked = Defect (creates maintenance task)</p>
            <div id="checklist-items">
              <!-- Items will be rendered here -->
            </div>
          </div>

          <!-- TASK-9741 — the picked unit's extra checklist (hidden when none applies) -->
          <div class="inspection-unit-checks hidden" id="unit-checks-section" style="margin:var(--spacing-sm) 0;padding:var(--spacing-sm);border:1px solid var(--border-light);border-radius:var(--radius-md,10px);"></div>

          <!-- DVIR Safety Defects Section -->
          <details class="dvir-defects-section" id="dvir-defects-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-section-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons" style="color:var(--status-danger);">warning</span>
              Safety Defects / Issues Found
            </summary>
            <div style="padding-top:12px;">
              <div class="form-group" style="margin-bottom:0;">
                <textarea id="dvir-defects" rows="3" placeholder="Safety defects will place this vehicle on hold until repaired and signed off by a mechanic."></textarea>
              </div>
            </div>
          </details>

          <!-- Maintenance Notes (Non-Safety) -->
          <details class="dvir-maint-notes-section" id="dvir-maint-notes-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-section-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons" style="color:var(--status-warning);">build</span>
              Maintenance Notes (Non-Safety)
            </summary>
            <div style="padding-top:12px;">
              <div class="form-group" style="margin-bottom:0;">
                <textarea id="maintenance-notes" rows="2" placeholder="Note any maintenance needs that aren't safety defects. Creates a task for shop."></textarea>
              </div>
            </div>
          </details>

          <!-- Fluids Added During Inspection -->
          <details class="dvir-fluids-section" id="dvir-fluids-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-fluids-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons" style="color:var(--accent-teal);">water_drop</span>
              Fluids Added
              <span class="dvir-fluids-badge hidden" id="dvir-fluids-badge">0</span>
            </summary>
            <div style="padding-top:12px;">
              <div class="dvir-fluid-form">
                <div class="form-row">
                  <div class="form-group">
                    <label for="dvir-fluid-type">Type *</label>
                    <select id="dvir-fluid-type" onchange="window.PretripModule.toggleDvirOilFields()">
                      <option value="Oil">Oil</option>
                      <option value="Coolant">Coolant</option>
                      <option value="DEF">DEF</option>
                      <option value="Hydraulic">Hydraulic</option>
                      <option value="Transmission">Transmission</option>
                      <option value="Power Steering">Power Steering</option>
                      <option value="Brake Fluid">Brake Fluid</option>
                      <option value="Windshield">Windshield Washer</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="dvir-fluid-amount">Amount *</label>
                    <input type="number" id="dvir-fluid-amount" step="0.25" min="0" placeholder="1.5">
                  </div>
                  <div class="form-group">
                    <label for="dvir-fluid-unit">Unit</label>
                    <select id="dvir-fluid-unit">
                      <option value="quarts">Quarts</option>
                      <option value="gallons">Gallons</option>
                      <option value="liters">Liters</option>
                    </select>
                  </div>
                </div>
                <div id="dvir-oil-fields" class="hidden">
                  <div class="form-row">
                    <div class="form-group">
                      <label for="dvir-oil-brand">Oil Brand</label>
                      <input type="text" id="dvir-oil-brand" placeholder="Shell Rotella">
                    </div>
                    <div class="form-group">
                      <label for="dvir-oil-weight">Oil Weight</label>
                      <select id="dvir-oil-weight">
                        <option value="">Select weight</option>
                        <option value="15W-40">15W-40</option>
                        <option value="10W-30">10W-30</option>
                        <option value="5W-30">5W-30</option>
                        <option value="5W-40">5W-40</option>
                        <option value="0W-20">0W-20</option>
                        <option value="SAE 30">SAE 30</option>
                      </select>
                    </div>
                  </div>
                </div>
                <button type="button" class="btn btn-secondary" onclick="window.PretripModule.addDvirFluid()">
                  <span class="material-icons">add</span> Add Entry
                </button>
              </div>
              <div id="dvir-fluids-list"></div>
            </div>
          </details>

          <!-- DVIR Photos -->
          <details class="dvir-photos-section" id="dvir-photos-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-section-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons">photo_camera</span>
              Add Photos
              <span class="photo-count" id="photo-count">0 of ${STARTER_LIMITS.dvir.maxPhotos}</span>
            </summary>
            <div style="padding-top:12px;">
              <div class="dvir-photo-grid" id="dvir-photo-grid"></div>
              <input type="file" accept="image/*" capture="environment" id="dvir-photo-input" class="hidden" onchange="window.PretripModule.handlePhotoCapture(event)">
              <button type="button" class="btn btn-secondary dvir-add-photo-btn" id="btn-add-photo" onclick="document.getElementById('dvir-photo-input').click()">
                <span class="material-icons">add_a_photo</span> Add Photo
              </button>
            </div>
          </details>

          <!-- Unknown Driver Name (shown when "Other" selected) - positioned above signature -->
          <div class="form-group hidden" id="unknown-driver-group" style="margin-bottom: 16px; padding: 12px; background: var(--bg-surface); border-radius: 8px; border: 2px solid var(--accent-primary);">
            <label for="unknown-driver-name" style="font-weight: 600; color: var(--accent-primary);">
              <span class="material-icons" style="vertical-align: middle; font-size: 18px;">badge</span>
              Your Name (for signature) *
            </label>
            <input type="text" id="unknown-driver-name" placeholder="Enter your full name" style="font-size: 16px;">
          </div>

          <!-- Signature on File (shown when driver has signature) -->
          <div class="dvir-signature-on-file hidden" id="signature-on-file-section">
            <label>
              <span class="material-icons">verified</span>
              Signature on File
            </label>
            <div class="signature-on-file-display">
              <img id="signature-on-file-img" src="" alt="Signature on file">
            </div>
            <p class="signature-hint">Your signature is on file. Check the box below to confirm this inspection.</p>
          </div>

          <!-- Driver Signature Capture (shown when no signature on file) -->
          <div class="dvir-signature-section">
            <label>
              <span class="material-icons">draw</span>
              Driver Signature *
            </label>
            <div class="signature-pad-container">
              <canvas id="signature-canvas" width="400" height="150"></canvas>
              <button type="button" class="btn btn-sm btn-secondary signature-clear-btn" onclick="window.PretripModule.clearSignature()">
                <span class="material-icons">refresh</span> Clear
              </button>
            </div>
            <p class="signature-hint">Sign above using your finger or mouse</p>
          </div>

          <!-- Printed name fallback toggle -->
          <div class="form-group" style="margin-top:8px;">
            <label class="checkbox-label" style="font-size:0.85rem;cursor:pointer;">
              <input type="checkbox" id="dvir-use-printed-name" onchange="window.PretripModule.togglePrintedName()">
              <span>Use printed name instead of signature</span>
            </label>
          </div>
          <div class="form-group hidden" id="dvir-printed-name-group">
            <input type="text" id="dvir-typed-name" class="form-control" placeholder="Type your full legal name" autocomplete="off" style="font-size:1rem;font-weight:600;">
          </div>

          <!-- Confirmation checkbox -->
          <div class="form-group dvir-confirmation">
            <label class="checkbox-label">
              <input type="checkbox" id="dvir-confirm" required>
              <span>I confirm I performed this inspection</span>
            </label>
          </div>
          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer">
            <button type="button" class="btn btn-secondary" onclick="window.PretripModule.closeModal('inspection')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-submit-inspection">Submit Inspection</button>
          </div>
        </form>
      </div>
    </div>
  `;
}

function getSafetyHoldModalHTML() {
  return `
    <!-- Safety Hold Warning Modal -->
    <div class="modal hidden" id="modal-safety-hold">
      <div class="modal-content modal-small">
        <div class="modal-header modal-header-danger">
          <h3><span class="material-icons">warning</span> Vehicle on Safety Hold</h3>
        </div>
        <div class="modal-body">
          <div class="safety-hold-info">
            <p class="safety-hold-unit" id="safety-hold-unit"></p>
            <p class="safety-hold-message">This vehicle has unresolved safety defects that must be cleared by a mechanic before operation.</p>
            <div class="safety-hold-defects" id="safety-hold-defects"></div>
            <p class="safety-hold-action">Contact maintenance to resolve.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" onclick="window.PretripModule.closeModal('safety-hold')">I Understand</button>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// HTML TEMPLATE
// ============================================
function getPretripHTML(forceFullView = false) {
  const isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || (window.AppState?.driverModePin === true);

  // Simplified driver view - just the inspection button, no history
  if (isDriverMode && !forceFullView) {
    return `
      <div class="pretrip-view pretrip-driver-view">
        <!-- TASK-377: removed redundant "Back to Jobs" top button — generic bottom-nav handles it -->

        <!-- Driver-focused Header -->
        <div class="pretrip-header pretrip-header-driver">
          <div class="pretrip-header-left">
            <h1>Pre-Trip Inspection</h1>
            <p class="pretrip-subtitle">Complete your vehicle inspection before starting</p>
          </div>
        </div>

        <!-- Big Action Button for Drivers -->
        <div class="pretrip-driver-action">
          <button class="btn btn-primary btn-driver-inspection" onclick="window.PretripModule.showNewInspection()">
            <span class="material-icons">checklist</span>
            <span>Start Inspection</span>
          </button>
          <p class="pretrip-driver-hint">Tap to begin your pre-trip or post-trip inspection</p>
        </div>
      </div>
      ${getInspectionModalHTML()}
      ${getSafetyHoldModalHTML()}
    `;
  }

  // Full dispatcher/admin view with history
  return `
    <div class="pretrip-view">
      <!-- Header -->
      <div style="text-align:center;margin-bottom:var(--spacing-sm);">
        <h1 style="margin:0;font-size:1.5rem;font-weight:700;">Inspections</h1>
        <p class="pretrip-subtitle">Pre-trip and post-trip vehicle inspections</p>
      </div>
      <div class="pretrip-header">
        <div class="pretrip-header-actions" style="justify-content:center;width:100%;display:flex;gap:8px;flex-wrap:wrap;">
          <button class="btn btn-secondary" style="padding:12px 20px;font-size:0.95rem;font-weight:600;border-radius:10px;" onclick="window.PretripModule.showBlankDVIR()">
            <span class="material-icons">description</span> View Blank Form
          </button>
          ${!isDriverMode && (typeof window.hrEffectiveAdmin !== 'function' || window.hrEffectiveAdmin()) ? `
          <!-- TASK-9337 — admin-only: saveInspectionSettings writes tenant-root
               settings (dvirAccountability, jsaAccountability, enableCitations,
               lsraRules) that no module arm grants, so a staff member would edit
               and then be refused at Save. Hidden for non-admins instead. -->
          <!-- TASK-376 — Inspection & Compliance Settings is admin config; never
               for PIN/public/driver sessions even though the driver shell renders
               this full view via getPretripHTML(true) (forceFullView). -->
          <button class="btn btn-secondary btn-neon-settings btn-module-header" style="font-weight:600;border-radius:10px;" onclick="window.openInspectionSettingsModal&&window.openInspectionSettingsModal()" title="Inspection &amp; Compliance Settings">
            <span class="material-icons">settings</span> Settings
          </button>` : ''}
          ${!isDriverMode && canManageInspectionTemplates() ? `
          <!-- TASK-9741 — per type / per unit checklists + blank PDFs. Module
               access (maintenance) or admin; never a driver / PIN session. -->
          <button id="btn-inspection-templates" class="btn btn-secondary" style="padding:12px 20px;font-size:0.95rem;font-weight:600;border-radius:10px;" onclick="window.PretripModule.openInspectionTemplates()">
            <span class="material-icons">fact_check</span> Unit Checklists
          </button>` : ''}
          <button id="btn-new-inspection" class="btn btn-primary" style="padding:12px 20px;font-size:0.95rem;font-weight:600;border-radius:10px;" onclick="window.PretripModule.showNewInspection()">
            <span class="material-icons">add</span> New Inspection
          </button>
        </div>
      </div>

      <!-- Search -->
      <div style="max-width:400px;margin:0 auto 12px auto;padding:0 12px;">
        <input type="text" id="pretrip-search" class="form-control" placeholder="Search inspections..." value="" oninput="window.PretripModule.searchInspections(this.value)" style="border-radius:10px;border:1px solid var(--border-light);box-shadow:0 1px 3px rgba(0,0,0,0.06);width:100%;box-sizing:border-box;">
      </div>

      <!-- Filter Tabs -->
      <!-- TASK-4700 — DEFECTS is the default tab, not All. 99% of DVIRs are
           clean (measured 2026-09-01: 1,666 of 1,686 across all tenants), so
           an "All"-first list buries the handful that actually identify
           something. Nothing is hidden destructively — "All" is one click away
           and is the obvious escape hatch. Order is priority order, so the
           active tab is also the first tab.
           TASK-9405 — "Open defects": a defect leaves this tab once repaired
           and signed by mechanic + next driver (dvirDefectClosure). The label
           says so, or users read the drop as signed-off records deleted. -->
      <div class="pretrip-filters">
        <button class="pretrip-filter active" data-filter="defects" onclick="window.PretripModule.filterInspections('defects')">
          Open defects <span class="filter-count count-zero" id="count-defects">0</span>
        </button>
        <button class="pretrip-filter" data-filter="pass" onclick="window.PretripModule.filterInspections('pass')">
          Pass <span class="filter-count count-zero" id="count-pass">0</span>
        </button>
        <button class="pretrip-filter" data-filter="all" onclick="window.PretripModule.filterInspections('all')">
          All <span class="filter-count count-zero" id="count-all">0</span>
        </button>
        <button class="pretrip-filter" data-filter="archived" onclick="window.PretripModule.filterInspections('archived')">
          Archived <span class="filter-count count-zero" id="count-archived">0</span>
        </button>
        <!-- TASK-4712 — the onboarding ask ("I can't find anything" on 870 DVIRs,
             866 of them clean). Archive in bulk, NOT delete: see bulkSetArchived. -->
        <button class="pretrip-select-btn" id="pretrip-select-toggle"
          style="margin-left:auto;padding:6px 12px;border:1px solid var(--border-light);background:transparent;border-radius:8px;cursor:pointer;font-size:0.82rem;font-weight:600;color:var(--text-secondary);min-height:36px;"
          onclick="window.PretripModule.toggleSelectMode()">
          <span class="material-icons" style="font-size:15px;vertical-align:-3px;">checklist</span> Select
        </button>
        ${_canExportInspections() ? `<button class="pretrip-select-btn" title="Download every inspection in a date range: spreadsheets plus a PDF of each" style="padding:6px 12px;border:1px solid var(--border-light);background:transparent;border-radius:8px;cursor:pointer;font-size:0.82rem;font-weight:600;color:var(--text-secondary);min-height:36px;" onclick="window.PretripModule.openInspectionExport()"><span class="material-icons" style="font-size:15px;vertical-align:-3px;">download</span> Export</button>` : ''}
      </div>

      <!-- Bulk action bar — only rendered while select mode is on. -->
      <div id="pretrip-bulk-bar" class="hidden" style="display:flex;flex-wrap:wrap;gap:10px;align-items:center;padding:12px 14px;margin:0 0 12px;background:var(--bg-secondary,#f1f5f9);border:1px solid var(--border-light);border-radius:10px;">
        <label style="display:flex;align-items:center;gap:8px;cursor:pointer;font-size:0.85rem;font-weight:600;color:var(--text-primary);min-height:44px;">
          <input type="checkbox" id="pretrip-select-all" onchange="window.PretripModule.toggleSelectAll(this.checked)" style="width:18px;height:18px;cursor:pointer;">
          <span id="pretrip-select-all-label">Select all loaded</span>
        </label>
        <span id="pretrip-bulk-count" style="font-size:0.85rem;color:var(--text-secondary);"></span>
        <div style="margin-left:auto;display:flex;gap:8px;">
          <button type="button" class="btn btn-secondary" onclick="window.PretripModule.toggleSelectMode()" style="min-height:44px;">Cancel</button>
          <button type="button" class="btn btn-primary" id="pretrip-bulk-action" onclick="window.PretripModule.bulkArchiveSelected()" style="min-height:44px;" disabled>Archive</button>
        </div>
        <div id="pretrip-bulk-scope" style="flex-basis:100%;font-size:0.78rem;color:var(--text-secondary);"></div>
      </div>

      <!-- Inspections List -->
      <div class="pretrip-list" id="pretrip-list">
        <div class="loading-state"><div class="loading-spinner"></div><p>Loading inspections...</p></div>
      </div>
    </div>

    <!-- New Inspection Modal -->
    <div class="modal hidden" id="modal-inspection">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3 id="modal-inspection-title">New Pre-Trip Inspection</h3>
          <button class="btn-icon" onclick="window.PretripModule.closeModal('inspection')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <form id="form-inspection" class="modal-form-scrollable" novalidate onsubmit="window.PretripModule.saveInspection(event)">
          <div class="modal-form-scroll">
          <input type="hidden" id="inspection-id">

          <!-- Driver & Asset Selection -->
          <div class="form-row">
            <div class="form-group">
              <label for="inspection-driver">Driver *</label>
              <select id="inspection-driver" required>
                <option value="">Select driver</option>
              </select>
            </div>
            <div class="form-group">
              <label for="inspection-asset-type">Asset Type *</label>
              <select id="inspection-asset-type" required onchange="window.PretripModule.onAssetTypeChange()">
                <option value="truck">Vehicle</option>
                <option value="trailer">Trailer</option>
                <option value="equipment">Equipment</option>
              </select>
            </div>
            <div class="form-group">
              <label for="inspection-truck" id="label-inspection-asset">Vehicle *</label>
              <select id="inspection-truck" required onchange="window.PretripModule.onAssetSelected?.()">
                <option value="">Select vehicle</option>
              </select>
              <input type="search" id="inspection-asset-filter" class="hidden" placeholder="Search equipment" aria-label="Search equipment" autocomplete="off" enterkeyhint="search" style="margin-top:6px;min-height:44px;" oninput="window.PretripModule.onAssetFilterInput?.()" onkeydown="if (event.key === 'Enter') { event.preventDefault(); this.blur(); }">
            </div>
          </div>

          <!-- Return Inspection Section (shown when asset has pending return) -->
          <div id="return-inspection-section" class="hidden" style="background:linear-gradient(135deg,rgba(59,130,246,0.06),rgba(6,182,212,0.06));border:1px solid var(--border-light);border-radius:var(--radius-md);padding:var(--spacing-md);margin-bottom:var(--spacing-md);">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:var(--spacing-sm);">
              <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">assignment_return</span>
              <span style="font-weight:700;font-size:0.9rem;color:var(--text-primary);">Return Inspection</span>
              <span id="ri-expected-badge" style="font-size:0.75rem;color:var(--text-secondary);margin-left:auto;"></span>
            </div>
            <div class="form-row">
              <div class="form-group" id="ri-qty-group">
                <label for="ri-qty-returned">Qty Returned *</label>
                <input type="number" id="ri-qty-returned" min="0" placeholder="0" oninput="window.PretripModule.checkReturnQtyWarning?.()">
              </div>
              <div class="form-group">
                <label for="ri-receiver">Received By *</label>
                <input type="text" id="ri-receiver" placeholder="Name of person receiving">
              </div>
              <div class="form-group">
                <label for="ri-yard">Returned To Yard</label>
                <select id="ri-yard">
                  <option value="">Select yard</option>
                </select>
              </div>
            </div>
            <div id="ri-qty-warning" class="hidden" style="padding:6px 10px;border-radius:var(--radius-sm);font-size:0.78rem;font-weight:600;margin-bottom:var(--spacing-xs);"></div>
            <div class="form-group">
              <label for="ri-condition">Condition Notes</label>
              <textarea id="ri-condition" rows="2" placeholder="Note any damage, missing parts, wear, etc."></textarea>
            </div>
            <div class="form-group hidden" id="ri-hours-group">
              <label for="ri-hours-reading">Current Hours Reading</label>
              <input type="number" id="ri-hours-reading" step="0.1" min="0" placeholder="Hour meter reading">
            </div>
          </div>

          <!-- Previous inspections on this vehicle (driver mode only) -->
          <div id="truck-recent-inspections" class="hidden"></div>

          <div class="form-row">
            <div class="form-group">
              <label for="inspection-type">Inspection Type *</label>
              <select id="inspection-type" required onchange="window.PretripModule.onInspectionTypeChange()">
                <option value="pre">Pre-Trip</option>
                <option value="post">Post-Trip</option>
              </select>
            </div>
            <div class="form-group">
              <label for="inspection-date">Date</label>
              <input type="date" id="inspection-date">
            </div>
            <div class="form-group">
              <label for="inspection-mileage">Mileage</label>
              <input type="number" id="inspection-mileage" placeholder="Current odometer">
            </div>
            <div class="form-group">
              <label for="inspection-hours">Hours</label>
              <input type="number" id="inspection-hours" placeholder="Current hour meter">
            </div>
          </div>

          <!-- Collapsible Checkbox System -->
          <div class="checklist-toggle-section">
            <label class="checklist-toggle">
              <input type="checkbox" id="use-checkbox-system" onchange="window.PretripModule.toggleChecklistSystem()">
              <span class="toggle-label">Use Checkbox System</span>
              <span class="toggle-hint">(Optional)</span>
            </label>
          </div>

          <div class="inspection-checklist collapsed" id="checklist-section">
            <p class="checklist-hint">✓ Checked = Item OK  |  ☐ Unchecked = Defect (creates maintenance task)</p>
            <div id="checklist-items">
              <!-- Items will be rendered here -->
            </div>
          </div>

          <!-- TASK-9741 — the picked unit's extra checklist (hidden when none applies) -->
          <div class="inspection-unit-checks hidden" id="unit-checks-section" style="margin:var(--spacing-sm) 0;padding:var(--spacing-sm);border:1px solid var(--border-light);border-radius:var(--radius-md,10px);"></div>

          <!-- DVIR Safety Defects Section -->
          <details class="dvir-defects-section" id="dvir-defects-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-section-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons" style="color:var(--status-danger);">warning</span>
              Safety Defects / Issues Found
            </summary>
            <div style="padding-top:12px;">
              <div class="form-group" style="margin-bottom:0;">
                <textarea id="dvir-defects" rows="3" placeholder="Safety defects will place this vehicle on hold until repaired and signed off by a mechanic."></textarea>
              </div>
            </div>
          </details>

          <!-- Maintenance Notes (Non-Safety) -->
          <details class="dvir-maint-notes-section" id="dvir-maint-notes-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-section-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons" style="color:var(--status-warning);">build</span>
              Maintenance Notes (Non-Safety)
            </summary>
            <div style="padding-top:12px;">
              <div class="form-group" style="margin-bottom:0;">
                <textarea id="maintenance-notes" rows="2" placeholder="Note any maintenance needs that aren't safety defects. Creates a task for shop."></textarea>
              </div>
            </div>
          </details>

          <!-- Fluids Added During Inspection -->
          <details class="dvir-fluids-section" id="dvir-fluids-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-fluids-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons" style="color:var(--accent-teal);">water_drop</span>
              Fluids Added
              <span class="dvir-fluids-badge hidden" id="dvir-fluids-badge">0</span>
            </summary>
            <div style="padding-top:12px;">
              <div class="dvir-fluid-form">
                <div class="form-row">
                  <div class="form-group">
                    <label for="dvir-fluid-type">Type *</label>
                    <select id="dvir-fluid-type" onchange="window.PretripModule.toggleDvirOilFields()">
                      <option value="Oil">Oil</option>
                      <option value="Coolant">Coolant</option>
                      <option value="DEF">DEF</option>
                      <option value="Hydraulic">Hydraulic</option>
                      <option value="Transmission">Transmission</option>
                      <option value="Power Steering">Power Steering</option>
                      <option value="Brake Fluid">Brake Fluid</option>
                      <option value="Windshield">Windshield Washer</option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label for="dvir-fluid-amount">Amount *</label>
                    <input type="number" id="dvir-fluid-amount" step="0.25" min="0" placeholder="1.5">
                  </div>
                  <div class="form-group">
                    <label for="dvir-fluid-unit">Unit</label>
                    <select id="dvir-fluid-unit">
                      <option value="quarts">Quarts</option>
                      <option value="gallons">Gallons</option>
                      <option value="liters">Liters</option>
                    </select>
                  </div>
                </div>
                <div id="dvir-oil-fields" class="hidden">
                  <div class="form-row">
                    <div class="form-group">
                      <label for="dvir-oil-brand">Oil Brand</label>
                      <input type="text" id="dvir-oil-brand" placeholder="Shell Rotella">
                    </div>
                    <div class="form-group">
                      <label for="dvir-oil-weight">Oil Weight</label>
                      <select id="dvir-oil-weight">
                        <option value="">Select weight</option>
                        <option value="15W-40">15W-40</option>
                        <option value="10W-30">10W-30</option>
                        <option value="5W-30">5W-30</option>
                        <option value="5W-40">5W-40</option>
                        <option value="0W-20">0W-20</option>
                        <option value="SAE 30">SAE 30</option>
                      </select>
                    </div>
                  </div>
                </div>
                <button type="button" class="btn btn-secondary" onclick="window.PretripModule.addDvirFluid()">
                  <span class="material-icons">add</span> Add Entry
                </button>
              </div>
              <div id="dvir-fluids-list"></div>
            </div>
          </details>

          <!-- DVIR Photos -->
          <details class="dvir-photos-section" id="dvir-photos-section">
            <summary style="cursor:pointer;font-weight:600;display:flex;align-items:center;gap:8px;list-style:none;">
              <span class="material-icons dvir-section-chevron" style="font-size:20px;color:var(--text-secondary);transition:transform 0.2s;">expand_more</span>
              <span class="material-icons">photo_camera</span>
              Add Photos
              <span class="photo-count" id="photo-count">0 of ${STARTER_LIMITS.dvir.maxPhotos}</span>
            </summary>
            <div style="padding-top:12px;">
              <div class="dvir-photo-grid" id="dvir-photo-grid"></div>
              <input type="file" accept="image/*" capture="environment" id="dvir-photo-input" class="hidden" onchange="window.PretripModule.handlePhotoCapture(event)">
              <button type="button" class="btn btn-secondary dvir-add-photo-btn" id="btn-add-photo" onclick="document.getElementById('dvir-photo-input').click()">
                <span class="material-icons">add_a_photo</span> Add Photo
              </button>
            </div>
          </details>

          <!-- Unknown Driver Name (shown when "Other" selected) - positioned above signature -->
          <div class="form-group hidden" id="unknown-driver-group" style="margin-bottom: 16px; padding: 12px; background: var(--bg-surface); border-radius: 8px; border: 2px solid var(--accent-primary);">
            <label for="unknown-driver-name" style="font-weight: 600; color: var(--accent-primary);">
              <span class="material-icons" style="vertical-align: middle; font-size: 18px;">badge</span>
              Your Name (for signature) *
            </label>
            <input type="text" id="unknown-driver-name" placeholder="Enter your full name" style="font-size: 16px;">
          </div>

          <!-- Signature on File (shown when driver has signature) -->
          <div class="dvir-signature-on-file hidden" id="signature-on-file-section">
            <label>
              <span class="material-icons">verified</span>
              Signature on File
            </label>
            <div class="signature-on-file-display">
              <img id="signature-on-file-img" src="" alt="Signature on file">
            </div>
            <p class="signature-hint">Your signature is on file. Check the box below to confirm this inspection.</p>
          </div>

          <!-- Driver Signature Capture (shown when no signature on file) -->
          <div class="dvir-signature-section">
            <label>
              <span class="material-icons">draw</span>
              Driver Signature *
            </label>
            <div class="signature-pad-container">
              <canvas id="signature-canvas" width="400" height="150"></canvas>
              <button type="button" class="btn btn-sm btn-secondary signature-clear-btn" onclick="window.PretripModule.clearSignature()">
                <span class="material-icons">refresh</span> Clear
              </button>
            </div>
            <p class="signature-hint">Sign above using your finger or mouse</p>
          </div>

          <!-- Printed name fallback toggle -->
          <div class="form-group" style="margin-top:8px;">
            <label class="checkbox-label" style="font-size:0.85rem;cursor:pointer;">
              <input type="checkbox" id="dvir-use-printed-name" onchange="window.PretripModule.togglePrintedName()">
              <span>Use printed name instead of signature</span>
            </label>
          </div>
          <div class="form-group hidden" id="dvir-printed-name-group">
            <input type="text" id="dvir-typed-name" class="form-control" placeholder="Type your full legal name" autocomplete="off" style="font-size:1rem;font-weight:600;">
          </div>

          <!-- Confirmation checkbox -->
          <div class="form-group dvir-confirmation">
            <label class="checkbox-label">
              <input type="checkbox" id="dvir-confirm" required>
              <span>I confirm I performed this inspection</span>
            </label>
          </div>
          </div><!-- end modal-form-scroll -->

          <div class="modal-form-footer">
            <button type="button" class="btn btn-secondary" onclick="window.PretripModule.closeModal('inspection')">Cancel</button>
            <button type="submit" class="btn btn-primary" id="btn-submit-inspection">Submit Inspection</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Blank DVIR Modal -->
    <div class="modal hidden" id="modal-blank-dvir">
      <div class="modal-content modal-large">
        <div class="modal-header">
          <h3>Blank DVIR Form</h3>
          <button class="btn-icon" onclick="window.PretripModule.closeModal('blank-dvir')">
            <span class="material-icons">close</span>
          </button>
        </div>
        <div class="blank-dvir-content">
          <div class="dvir-form-header">
            <div class="dvir-logo">
              <img src="/logo-sm.png" alt="DispatchDeck" style="height: 40px;">
            </div>
            <h2>Driver Vehicle Inspection Report (DVIR)</h2>
            <p class="dvir-regulation">49 CFR 396.11 & 396.13</p>
          </div>

          <div class="dvir-form-section">
            <div class="dvir-form-row">
              <div class="dvir-field">
                <label>Date:</label>
                <span class="dvir-blank-line"></span>
              </div>
              <div class="dvir-field">
                <label>Time:</label>
                <span class="dvir-blank-line"></span>
              </div>
            </div>
            <div class="dvir-form-row">
              <div class="dvir-field">
                <label>Inspection Type:</label>
                <span class="dvir-checkbox-inline">☐ Pre-Trip &nbsp; ☐ Post-Trip</span>
              </div>
            </div>
          </div>

          <div class="dvir-form-section">
            <h4>Vehicle Information</h4>
            <div class="dvir-form-row">
              <div class="dvir-field">
                <label>Unit #:</label>
                <span class="dvir-blank-line"></span>
              </div>
              <div class="dvir-field">
                <label>Odometer:</label>
                <span class="dvir-blank-line"></span>
              </div>
            </div>
            <div class="dvir-form-row">
              <div class="dvir-field full-width">
                <label>Make/Model:</label>
                <span class="dvir-blank-line"></span>
              </div>
            </div>
          </div>

          <div class="dvir-form-section">
            <h4>Inspection Checklist</h4>
            <p class="dvir-instruction">Check (✓) if satisfactory. Mark (X) if defective. Leave blank if not applicable.</p>
            <div class="dvir-checklist-grid">
              <div class="dvir-check-item">☐ Lights (head, tail, brake, turn)</div>
              <div class="dvir-check-item">☐ Brakes (service, parking, connections)</div>
              <div class="dvir-check-item">☐ Tires (condition, inflation, tread)</div>
              <div class="dvir-check-item">☐ Mirrors (condition, adjustment)</div>
              <div class="dvir-check-item">☐ Horn (operation)</div>
              <div class="dvir-check-item">☐ Windshield Wipers (condition, operation)</div>
              <div class="dvir-check-item">☐ Steering (free play, operation)</div>
              <div class="dvir-check-item">☐ Fluid Levels (oil, coolant, fuel)</div>
              <div class="dvir-check-item">☐ Belts & Hoses (condition, leaks)</div>
              <div class="dvir-check-item">☐ Emergency Equipment (triangles, extinguisher)</div>
              <div class="dvir-check-item">☐ Coupling Devices (5th wheel, pintle, safety chains)</div>
              <div class="dvir-check-item">☐ Exhaust System (leaks, condition)</div>
            </div>
          </div>

          <div class="dvir-form-section">
            <h4>Defects / Remarks</h4>
            <p class="dvir-instruction">Describe any defects found. Safety defects must be corrected before vehicle operation.</p>
            <div class="dvir-remarks-box"></div>
          </div>

          <div class="dvir-form-section">
            <h4>Certification</h4>
            <div class="dvir-certification">
              <p>☐ <strong>No Defects</strong> - I have inspected this vehicle and found no defects.</p>
              <p>☐ <strong>Defects Found</strong> - I have inspected this vehicle and found the defects noted above.</p>
            </div>
          </div>

          <div class="dvir-form-section dvir-signatures">
            <div class="dvir-signature-block">
              <label>Driver Signature:</label>
              <span class="dvir-signature-line"></span>
              <div class="dvir-print-name">
                <label>Print Name:</label>
                <span class="dvir-blank-line-sm"></span>
              </div>
            </div>
            <div class="dvir-signature-block">
              <label>Reviewer Signature (if defects corrected):</label>
              <span class="dvir-signature-line"></span>
              <div class="dvir-print-name">
                <label>Date Corrected:</label>
                <span class="dvir-blank-line-sm"></span>
              </div>
            </div>
          </div>

          <div class="dvir-form-footer">
            <p>This form must be retained for 3 months per FMCSA regulations.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" onclick="window.PretripModule.closeModal('blank-dvir')">Close</button>
        </div>
      </div>
    </div>

    <!-- Safety Hold Warning Modal -->
    <div class="modal hidden" id="modal-safety-hold">
      <div class="modal-content modal-small">
        <div class="modal-header modal-header-danger">
          <h3><span class="material-icons">warning</span> Vehicle on Safety Hold</h3>
        </div>
        <div class="modal-body">
          <div class="safety-hold-info">
            <p class="safety-hold-unit" id="safety-hold-unit"></p>
            <p class="safety-hold-message">This vehicle has unresolved safety defects that must be cleared by a mechanic before operation.</p>
            <div class="safety-hold-defects" id="safety-hold-defects"></div>
            <p class="safety-hold-action">Contact maintenance to resolve.</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" onclick="window.PretripModule.closeModal('safety-hold')">I Understand</button>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// INSPECTION CHECKLIST ITEMS
// ============================================
// Full DOT DVIR checklist per FMCSA 396.11/396.13
// Grouped by category for UX — drivers see collapsible sections
// TASK-9741 — the three base lists moved VERBATIM to the shared module
// (functions/src/shared/inspectionTemplates.js INSPECTION_BASE_ITEMS) so the
// driver form and the server-printed blank checklist read ONE list. A tenant
// template only ever ADDS items after these; it never edits or removes them.
const TRUCK_INSPECTION_ITEMS = window.InspectionTemplates.inspectionBaseItems('truck');
const TRAILER_INSPECTION_ITEMS = window.InspectionTemplates.inspectionBaseItems('trailer');
const EQUIPMENT_INSPECTION_ITEMS = window.InspectionTemplates.inspectionBaseItems('equipment');

// Legacy constant for backwards compatibility
const INSPECTION_ITEMS = TRUCK_INSPECTION_ITEMS;

function getInspectionItems(assetType) {
  if (assetType === 'trailer') return TRAILER_INSPECTION_ITEMS;
  if (assetType === 'equipment') return EQUIPMENT_INSPECTION_ITEMS;
  return TRUCK_INSPECTION_ITEMS;
}

// ============================================
// TASK-9741 — UNIT CHECKS (per type / per unit checklist)
// ============================================
// The unit record behind a picked asset id (null when not loaded).
function _inspectionUnitRecord(assetType, assetId) {
  if (!assetId) return null;
  const list = assetType === 'equipment' ? PretripState.assets
    : assetType === 'trailer' ? PretripState.trailers : PretripState.trucks;
  return (list || []).find(u => u && String(u.id) === String(assetId)) || null;
}

// Which template applies: unit override > vehicle type > none (shared rule).
// Equipment matches on its category; trucks + trailers on their free-text type.
// TASK-10135 — every item for the unit, both inspections (renderUnitChecks
// picks the ones for the Pre-Trip / Post-Trip on the form).
function resolveUnitInspectionTemplate(assetType, assetId) {
  const IT = window.InspectionTemplates;
  if (!IT || !assetId) return null;
  const rec = _inspectionUnitRecord(assetType, assetId);
  const unitType = rec ? (assetType === 'equipment' ? rec.category : rec.type) : '';
  return IT.resolveInspectionTemplate(window.AppState?.settings?.inspectionTemplates, {
    assetKind: assetType, unitId: String(assetId), unitType: unitType || '',
  });
}

// TASK-10135 — the inspection picked on the form ('pre' unless Post-Trip).
function _inspectionPhase() {
  return document.getElementById('inspection-type')?.value === 'post' ? 'post' : 'pre';
}

// TASK-10135 — unit-check answers for THIS form, per unit. They outlive a
// repaint (Pre/Post switch, picking another unit and coming back) and are
// dropped only when a new form opens or the asset type changes — the same
// moments the base checklist is redrawn.
function _resetUnitCheckAnswers() {
  PretripState.unitCheckAnswers = Object.create(null);
  // And forget the block on screen, so the next repaint can't save the old
  // form's ticks back into the fresh answers.
  PretripState.unitChecks = null;
}

function _unitCheckAnswers(assetType, assetId) {
  if (!PretripState.unitCheckAnswers) PretripState.unitCheckAnswers = Object.create(null);
  const k = assetType + '|' + String(assetId || '');
  return PretripState.unitCheckAnswers[k] || (PretripState.unitCheckAnswers[k] = Object.create(null));
}

// TASK-10135 — switching Pre-Trip / Post-Trip re-picks the unit checks.
function onInspectionTypeChange() {
  const assetType = document.getElementById('inspection-asset-type')?.value || 'truck';
  const assetId = document.getElementById('inspection-truck')?.value || '';
  renderUnitChecks(assetType, assetId);
}

// Paint (or hide) the "Unit checks" block for the picked unit. Hidden — and
// the form exactly as before — when no template applies. Always shown when one
// does, independent of the optional checkbox system: these are the checks the
// office asked for on THIS unit.
// TASK-10135: shows the items for the picked inspection (Both + Pre, or Both
// + Post) PLUS any item already marked FAILED on the other one — a failure is
// never dropped by switching Pre/Post (COD r1/r2: 396.11 — a defect the driver
// found gets reported). Answers come back as the driver left them.
function renderUnitChecks(assetType, assetId) {
  const box = document.getElementById('unit-checks-section');
  const IT = window.InspectionTemplates;
  const prev = PretripState.unitChecks;
  if (box && prev) {
    // Save what is on screen into the answers of the unit it was drawn for.
    const prevAnswers = _unitCheckAnswers(prev.assetType, prev.assetId);
    box.querySelectorAll('input[data-tpl-key]').forEach(cb => { prevAnswers[cb.dataset.tplKey] = cb.checked; });
  }
  const kept = _unitCheckAnswers(assetType, assetId);
  const full = resolveUnitInspectionTemplate(assetType, assetId);
  const phase = _inspectionPhase();
  const keyOf = (t, it) => IT.inspectionTemplateItemKey(it.tplId || t.id, it.id);
  const onThis = it => { const p = IT.inspectionItemPhase(it.phase); return p === 'both' || p === phase; };
  let hit = null;
  if (full) {
    const items = full.template.items.filter(it => onThis(it) || kept[keyOf(full.template, it)] === false);
    if (items.length) hit = { template: { ...full.template, items }, matchedBy: full.matchedBy };
  }
  PretripState.unitChecks = hit ? { template: hit.template, matchedBy: hit.matchedBy, assetType, assetId: String(assetId) } : null;
  if (!box) return;
  if (!hit) { box.innerHTML = ''; box.classList.add('hidden'); return; }
  const t = hit.template;
  box.innerHTML = `
    <div style="display:flex;align-items:center;gap:8px;margin-bottom:6px;">
      <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">fact_check</span>
      <span style="font-weight:700;font-size:0.9rem;color:var(--text-primary);">Unit checks — ${escapeHtml(t.name)}</span>
    </div>
    <p class="checklist-hint" style="margin:0 0 6px;">✓ Checked = OK  |  ☐ Unchecked = Failed</p>
    <div class="checklist-grid">
      ${t.items.map(it => {
        const key = keyOf(t, it);
        const carried = !onThis(it) ? (IT.inspectionItemPhase(it.phase) === 'pre' ? 'Pre-trip item, still reported' : 'Post-trip item, still reported') : '';
        const tags = [it.required ? 'Out of service if failed' : '', it.photoRequired ? 'Photo if failed' : '', carried].filter(Boolean).join(' · ');
        const ok = kept[key] !== false;
        return `
        <label class="checklist-item${ok ? '' : ' failed'}" data-tpl-item="${escapeHtml(key)}">
          <input type="checkbox" ${ok ? 'checked' : ''} data-tpl-key="${escapeHtml(key)}" onchange="this.closest('.checklist-item').classList.toggle('failed', !this.checked)">
          <span class="checklist-icon"><span class="material-icons">${it.required ? 'report' : 'build'}</span></span>
          <span class="checklist-label">${escapeHtml(it.label)}${tags ? `<br><span style="font-size:0.72rem;color:var(--text-secondary);">${escapeHtml(tags)}</span>` : ''}</span>
        </label>`;
      }).join('')}
    </div>`;
  box.classList.remove('hidden');
}

// ============================================
// MODAL HANDLERS
// ============================================
function setupModals() {
  // Close modal on backdrop click (but not for form modals to prevent data loss)
  document.querySelectorAll('.modal').forEach(modal => {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        // Don't close modals with form inputs on backdrop click (prevents data loss)
        const hasFormInputs = modal.querySelector('input, select, textarea');
        if (hasFormInputs) return;

        modal.classList.add('hidden');
      }
    });
  });
}

// ============================================
// DATA SUBSCRIPTIONS
// ============================================
function subscribeToData(tenantId) {
  const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;

  // Subscribe to inspections — last 30 days only. Older docs load on demand
  // via the "Load older" button at the bottom of the list. Previously this
  // query loaded ALL pretrips ever (comment said "30 days" but the where
  // filter was missing), pulling ~2.3 MB / 200+ docs on every view open at
  // TITS volume. Now caps at ~one month's worth.
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  // TASK-544 — data-exposure fix. A PIN driver must only ever receive their
  // OWN inspections. Scope at the QUERY boundary (not a DOM filter — that's
  // privacy theater since the data would still reach the browser). Only the
  // driver-mode path filters; founder/admin/dispatcher full view is untouched.
  const _isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || (window.AppState?.driverModePin === true);
  const _driverScoped = _isDriverMode;
  const _driverPersonId = window.AppState?.driverPersonId || null;

  // Re-leak guard: a driver-scoped session whose PIN didn't resolve a roster
  // person must NOT fall back to the unfiltered tenant-wide query. Show empty.
  if (_driverScoped && !_driverPersonId) {
    PretripState.inspections = [];
    PretripState._pretripsLoaded = true;
    PretripState._olderExhausted = true;
    renderInspectionsList();
    updateCounts();
    setTimeout(drainPretripRecordFocus, 0); // TASK-9658 S1 — resolves to "not found"
  } else {
    const inspectionsQuery = _driverScoped
      ? query(
          collection(db, 'tenants', tenant, 'pretrips'),
          where('driverId', '==', _driverPersonId),
          where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
          orderBy('createdAt', 'desc')
        )
      : query(
          collection(db, 'tenants', tenant, 'pretrips'),
          where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
          orderBy('createdAt', 'desc')
        );

    const unsubInspections = onSnapshot(inspectionsQuery, (snapshot) => {
      PretripState.inspections = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      PretripState._pretripsLoaded = true;
      _syncLinkedTaskWatchers(); // TASK-9405
      renderInspectionsList();
      updateCounts();
      drainPretripRecordFocus(); // TASK-9658 S1
    }, (error) => {
      console.error('[Pretrip] Error loading inspections:', error);
      drainPretripRecordFocus(); // TASK-9658 S1 — the by-id read decides
    });
    PretripState.unsubscribers.push(unsubInspections);
  }

  // Subscribe to people (drivers)
  const peopleQuery = query(
    collection(db, 'tenants', tenant, 'people'),
    orderBy('name')
  );
  const unsubPeople = onSnapshot(peopleQuery, (snapshot) => {
    PretripState.people = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    updateDriverDropdown();
  });
  PretripState.unsubscribers.push(unsubPeople);

  // Subscribe to trucks
  const trucksQuery = query(
    collection(db, 'tenants', tenant, 'trucks'),
    orderBy('unit')
  );
  const unsubTrucks = onSnapshot(trucksQuery, (snapshot) => {
    PretripState.trucks = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('[Pretrip] Trucks loaded:', PretripState.trucks.length);
    updateTruckDropdown();
    renderInspectionsList(); // Re-render cards now that truck names are available
  }, (error) => {
    console.error('[Pretrip] Error loading trucks:', error);
  });
  PretripState.unsubscribers.push(unsubTrucks);

  // Subscribe to trailers
  const trailersRef = collection(db, 'tenants', tenant, 'trailers');
  const unsubTrailers = onSnapshot(trailersRef, (snapshot) => {
    PretripState.trailers = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(t => t.active !== false)
      .sort((a, b) => (a.unit || a.name || '').localeCompare(b.unit || b.name || ''));
    console.log('[Pretrip] Trailers loaded:', PretripState.trailers.length);
    updateTruckDropdown(); // Refresh dropdown with trailer data
    renderInspectionsList(); // Re-render cards now that trailer names are available
  }, (error) => {
    console.error('[Pretrip] Error loading trailers:', error);
    PretripState.trailers = [];
  });
  PretripState.unsubscribers.push(unsubTrailers);

  // Subscribe to assets (equipment)
  const assetsRef = collection(db, 'tenants', tenant, 'assets');
  const unsubAssets = onSnapshot(assetsRef, (snapshot) => {
    PretripState.assets = snapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(a => a.active !== false)
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  });
  PretripState.unsubscribers.push(unsubAssets);

  // Subscribe to pending return inspections — dispatcher/mechanic concept only.
  // TASK-698: skip for driver-scoped (PIN) sessions. The returnInspections read
  // rule requires isTenantMember, which a driver-PIN anon session is NOT, so this
  // listener throws permission-denied on every driver #/pretrip load and floods
  // errorLogs/debugLogs. Drivers don't display or need return inspections.
  if (!_driverScoped) {
    const riRef = collection(db, 'tenants', tenant, 'returnInspections');
    const unsubRI = onSnapshot(riRef, (snapshot) => {
      PretripState.returnInspections = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(ri => ri.status === 'pending');
    });
    PretripState.unsubscribers.push(unsubRI);
  }

  // Register fields with Smart Assistant
  if (window.SmartAssistant) {
    const SA = window.SmartAssistant;
    SA.register('dvir-defects', () => [], 'DVIR', { formatOnly: true });
    SA.register('maintenance-notes', () => [], 'DVIR', { formatOnly: true });
    SA.register('dvir-oil-brand', () => [], 'DVIR', { formatOnly: true });
    SA.register('unknown-driver-name', () => [], 'DVIR', { formatOnly: true });
  }
}

// ============================================
// RENDER FUNCTIONS
// ============================================
function applySearch(inspections) {
  if (!PretripState.searchQuery) return inspections;
  const q = PretripState.searchQuery;
  return inspections.filter(i => {
    const unit = (i.truckUnit || '').toLowerCase();
    const driver = (i.driverName || '').toLowerCase();
    const status = (i.status || '').toLowerCase();
    // Resolve display name from state
    let display = '';
    if (i.assetType === 'equipment') {
      const asset = PretripState.assets.find(a => a.id === i.truckId);
      display = (asset?.name || '').toLowerCase();
    } else {
      const truck = i.truckId ? PretripState.trucks.find(t => t.id === i.truckId) : null;
      display = (truck?.nickname || truck?.unit || '').toLowerCase();
    }
    return unit.includes(q) || driver.includes(q) || status.includes(q) || display.includes(q);
  });
}

function resolveAssetDisplay(inspection) {
  if (inspection.assetType === 'equipment') {
    const asset = PretripState.assets.find(a => a.id === inspection.truckId);
    return { unit: asset?.name || inspection.truckUnit || '?', name: '', icon: 'precision_manufacturing' };
  }
  if (inspection.assetType === 'trailer') {
    const trailer = PretripState.trailers.find(t => t.id === inspection.truckId);
    return { unit: trailer?.unit || inspection.truckUnit || '?', name: trailer?.nickname || '', icon: 'rv_hookup' };
  }
  const truck = inspection.truckId ? PretripState.trucks.find(t => t.id === inspection.truckId) : null;
  const unit = truck?.unit || inspection.truckUnit || '?';
  const name = truck?.nickname || [truck?.make, truck?.model].filter(Boolean).join(' ') || '';
  return { unit, name, icon: 'local_shipping' };
}

// TASK-1289 — the ONE display-time resolver for an inspection. The list is
// ordered by `createdAt` (Firestore ingest time) but every card RENDERS
// `submittedAt` (when the driver actually did the walk-around), so a late or
// offline-synced pre-trip sorted to the top while showing an older time —
// Chris: "interesting that the inspections section has this Jul 20th dvir at
// the top?". Sort key and display key now come from the same function so they
// cannot drift again. Tolerates a Firestore Timestamp, a plain JS Date (legacy
// GAS imports), or nothing at all.
function inspectionDisplayDate(i) {
  const raw = i?.submittedAt || i?.createdAt || null;
  const d = raw?.toDate?.() || (raw instanceof Date ? raw : (raw ? new Date(raw) : null));
  return (d && !isNaN(d.getTime())) ? d : new Date(0);
}

// TASK-4700 — the DVIR list opens on DEFECTS. Single source of truth for the
// default so the chip markup, the render default, and every
// "read the active chip" fallback below can never drift apart (a drifted
// fallback would silently reset the user to All on the paging path).
const PRETRIP_DEFAULT_FILTER = 'defects';

// TASK-9405 — the no-arg default is the ACTIVE chip (which itself falls back to
// PRETRIP_DEFAULT_FILTER). It used to be the default filter outright, so every
// live re-render (the 30-day onSnapshot, trucks/trailers loading, and now a
// repair task completing) quietly painted the Defects set under whatever chip
// the user had open — e.g. the All chip lit while showing only open defects.
function renderInspectionsList(filter = _activeFilter()) {
  const container = document.getElementById('pretrip-list');
  if (!container) return;

  // TASK-4712 — ONE predicate, shared with _visibleInspections(). It used to be
  // inlined here and copied there; COD flagged that as the drift this file's own
  // comments keep trying to prevent, and it is worse than cosmetic: the bulk bar
  // decides what "selected" MEANS from its copy, so the two drifting apart lets
  // a user act on a set the list never showed.
  let inspections = visibleInspectionsFor(filter);

  // TASK-4712 — prune the selection to what is actually on screen, on EVERY
  // render. This is the single choke point every path runs through (tab change,
  // search keystroke, and the live 30-day onSnapshot firing on its own), so
  // pruning here closes all three at once instead of patching each caller.
  // Without it a user selects on one tab, switches, and bulk-archives records
  // they can no longer see.
  if (PretripState._selectMode) {
    if (PretripState._selected.size) {
      const onScreen = new Set(inspections.map(i => i.id));
      PretripState._selected.forEach(id => { if (!onScreen.has(id)) PretripState._selected.delete(id); });
    }
    // Re-sync BEFORE the empty-state early-returns below, so every path lands
    // here. The button's LABEL AND ACTION are derived from `filter`, not from a
    // value captured when select mode opened — otherwise switching to Archived
    // mid-selection leaves a button that says Archive and archives.
    _syncBulkBar(filter);
  }

  // TASK-1289 — render-only re-sort by the time the card actually SHOWS.
  // Deliberately does NOT touch PretripState.inspections / .olderInspections or
  // getAllInspections(): `loadOlderInspections` pages on `createdAt` as its
  // cursor and that must stay the natural ingest order. Known limitation: this
  // sorts only the already-loaded window, so paging still walks `createdAt`.
  inspections = inspections.slice().sort((a, b) => inspectionDisplayDate(b) - inspectionDisplayDate(a));

  if (inspections.length === 0) {
    if (!PretripState._pretripsLoaded) {
      container.innerHTML = `
        <div style="text-align:center;padding:var(--spacing-xl);">
          <div class="btn-spinner" style="width:32px;height:32px;margin:0 auto var(--spacing-md);"></div>
          <div style="color:var(--text-secondary);font-size:0.875rem;">Loading...</div>
        </div>
      `;
    } else {
      // TASK-4700 — a defects-FIRST default must never look like an empty app.
      // A carrier with a clean fleet (the common case: 99% of DVIRs pass) would
      // otherwise open Inspections and see "No inspections", which reads as data
      // loss / a broken view. When the only reason the list is empty is that
      // nothing is wrong, say so plainly and hand them the way to everything
      // else. Not shown while searching — an empty SEARCH is a different answer.
      const _totalLoaded = getAllInspections().filter(i => i.archived !== true).length;
      if (filter === 'archived' && !PretripState.searchQuery) {
        container.innerHTML = `
          <div class="empty-state">
            <div class="empty-state-icon">🗄️</div>
            <div class="empty-state-title">Nothing archived</div>
            <div class="empty-state-text" style="color:var(--text-secondary);">
              Archived inspections are kept and show up here, so you can always get them back.
            </div>
          </div>
        `;
      } else if (filter === 'defects' && !PretripState.searchQuery && _totalLoaded > 0) {
        // The live subscription only loads the last 30 days, so "no defects"
        // can mean "none recently". Say that, and keep the Load-older path
        // reachable from here — otherwise a defects-first default would strand
        // a user with older defects on a dead-end screen.
        const _more = !PretripState._olderExhausted;
        // TASK-9405 — "passed with nothing reported" is false once a loaded
        // DVIR carries a defect that has since been repaired and signed off.
        // Those left this tab (closed), they did not pass: say which it is.
        const _closedDefects = getAllInspections()
          .filter(i => i.archived !== true && i.status === 'defects').length;
        const _emptyText = _closedDefects > 0
          ? `Every defect in the ${_totalLoaded} loaded inspection${_totalLoaded === 1 ? '' : 's'} has been repaired and signed off by the mechanic and the next driver (${_closedDefects} closed). Closed defect reports are kept — find them under All.`
          : `All ${_totalLoaded} loaded inspection${_totalLoaded === 1 ? '' : 's'} passed with nothing reported. Clean inspections are kept — they're just out of the way.`;
        container.innerHTML = `
          <div class="empty-state">
            <div class="empty-state-icon">✅</div>
            <div class="empty-state-title">No open defects</div>
            <div class="empty-state-text" style="color:var(--text-secondary);">
              ${_emptyText}${_more ? ' Only the last 30 days are loaded.' : ''}
            </div>
            <button class="btn btn-secondary" style="margin-top:var(--spacing-md);"
              onclick="window.PretripModule.filterInspections('all')">
              Show all ${_totalLoaded} inspection${_totalLoaded === 1 ? '' : 's'}
            </button>
          </div>
          ${_more ? renderLoadOlderFooter() : ''}
        `;
      } else {
        container.innerHTML = `
          <div class="empty-state">
            <div class="empty-state-icon">📋</div>
            <div class="empty-state-title">No inspections ${filter !== 'all' ? 'with ' + filter : ''}${PretripState.searchQuery ? ' matching "' + escapeHtml(PretripState.searchQuery) + '"' : ''}</div>
            <div class="empty-state-text">Complete an inspection to get started.</div>
          </div>
        `;
      }
    }
    return;
  }

  container.innerHTML = inspections.map(inspection => {
    const date = inspectionDisplayDate(inspection); // TASK-1289 — same key the sort above uses
    const dateStr = formatDate(date);
    const timeStr = formatTime(date);
    const hasDefects = inspection.status === 'defects';
    const defectCount = inspection.defects?.length || 0;
    const assetInfo = resolveAssetDisplay(inspection);
    const cardTruckDisplay = assetInfo.name ? `${assetInfo.unit} — ${assetInfo.name}` : assetInfo.unit;
    const _sel = PretripState._selectMode; // TASK-4712 — in select mode the card selects, it does not navigate

    return `
      <div class="pretrip-card ${hasDefects ? 'has-defects' : 'pass'}" data-id="${inspection.id}" onclick="${_sel
          ? `window.PretripModule.toggleSelected('${_ea(inspection.id)}')`
          : `window.PretripModule.viewInspection('${_ea(inspection.id)}')`}">
        <div class="pretrip-card-status">
          ${_sel
            ? `<input type="checkbox" class="pretrip-select-box" ${PretripState._selected.has(inspection.id) ? 'checked' : ''}
                 onclick="event.stopPropagation();window.PretripModule.toggleSelected('${_ea(inspection.id)}')"
                 style="width:20px;height:20px;cursor:pointer;">`
            : `<span class="material-icons">${hasDefects ? 'warning' : 'check_circle'}</span>`}
        </div>
        <div class="pretrip-card-info">
          <div class="pretrip-card-header">
            <span class="pretrip-card-truck">${escapeHtml(cardTruckDisplay)}</span>
            <span class="pretrip-card-type">${inspection.type === 'pre' ? 'Pre-Trip' : 'Post-Trip'}</span>
            ${inspection.archived ? `<span class="pretrip-card-type" style="background:rgba(100,116,139,0.14);color:var(--text-secondary);">Archived</span>` : ''}
          </div>
          <div class="pretrip-card-meta">
            <span><span class="material-icons">person</span> ${escapeHtml(inspection.driverName || 'Unknown')}</span>
            <span><span class="material-icons">schedule</span> ${dateStr} at ${timeStr}</span>
            ${inspection.mileage ? `<span><span class="material-icons">speed</span> ${inspection.mileage.toLocaleString()} mi</span>` : ''}
          </div>
          ${hasDefects ? `
            <div class="pretrip-card-defects">
              <span class="defect-badge">${defectCount > 0 ? `${defectCount} defect${defectCount > 1 ? 's' : ''}` : 'Defects noted'}</span>
              ${dvirClosureBadgeHtml(dvirDefectClosure(inspection, PretripState._linkedTasks.get(inspection.id)))}
              ${inspection.defects?.slice(0, 3).map(d => `<span class="defect-item">${escapeHtml(d)}</span>`).join('') || ''}
            </div>
          ` : ''}
          ${inspection.photos?.length ? `
            <div class="pretrip-card-photos">
              ${inspection.photos.slice(0, 3).map(p => `<img src="${escapeHtml(p.thumbUrl)}" alt="DVIR photo" class="pretrip-thumb" onclick="event.stopPropagation();window.PretripModule.showFullPhoto('${escJsAttr(p.fullUrl)}')">`).join('')}
              ${inspection.photos.length > 3 ? `<span class="pretrip-photo-more">+${inspection.photos.length - 3}</span>` : ''}
            </div>
          ` : ''}
        </div>
      </div>
    `;
  }).join('') + renderLoadOlderFooter();
}

// "Load older inspections" footer — only shown when the live 30-day window
// isn't proven empty (avoids showing the button on a brand-new tenant) and
// pagination hasn't been exhausted. One-shot getDocs per click via
// loadOlderInspections() below.
function renderLoadOlderFooter() {
  if (!PretripState._pretripsLoaded) return '';
  if (PretripState._olderExhausted) {
    if (PretripState.olderInspections.length === 0) return '';
    return `
      <div style="text-align:center;padding:18px 12px;color:var(--text-muted);font-size:0.82rem;">
        That's everything — no older inspections.
      </div>
    `;
  }
  const label = PretripState._olderLoading
    ? '<span class="btn-spinner" style="width:14px;height:14px;vertical-align:middle;"></span> Loading…'
    : '<span class="material-icons" style="font-size:16px;vertical-align:middle;">history</span> Load older inspections';
  return `
    <div style="text-align:center;padding:14px 12px;">
      <button type="button" class="btn btn-text"
        onclick="window.PretripModule.loadOlderInspections()"
        ${PretripState._olderLoading ? 'disabled' : ''}
        style="padding:8px 16px;font-size:0.85rem;color:var(--brand-blue);background:transparent;border:1px solid var(--border-light);border-radius:8px;cursor:pointer;font-weight:600;">
        ${label}
      </button>
    </div>
  `;
}

// One-shot read of pretrips older than the oldest doc currently in state.
// Uses where(createdAt < cursor) + orderBy desc + limit pagination — no
// startAfter import needed (createdAt is the natural cursor).
async function loadOlderInspections() {
  if (PretripState._olderLoading || PretripState._olderExhausted) return;
  const _viewGenAtStart = PretripState._viewGen; // TASK-9405 (COD)

  const tenant = (window.AppState?.tenant === 'demo-tenant') ? 'demo' : window.AppState?.tenant;
  if (!tenant) return;

  // TASK-544 — keep pagination driver-scoped. Mirror the live-query scoping so
  // "Load older" can never page in another driver's inspections.
  const _isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || (window.AppState?.driverModePin === true);
  const _driverScoped = _isDriverMode;
  const _driverPersonId = window.AppState?.driverPersonId || null;
  // Re-leak guard: driver-scoped session without a resolved person pages nothing.
  if (_driverScoped && !_driverPersonId) {
    PretripState._olderExhausted = true;
    return;
  }

  // Cursor: oldest createdAt across the live + already-loaded-older sets.
  // Both lists are sorted desc by createdAt, so the cursor is the last
  // element of olderInspections if any, else last of inspections.
  const combined = getAllInspections();
  const oldest = combined[combined.length - 1];
  const cursorTs = oldest?.createdAt;
  if (!cursorTs) {
    // No live docs yet to anchor against — nothing to page past.
    PretripState._olderExhausted = true;
    renderInspectionsList(
      document.querySelector('.pretrip-filter.active')?.dataset?.filter || PRETRIP_DEFAULT_FILTER
    );
    return;
  }

  PretripState._olderLoading = true;
  renderInspectionsList(
    document.querySelector('.pretrip-filter.active')?.dataset?.filter || PRETRIP_DEFAULT_FILTER
  );

  try {
    const olderQ = _driverScoped
      ? query(
          collection(db, 'tenants', tenant, 'pretrips'),
          where('driverId', '==', _driverPersonId),
          where('createdAt', '<', cursorTs),
          orderBy('createdAt', 'desc'),
          limit(PretripState._olderPageSize)
        )
      : query(
          collection(db, 'tenants', tenant, 'pretrips'),
          where('createdAt', '<', cursorTs),
          orderBy('createdAt', 'desc'),
          limit(PretripState._olderPageSize)
        );
    const snap = await getDocs(olderQ);
    // TASK-9405 (COD r2) — the view was torn down while this page loaded:
    // cleanup already reset olderInspections / _olderExhausted. Drop the page
    // rather than write it into the next view's state.
    if (_viewGenAtStart !== PretripState._viewGen) return;
    const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    PretripState.olderInspections.push(...docs);
    if (snap.size < PretripState._olderPageSize) {
      PretripState._olderExhausted = true;
    }
  } catch (e) {
    console.error('[Pretrip] loadOlderInspections failed:', e);
    window.showToast?.('Could not load older inspections', 'error');
  } finally {
    PretripState._olderLoading = false;
    // TASK-9405 — older pages carry defects too. Skipped if the view was torn
    // down while this page was loading (cleanup already stopped every listener).
    if (_viewGenAtStart === PretripState._viewGen) _syncLinkedTaskWatchers();
    renderInspectionsList(
      document.querySelector('.pretrip-filter.active')?.dataset?.filter || PRETRIP_DEFAULT_FILTER
    );
    updateCounts();
  }
}

// ============================================
// TASK-9554 — WHERE AN INSPECTION WAS SUBMITTED
// ============================================
// One position fix, taken at Submit (never on page load — iOS Safari shows its
// permission prompt the moment getCurrentPosition is called). No preamble, no
// retry, no blocking: denied / unavailable / slow resolve to a status and the
// inspection saves anyway. Stored on the pretrip doc itself, so the audience
// is exactly the inspection's (pretrips read rule) and the record is frozen
// with the rest of the DVIR (not on pretripPostSubmitFieldsOk's allowlist).
// Tenant setting captureGpsOnInspection, default OFF (opt-in, like captureGpsOnClock;
// COD 2026-10-01: no surprise location prompt for every tenant's drivers).
const INSPECTION_GPS_TIMEOUT_MS = 5000;   // the browser's own timeout
const INSPECTION_GPS_HARD_CAP_MS = 6000;  // ours — covers a prompt left unanswered
const INSPECTION_GPS_STATUS_TEXT = {
  denied: 'location permission denied',
  unavailable: 'location unavailable',
  timeout: 'location timed out',
};

function _inspectionGpsEnabled(settings) {
  return !!settings && settings.captureGpsOnInspection === true;
}

// A value counts only if it is already a finite NUMBER. Number(null) is 0,
// which would print a pin at 0,0 or "±0 m" (COD Code Reviewer).
function _finiteNum(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : NaN;
}

function _roundCoord(v) {
  return Math.round(v * 1e4) / 1e4; // 4 dp ≈ 11 m — "at the unit", not a street address
}

// position → the fields saved on the pretrip. Anything out of range is
// 'unavailable', never a bogus pin.
function _inspectionLocationFromPosition(pos) {
  const c = pos && pos.coords;
  const lat = _finiteNum(c && c.latitude);
  const lng = _finiteNum(c && c.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return { locationStatus: 'unavailable' };
  }
  const acc = _finiteNum(c.accuracy);
  const ts = _finiteNum(pos.timestamp);
  return {
    locationStatus: 'captured',
    location: {
      lat: _roundCoord(lat),
      lng: _roundCoord(lng),
      accuracy: Number.isFinite(acc) && acc >= 0 ? Math.round(acc) : null,
      capturedAt: Timestamp.fromMillis(Number.isFinite(ts) && ts > 0 ? ts : Date.now()),
    },
  };
}

// Always resolves, never throws. `geo` is navigator.geolocation (injectable for tests).
function captureInspectionLocation(geo) {
  return new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const done = (r) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      resolve(r);
    };
    if (!geo || typeof geo.getCurrentPosition !== 'function') {
      done({ locationStatus: 'unavailable' });
      return;
    }
    timer = setTimeout(() => done({ locationStatus: 'timeout' }), INSPECTION_GPS_HARD_CAP_MS);
    try {
      geo.getCurrentPosition(
        (pos) => done(_inspectionLocationFromPosition(pos)),
        (err) => done({ locationStatus: err && err.code === 1 ? 'denied' : err && err.code === 3 ? 'timeout' : 'unavailable' }),
        { enableHighAccuracy: true, timeout: INSPECTION_GPS_TIMEOUT_MS, maximumAge: 0 }
      );
    } catch (_) {
      done({ locationStatus: 'unavailable' });
    }
  });
}

// { text, url } for display; null for inspections that never had capture
// (pre-TASK-9554 or the tenant setting off). Re-validates the stored numbers:
// the doc is client-written, so nothing reaches a URL that isn't a finite coord.
function inspectionLocationSummary(inspection, locDoc) {
  const status = inspection && inspection.locationStatus;
  if (!status) return null;
  // Coordinates come from pretripLocations (office-only), passed in as locDoc.
  const loc = locDoc || {};
  const lat = _finiteNum(loc.lat);
  const lng = _finiteNum(loc.lng);
  if (status === 'captured' && Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lat) <= 90 && Math.abs(lng) <= 180) {
    const acc = _finiteNum(loc.accuracy);
    const coords = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    return {
      captured: true,
      // "device-reported": the fix comes from the driver's phone and can be spoofed (COD Adversary/Domain).
      text: `Submitted near ${coords} (${Number.isFinite(acc) && acc >= 0 ? `±${Math.round(acc).toLocaleString('en-US')} m, ` : ''}device-reported)`,
      url: `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(4)},${lng.toFixed(4)}`,
    };
  }
  return { captured: false, text: `Location not captured — ${INSPECTION_GPS_STATUS_TEXT[status] || 'location unavailable'}`, url: null };
}

// The small "Submitted near …" line for office views. Link out only. Empty in
// a driver session whichever caller asks (pretrip viewInspection, maintenance
// viewSourceDvir) — COD Edge Case Hunter.
const _inspectionLocCache = new Map(); // pretrip id -> coords doc | null (not readable / absent)

// Reads pretripLocations/{id}. Office-only by rule; anyone else gets a
// permission-denied, which we treat as "no coordinates" and cache.
async function _loadInspectionLocation(id) {
  if (_inspectionLocCache.has(id)) return _inspectionLocCache.get(id);
  let val = null;
  try {
    const tenant = window.AppState?.tenant;
    const snap = await getDoc(doc(db, 'tenants', tenant, 'pretripLocations', id));
    val = snap.exists() ? snap.data() : null;
  } catch (_) { val = null; }
  _inspectionLocCache.set(id, val);
  return val;
}

function _inspectionLocationLineInner(inspection, locDoc) {
  const s = inspectionLocationSummary(inspection, locDoc);
  if (!s) return '';
  const link = s.url
    ? ` · <a href="${escapeHtml(s.url)}" target="_blank" rel="noopener noreferrer" style="color:var(--brand-blue);">Map</a>`
    : '';
  return `<span class="material-icons" style="font-size:16px;" aria-hidden="true">${s.captured ? 'place' : 'location_off'}</span>`
    + `<span>${escapeHtml(s.text)}${link}</span>`;
}

// The small "Submitted near …" line for office views. Link out only. Empty in
// a driver session whichever caller asks. Coordinates are fetched from the
// office-only pretripLocations doc; the line fills in when the read lands.
// `id` defaults to inspection.id (maintenance's source-DVIR modal passes it).
function inspectionLocationLineHTML(inspection, id) {
  if (_isDriverSessionView()) return '';
  const pid = id || (inspection && inspection.id) || '';
  const wrapStyle = 'display:flex;align-items:center;gap:6px;font-size:0.8rem;color:var(--text-secondary);margin:6px 0 10px;';
  const status = inspection && inspection.locationStatus;
  if (!status) return '';
  if (status === 'captured' && pid) {
    if (!_inspectionLocCache.has(pid)) {
      _loadInspectionLocation(pid).then((locDoc) => {
        const inner = _inspectionLocationLineInner(inspection, locDoc);
        document.querySelectorAll('.dvir-location-line[data-loc-id]').forEach((el) => {
          if (el.getAttribute('data-loc-id') === pid) el.innerHTML = inner;
        });
      });
      return `<div class="dvir-location-line" data-loc-id="${escapeHtml(pid)}" style="${wrapStyle}"><span class="material-icons" style="font-size:16px;" aria-hidden="true">place</span><span>Location recorded — loading…</span></div>`;
    }
    const cached = _inspectionLocCache.get(pid);
    if (!cached) return ''; // captured but this session can't read it: say nothing
    return `<div class="dvir-location-line" data-loc-id="${escapeHtml(pid)}" style="${wrapStyle}">${_inspectionLocationLineInner(inspection, cached)}</div>`;
  }
  const inner = _inspectionLocationLineInner(inspection, null);
  return inner ? `<div class="dvir-location-line" style="${wrapStyle}">${inner}</div>` : '';
}

function _isDriverSessionView() {
  const a = window.AppState || {};
  return !!(a.driverMode || a.isPublicAccess || a.driverModePin === true);
}

function viewInspection(inspectionId) {
  const inspection = _findInspectionById(inspectionId);
  if (!inspection) return;

  const date = inspectionDisplayDate(inspection); // TASK-1289 — shared resolver
  const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const hasDefects = inspection.status === 'defects';
  const statusIcon = hasDefects ? 'warning' : 'check_circle';
  // TASK-10146 — a pass with non-safety maintenance notes is not "No Issues".
  const statusLabel = hasDefects ? 'Defects Found'
    : (String(inspection.maintenanceNotes || '').trim() ? 'Pass — maintenance note' : 'Pass — No Issues');
  const headerClass = hasDefects ? 'defects' : 'pass';

  // Build sections
  let sectionsHtml = '';

  // Audit-trail banner — when this DVIR is linked as proof of repair for one
  // or more citations (49 CFR 396), surface it so reviewers know this record
  // is part of the audit packet for those citations and edits should be
  // limited to acknowledgments / sign-offs (not the inspection content).
  const linkedCitationIds = Array.isArray(inspection.linkedCitationIds)
    ? inspection.linkedCitationIds.filter(Boolean)
    : [];
  if (linkedCitationIds.length > 0) {
    sectionsHtml += `
      <div class="dvir-detail-section" style="background:#EFF6FF;border:1px solid #BFDBFE;padding:10px 12px;margin-bottom:10px;">
        <div style="display:flex;align-items:flex-start;gap:8px;">
          <span class="material-icons" style="font-size:20px;color:#1E40AF;flex-shrink:0;">verified</span>
          <div style="flex:1;">
            <div style="font-weight:700;color:#1E40AF;font-size:0.85rem;">Audit Trail — Proof of Repair</div>
            <div style="color:#1E3A8A;font-size:0.78rem;margin-top:2px;line-height:1.4;">
              Linked to ${linkedCitationIds.length} roadside citation${linkedCitationIds.length === 1 ? '' : 's'} as proof of repair (49 CFR 396). This DVIR is part of the carrier's audit trail — edits to inspection content may compromise audit integrity. Acknowledgments and sign-offs are still allowed.
            </div>
          </div>
        </div>
      </div>`;
  }

  // Info grid with icons
  const typeLabel = inspection.type === 'pre' ? 'Pre-Trip' : 'Post-Trip';
  const assetLabel = inspection.assetType ? inspection.assetType.charAt(0).toUpperCase() + inspection.assetType.slice(1) : 'Truck';
  const detailAsset = resolveAssetDisplay(inspection);
  const truckDisplayName = detailAsset.name ? `${detailAsset.unit} — ${detailAsset.name}` : detailAsset.unit;
  sectionsHtml += `<div class="dvir-detail-info">
    <div class="dvir-detail-info-item"><span class="material-icons">person</span><strong>Driver</strong> ${escapeHtml(inspection.driverName || 'Unknown')}</div>
    <div class="dvir-detail-info-item"><span class="material-icons">${detailAsset.icon}</span><strong>Unit</strong> ${escapeHtml(truckDisplayName)} (${escapeHtml(assetLabel)})</div>
    <div class="dvir-detail-info-item"><span class="material-icons">assignment</span><strong>Type</strong> ${typeLabel}</div>
    <div class="dvir-detail-info-item"><span class="material-icons">event</span><strong>Date</strong> ${dateStr}</div>
    <div class="dvir-detail-info-item"><span class="material-icons">schedule</span><strong>Time</strong> ${timeStr}</div>
    ${inspection.mileage ? `<div class="dvir-detail-info-item"><span class="material-icons">speed</span><strong>Miles</strong> ${inspection.mileage.toLocaleString()}</div>` : ''}
    ${inspection.hours ? `<div class="dvir-detail-info-item"><span class="material-icons">hourglass_bottom</span><strong>Hours</strong> ${inspection.hours.toLocaleString()}</div>` : ''}
  </div>`;
  // TASK-9554 — office only; a PIN driver opening a crewmate's DVIR doesn't get the pin.
  if (!_isDriverSessionView()) sectionsHtml += inspectionLocationLineHTML(inspection);

  // Defects section
  if (hasDefects) {
    sectionsHtml += `<div class="dvir-detail-section defects">`;
    sectionsHtml += `<div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;">error</span> Safety Defects</div>`;
    if (inspection.defectNotes) {
      sectionsHtml += `<div style="font-size:0.82rem;white-space:pre-wrap;color:var(--text-primary);margin-bottom:6px;">${escapeHtml(inspection.defectNotes)}</div>`;
    }
    if (inspection.defects?.length > 0) {
      sectionsHtml += `<div style="margin-top:4px;">${inspection.defects.map(d => `<span class="dvir-detail-defect-chip">${escapeHtml(d)}</span>`).join('')}</div>`;
    }
    sectionsHtml += `</div>`;
  }

  // Maintenance notes
  if (inspection.maintenanceNotes) {
    sectionsHtml += `
      <div class="dvir-detail-section maint">
        <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;">build</span> Maintenance Notes</div>
        <div style="font-size:0.82rem;white-space:pre-wrap;color:var(--text-primary);">${escapeHtml(inspection.maintenanceNotes)}</div>
      </div>
    `;
  }

  // Checklist items
  if (inspection.useCheckboxSystem && inspection.itemsStatus) {
    const entries = Object.entries(inspection.itemsStatus);
    if (entries.length > 0) {
      sectionsHtml += `<div class="dvir-detail-section">
        <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;color:var(--text-secondary);">checklist</span> Checklist Items</div>
        <div class="dvir-detail-checklist">`;
      entries.forEach(([itemId, val]) => {
        const ok = val.ok;
        const label = inspection.items?.find?.(i => typeof i === 'string' && i.toLowerCase().includes(itemId.replace(/_/g, ' '))) || itemId.replace(/_/g, ' ');
        sectionsHtml += `<div class="dvir-detail-check-item">
          <span class="material-icons ${ok ? 'ok' : 'fail'}">${ok ? 'check_circle' : 'cancel'}</span>
          <span>${escapeHtml(typeof label === 'string' ? label : itemId)}</span>
        </div>`;
      });
      sectionsHtml += `</div></div>`;
    }
  }

  // Fluids
  if (inspection.fluids?.length > 0) {
    sectionsHtml += `<div class="dvir-detail-section">
      <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;color:#0E7490;">water_drop</span> Fluids Added</div>
      ${inspection.fluids.map(f => {
        const label = escapeHtml(f.type || 'Fluid');
        const detail = [f.amount ? escapeHtml(`${f.amount} ${f.unit || ''}`.trim()) : '', f.brand ? escapeHtml(f.brand) : '', f.oilWeight ? escapeHtml(f.oilWeight) : ''].filter(Boolean).join(' · ');
        return `<div class="dvir-detail-fluid-item"><span class="dvir-detail-fluid-badge">${label}</span>${detail ? `<span>${detail}</span>` : ''}</div>`;
      }).join('')}
    </div>`;
  }

  // Photos
  if (inspection.photos?.length > 0) {
    sectionsHtml += `<div class="dvir-detail-section">
      <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;color:var(--text-secondary);">photo_library</span> Photos (${inspection.photos.length})</div>
      <div class="dvir-detail-photos">
        ${inspection.photos.map(p => `<img class="dvir-detail-photo" src="${escapeHtml(p.thumbUrl || p.fullUrl)}" alt="DVIR photo" onclick="event.stopPropagation();window.PretripModule.showFullPhoto('${escJsAttr(p.fullUrl)}')">`).join('')}
      </div>
    </div>`;
  }

  // Driver Signature
  if (inspection.driverSignature) {
    sectionsHtml += `<div class="dvir-detail-section">
      <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;color:var(--text-secondary);">draw</span> Driver Signature</div>
      <div class="dvir-detail-sig"><img src="${escapeHtml(inspection.driverSignature)}" alt="Signature"></div>
    </div>`;
  } else if (inspection.noSignatureFallback) {
    sectionsHtml += `<div class="dvir-detail-no-sig"><span class="material-icons" style="font-size:14px;vertical-align:middle;margin-right:4px;">info</span>Submitted without signature</div>`;
  }

  // Mechanic / Reviewer sign-off
  if (inspection.mechanicSignature) {
    sectionsHtml += `<div class="dvir-detail-section">
      <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;color:var(--action-success);">verified</span> Reviewer Sign-Off</div>
      <div class="dvir-detail-sig"><img src="${escapeHtml(inspection.mechanicSignature)}" alt="Reviewer Signature"></div>
      ${inspection.mechanicSignedBy ? `<div class="dvir-detail-sig-by">Signed by: ${escapeHtml(inspection.mechanicSignedBy)}</div>` : ''}
    </div>`;
  } else if (hasDefects && !canSignDvirRepair()) {
    // TASK-9691 — a driver-PIN session (or a login without `maintenance`) can
    // no longer certify a repair (firestore.rules dvirSignerOk). Say who signs
    // instead of offering a dialog the rules would refuse.
    sectionsHtml += `
      <div class="dvir-detail-section">
        <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;">draw</span> Mechanic Sign-Off Required</div>
        <div style="font-size:0.82rem;color:var(--text-primary);">The mechanic or shop signs this off from their Maintenance login once the repair is done.</div>
      </div>`;
  } else if (hasDefects) {
    // Any active person can sign off — DOT requires the signature, not mechanic certification
    const eligiblePeople = PretripState.people.filter(p => p.active !== false);

    sectionsHtml += `
      <div class="dvir-detail-signoff">
        <div class="dvir-detail-section-title"><span class="material-icons" style="font-size:16px;">draw</span> Reviewer Sign-Off Required</div>
        <div>
          <label>Reviewed by:</label>
          <select id="dvir-signoff-person">
            <option value="">Select person...</option>
            ${eligiblePeople.map(p => {
              const roleLabel = window.getTypeLabel?.(p.crewType || p.role, 'crewTypes') || p.crewType || p.role || ''; // TASK-9766 — (slug, kind), was reversed
              return `<option value="${escapeHtml(p.name)}">${escapeHtml(p.name)}${roleLabel ? ` (${escapeHtml(roleLabel)})` : ''}</option>`;
            }).join('')}
          </select>
        </div>
        <div>
          <label>Signature:</label>
          <canvas id="dvir-signoff-canvas" width="400" height="120"></canvas>
          <div style="display:flex;justify-content:flex-end;margin-top:4px;">
            <button type="button" class="btn btn-secondary btn-sm" onclick="window.PretripModule.clearSignoffCanvas()" style="font-size:0.75rem;">Clear</button>
          </div>
        </div>
        <!-- TASK-9691 — the open repair work order(s) + the explicit
             "Repair done — also complete the work order" option. Filled by
             hydrateDvirRepair('dvir', …) from a fresh read. -->
        <div id="dvir-repair-panel" style="margin-bottom:8px;"></div>
        <button class="btn btn-primary btn-sm btn-signoff" id="btn-dvir-signoff" onclick="window.PretripModule.submitDvirSignoff('${inspectionId}')">
          <span class="material-icons" style="font-size:16px;">verified</span> Sign Off
        </button>
      </div>
    `;
  }

  const content = `
    <div class="dvir-detail-header ${headerClass}">
      <div class="dvir-detail-header-icon"><span class="material-icons">${statusIcon}</span></div>
      <div class="dvir-detail-header-text">
        <h3>DVIR — ${escapeHtml(truckDisplayName)}</h3>
        <div class="dvir-detail-status">${statusLabel}</div>
        ${hasDefects ? '<div id="dvir-hold-line"></div>' : ''}
      </div>
      <button class="dvir-detail-close" onclick="document.getElementById('modal-dvir-detail').classList.add('hidden')">
        <span class="material-icons" style="font-size:22px;">close</span>
      </button>
    </div>
    <div class="dvir-detail-body">
      ${sectionsHtml}
    </div>
    <div class="dvir-detail-footer">
      ${!(window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess) ? `
      <!-- TASK-4700 — ARCHIVE is the primary disposition; Delete is deliberately
           de-emphasised (link-weight, not a button) because a DVIR is often the
           only evidence the required pre-trip inspection actually happened.
           Archive is reversible and stays findable via the Archived tab — the
           ticket-fiasco shape (archive = invisible + silently un-actionable) is
           what the Restore button and that tab exist to prevent. -->
      <div style="display:flex;gap:12px;align-items:center;">
        ${inspection.archived
          ? `<button class="btn btn-secondary btn-sm" onclick="window.PretripModule.setInspectionArchived('${inspectionId}', false)">
               <span class="material-icons" style="font-size:16px;">unarchive</span> Restore
             </button>`
          : `<button class="btn btn-secondary btn-sm" onclick="window.PretripModule.setInspectionArchived('${inspectionId}', true)">
               <span class="material-icons" style="font-size:16px;">archive</span> Archive
             </button>`}
        <button onclick="window.PretripModule.deleteInspection('${inspectionId}')"
          style="background:none;border:none;padding:6px 2px;font-size:0.8rem;color:#b91c1c;text-decoration:underline;cursor:pointer;">
          Delete permanently
        </button>
      </div>` : ''}
      <div style="display:flex;gap:8px;">
        <button class="btn btn-secondary btn-sm" onclick="window.PretripModule.printInspection('${inspectionId}')">
          <span class="material-icons" style="font-size:16px;">print</span> Print
        </button>
        <button class="btn btn-primary btn-sm" onclick="document.getElementById('modal-dvir-detail').classList.add('hidden')">Close</button>
      </div>
    </div>
  `;

  let modal = document.getElementById('modal-dvir-detail');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'modal-dvir-detail';
    modal.className = 'modal';
    modal.innerHTML = '<div class="modal-content" style="max-width:550px;overflow:hidden;"></div>';
    modal.addEventListener('click', (e) => { if (e.target === modal) modal.classList.add('hidden'); });
    document.body.appendChild(modal);
  }
  modal.querySelector('.modal-content').innerHTML = content;
  modal.classList.remove('hidden');

  // TASK-9691 — header "On hold — work order open" + the sign-off panel's
  // work-order block, from a FRESH read of this DVIR's linked work orders.
  if (hasDefects) hydrateDvirRepair('dvir', inspectionId, `Unit ${detailAsset.unit || '?'}`, true);

  // Init sign-off canvas if present — use rAF + retry for mobile reliability
  requestAnimationFrame(() => {
    setTimeout(() => {
      const ok = initSignoffCanvas();
      if (!ok) setTimeout(() => initSignoffCanvas(), 300);
    }, 100);
  });
}

// ============================================
// DVIR SIGN-OFF (Reviewer/Mechanic)
// ============================================
let signoffCanvasCtx = null;
let signoffDrawing = false;

function initSignoffCanvas() {
  const canvas = document.getElementById('dvir-signoff-canvas');
  if (!canvas) return false;

  const rect = canvas.getBoundingClientRect();
  if (rect.width < 10 || rect.height < 10) return false; // not laid out yet

  // Prevent double-init — remove old listeners by replacing the element
  if (canvas._signoffInit) {
    const fresh = canvas.cloneNode(true);
    canvas.parentNode.replaceChild(fresh, canvas);
    return initSignoffCanvas(); // recurse once with clean element
  }

  const ctx = canvas.getContext('2d');
  signoffCanvasCtx = ctx;

  // Scale for retina
  canvas.width = rect.width * 2;
  canvas.height = rect.height * 2;
  ctx.scale(2, 2);
  ctx.lineWidth = 2;
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#000';

  const getPos = (e) => {
    const r = canvas.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - r.left, y: touch.clientY - r.top };
  };

  const start = (e) => { e.preventDefault(); signoffDrawing = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!signoffDrawing) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { signoffDrawing = false; };

  canvas.addEventListener('mousedown', start);
  canvas.addEventListener('mousemove', move);
  canvas.addEventListener('mouseup', end);
  canvas.addEventListener('mouseleave', end);
  canvas.addEventListener('touchstart', start, { passive: false });
  canvas.addEventListener('touchmove', move, { passive: false });
  canvas.addEventListener('touchend', end);
  canvas._signoffInit = true;
  return true;
}

function clearSignoffCanvas() {
  const canvas = document.getElementById('dvir-signoff-canvas');
  if (!canvas || !signoffCanvasCtx) return;
  signoffCanvasCtx.clearRect(0, 0, canvas.width, canvas.height);
}

function isSignoffCanvasBlank() {
  const canvas = document.getElementById('dvir-signoff-canvas');
  if (!canvas) return true;
  const ctx = canvas.getContext('2d');
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] > 0) return false;
  }
  return true;
}

async function submitDvirSignoff(inspectionId) {
  const person = document.getElementById('dvir-signoff-person')?.value?.trim();
  if (!person) {
    window.showToast?.('Please select who is signing off', 'error');
    return;
  }
  if (isSignoffCanvasBlank()) {
    window.showToast?.('Please provide a signature', 'error');
    return;
  }

  // TASK-9691 — the signer's work-order choice, read BEFORE the button locks so
  // a missing "What was done" keeps the drawn signature and the dialog intact.
  const choice = readRepairChoice('dvir', inspectionId);
  if (choice.error) {
    window.showToast?.(choice.error, 'error');
    return;
  }

  const canvas = document.getElementById('dvir-signoff-canvas');
  const signatureData = canvas.toDataURL('image/png');

  const btn = document.getElementById('btn-dvir-signoff');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  // TASK-9691 — "Repair done — also complete the work order": the DVIR
  // signature, the work order(s) and the audit row commit together on the
  // server (completeDvirRepairSignoff). No client write on this path, so a
  // refusal leaves NOTHING half-signed.
  if (choice.complete) {
    try {
      const res = await completeDvirRepairOnServer({
        dvirId: inspectionId, taskIds: choice.taskIds, notes: choice.notes,
        signerName: person, signature: signatureData,
      });
      const inspection = _findInspectionById(inspectionId);
      if (inspection) {
        inspection.mechanicSignature = signatureData;
        inspection.mechanicSignedBy = person;
      }
      const n = res.completed || choice.taskIds.length;
      window.showToast?.(`Signed off — ${n === 1 ? 'work order' : `${n} work orders`} completed — this DVIR's safety hold is cleared.`, 'success');
      viewInspection(inspectionId);
      _syncLinkedTaskWatchers();
      _refreshInspectionList();
    } catch (error) {
      console.error('[Pretrip] Repair sign-off refused:', error);
      window.showToast?.(error?.message || 'Could not complete the work order. Nothing was saved — try again.', 'error');
      if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">verified</span> Sign Off'; }
    }
    return;
  }

  try {
    const tenant = window.AppState?.tenant || 'demo';
    try {
      window.logAudit?.('pretrip', 'DVIR_DEFECT_SAVE', {
        entityType: 'dvirDefect',
        entityId: inspectionId,
        source: 'pretrip-mechanic-signoff',
        redFlags: { defectCountDrop: false, photoLost: false, statusReverted: false, signedBy: person }
      });
    } catch {}
    await updateDoc(doc(db, 'tenants', tenant, 'pretrips', inspectionId), {
      mechanicSignature: signatureData,
      mechanicSignedBy: person,
      mechanicSignedAt: Timestamp.now()
    });

    // Update local state — the live list OR the by-id cache (TASK-9372: a
    // crewmate's DVIR opened from the job modal lives only in the cache; a
    // stale unsigned copy would offer a second sign-off that the
    // immutable-signature rule then rejects).
    const inspection = _findInspectionById(inspectionId);
    if (inspection) {
      inspection.mechanicSignature = signatureData;
      inspection.mechanicSignedBy = person;
    }

    // TASK-9691 — never a plain green "done" while the unit is still held.
    if (!warnHoldStillOn(choice)) window.showToast?.('DVIR signed off successfully', 'success');

    // Refresh the detail view to show the saved signature
    viewInspection(inspectionId);
    // TASK-9405 — and the list + counts behind it. A record paged in by Load
    // older has no live listener, so without this it would sit on the tab with
    // its old chip until reload. Syncing the watchers starts the repair-state
    // listener for this now-signed DVIR.
    _syncLinkedTaskWatchers();
    _refreshInspectionList();
  } catch (error) {
    console.error('[Pretrip] Error saving sign-off:', error);
    window.showToast?.('Error saving sign-off', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">verified</span> Sign Off'; }
  }
}

// TASK-798 — When a DVIR is deleted, the OPEN CRITICAL maintenance task(s) it
// spawned (pretrip.js created them with source:'pretrip' + sourceId === the DVIR
// id) must be cleared, or the vehicle stays on the Dispatch safety hold forever
// (dispatch.js filters maintenance for priority==='critical' && status!=='completed').
// The DVIR itself is going away, so the whole defect chain is intentionally gone:
// mark the linked task(s) status:'completed' (clears the hold) WITH a note +
// dvirDeleted flag rather than hard-deleting, preserving the audit trail (the
// TASK-773 spirit: no silent loss of a safety record). ONLY tasks whose
// source==='pretrip' AND sourceId matches the deleted DVIR are touched — manual
// critical work orders and other DVIRs' tasks are never affected.
// Returns the count of cleared holds. Exposed on window for maintenance.js to reuse.
async function clearSafetyHoldsForDvir(tenant, dvirId) {
  if (!tenant || !dvirId) return 0;
  try {
    const q = query(
      collection(db, 'tenants', tenant, 'maintenance'),
      where('source', '==', 'pretrip'),
      where('sourceId', '==', dvirId)
    );
    const snap = await getDocs(q);
    const linked = snap.docs.filter(d => {
      const t = d.data();
      return t.priority === 'critical' && t.status !== 'completed';
    });
    for (const d of linked) {
      await updateDoc(doc(db, 'tenants', tenant, 'maintenance', d.id), {
        status: 'completed',
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        dvirDeleted: true,
        resolutionNote: `Source DVIR deleted ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} — safety hold cleared (TASK-798).`
      });
      // TASK-9766 (c) — the cascade closes a safety work order: record it with
      // the same MAINT_TASK_STATUS row maintenance.js writes (window.*, never a
      // cross-import — Pitfall #1).
      const _t = { id: d.id, ...(d.data() || {}) };
      if (typeof window.MaintenanceModule?.auditTaskStatus === 'function') {
        await window.MaintenanceModule.auditTaskStatus(_t, _t.status || null, 'completed', 'dvir_deleted');
      } else {
        console.error(`[DVIR] audit helper missing — no MAINT_TASK_STATUS row for task ${d.id}`);
        window.showToast?.('Saved, but the change was not recorded in the audit log. Tell your admin.', 'warning');
      }
    }
    if (linked.length) console.log(`[DVIR] Cleared ${linked.length} safety hold(s) for deleted DVIR ${dvirId}`);
    return linked.length;
  } catch (e) {
    console.error('[DVIR] Error clearing safety holds for deleted DVIR:', e);
    return 0;
  }
}
window.clearSafetyHoldsForDvir = clearSafetyHoldsForDvir;

// TASK-798 — pre-delete count of OPEN CRITICAL holds this DVIR spawned, so the
// delete confirmation can warn the dispatcher (Domain/COD §396.11 concern: deleting
// a defect-DVIR to un-stick a truck silently clears the hold; the warning makes the
// cascade explicit so a real unrepaired defect isn't waved through unknowingly).
async function getOpenDvirSafetyHoldCount(tenant, dvirId) {
  if (!tenant || !dvirId) return 0;
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'maintenance'),
      where('source', '==', 'pretrip'),
      where('sourceId', '==', dvirId)
    ));
    return snap.docs.filter(d => {
      const t = d.data();
      return t.priority === 'critical' && t.status !== 'completed';
    }).length;
  } catch (e) {
    console.error('[DVIR] Error counting safety holds:', e);
    return 0;
  }
}
window.getOpenDvirSafetyHoldCount = getOpenDvirSafetyHoldCount;

// TASK-4700 — 49 CFR 396.11 keeps a DEFECT report (plus the repair + driver-
// review certifications) for three months. Shared by the delete nudge and the
// refusal message so the UI and firestore.rules quote the SAME window.
const DVIR_RETENTION_DAYS = 90;

/** Mirrors pretripHasDefects() in firestore.rules — keep the two in sync. */
function inspectionHasDefects(i) {
  return i?.status === 'defects' || (Array.isArray(i?.defects) && i.defects.length > 0);
}

/** The date a defect DVIR leaves the federal retention window, or null. */
function inspectionDeletableOn(i) {
  const created = i?.createdAt?.toDate?.() || (i?.createdAt ? new Date(i.createdAt) : null);
  if (!created || isNaN(created.getTime())) return null;
  return new Date(created.getTime() + DVIR_RETENTION_DAYS * 24 * 60 * 60 * 1000);
}

// TASK-4712 — attribute-safe escape with a local fallback. escapeHtml does NOT
// escape quotes, so it is wrong for an attribute; window.escAttr is right but
// lives in sharedFormat.js, and an undefined call inside a template literal
// would throw mid-render and take the ENTIRE list down, not just one attribute.
function _ea(v) {
  if (typeof window.escAttr === 'function') return window.escAttr(v);
  return String(v == null ? '' : v).replace(/[&<>"']/g, c => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

// TASK-4712 — BULK ARCHIVE. Origin: an operator onboarding with 870 DVIRs, 866
// of them clean, who said "I can't find anything".
//
// 🔴 THERE IS DELIBERATELY NO BULK DELETE, and adding one is a decision, not a
// gap to fill. Deleting a DVIR calls clearSafetyHoldsForDvir() — so a mass
// delete silently releases every affected vehicle's safety hold back into
// dispatch at once. COD's Adversary lane rated a bulk-delete design 🔴 for
// exactly that, and the reason it is a 🔴 rather than a fixable UI problem is
// that firestore.rules has NO backstop here: the delete arm gates on WHO plus
// pretripDeleteRetentionOk(), and rules never read the `maintenance` collection,
// so any skip-list we build in this file is bypassable from devtools. Archive
// is reversible, releases no hold, and removes no capability — it solves the
// operator's whole problem with none of that exposure. UX Lead's call, and it
// is the right one: reserve hard-delete for one record at a time, where a human
// reads the warning that names the hold.
function toggleSelectMode() {
  PretripState._selectMode = !PretripState._selectMode;
  PretripState._selected.clear();
  const btn = document.getElementById('pretrip-select-toggle');
  if (btn) btn.classList.toggle('active', PretripState._selectMode);
  document.getElementById('pretrip-bulk-bar')?.classList.toggle('hidden', !PretripState._selectMode);
  renderInspectionsList(_activeFilter());
  _syncBulkBar();
}

function _activeFilter() {
  return document.querySelector('.pretrip-filter.active')?.dataset?.filter || PRETRIP_DEFAULT_FILTER;
}

// TASK-4700 — `archived` is a list-visibility flag ONLY. The working tabs hide
// archived records; the Archived tab is where they stay findable (never a black
// hole). Nothing else in the app filters on it, so an archived DVIR still drives
// safety holds and compliance rollups exactly as before.
//
// TASK-4712 — THE one filter+search predicate. renderInspectionsList() paints
// from it and the bulk bar decides what "selected" means from it, so they cannot
// disagree. If you add a tab, add it HERE and nowhere else.
function visibleInspectionsFor(filter) {
  let list = getAllInspections();
  list = (filter === 'archived')
    ? list.filter(i => i.archived === true)
    : list.filter(i => i.archived !== true).filter(i =>
        filter === 'pass' ? i.status === 'pass' : filter === 'defects' ? isOpenDefectInspection(i) : true);
  return applySearch(list);
}

// ============================================
// TASK-9405 — WHEN IS A DEFECT DVIR CLOSED?
// ============================================
// Chris 2026-09-28: the Defects tab showed every defect ever, including ones
// repaired and signed off, so its badge never meant "something needs doing".
// `status` stays 'defects' forever and that is CORRECT: a 49 CFR 396.11 defect
// report is never rewritten to a pass. Closure is derived, never written:
//
//   closed  ⇔  status === 'defects'
//             AND mechanicSignature          (396.11 repair certification)
//             AND every maintenance task it spawned (source:'pretrip',
//                 sourceId === dvir.id) is status 'completed'
//             AND nextDriverSignature        (396.13 next-driver review)
//
// "driver signature - mechanic signature - then the next driver signature"
// (Chris 9/28). The signature alone is NOT enough: confirmCompletion in
// maintenance.js stamps mechanicSignature when the FIRST critical task
// completes, and the in-list sign-offs sign with no task check, so a
// multi-defect DVIR can be signed while a sibling repair is still open.
//
// `linked` = { state:'ok', open:<n> } when the spawned tasks are known.
// TASK-9691 — <n> counts the CRITICAL safety work orders only (the ones that
// hold the unit on Dispatch). A defect DVIR can also spawn a priority:'normal'
// "Pretrip Note" task; counting it kept the DVIR open — and, once the
// next-driver ack is gated on 'awaiting-next-driver', would have withheld the
// ack forever over a note. summarizeDvirLinkedTasks is the one place that
// counts; maintenance.js renderDVIRList counts the same way.
// Anything else (not loaded yet, listener failed) keeps the DVIR OPEN — this
// file never hides a defect it cannot prove was repaired. A signed DVIR with
// ZERO spawned tasks (observed in prod: older/imported records) is closed once
// both signatures are on it — nothing is left open to wait for.
//
// ONE predicate: the Defects tab, its count badge, the card label and the
// maintenance DVIR history's "Resolved" (maintenance.js renderDVIRList, via
// window.dvirDefectClosure) all read THIS. Add a condition here, nowhere else.
function dvirDefectClosure(i, linked) {
  if (i?.status !== 'defects') return { open: false, stage: 'none' };
  if (!i.mechanicSignature) return { open: true, stage: 'needs-mechanic' };
  if (!linked || linked.state === 'loading') return { open: true, stage: 'checking' };
  if (linked.state !== 'ok' || !(linked.open >= 0)) return { open: true, stage: 'repair-unknown' };
  if (linked.open > 0) return { open: true, stage: 'repair-open' };
  if (!i.nextDriverSignature) return { open: true, stage: 'awaiting-next-driver' };
  return { open: false, stage: 'closed' };
}
window.dvirDefectClosure = dvirDefectClosure;

function isOpenDefectInspection(i) {
  return dvirDefectClosure(i, PretripState._linkedTasks.get(i?.id)).open;
}

// The card's status chip — says WHICH step is missing, and never a plain green
// "Signed off" while a repair is open or the next driver has not reviewed it.
function dvirClosureBadgeHtml(c) {
  const blue = 'background:rgba(59,130,246,0.12);color:var(--brand-blue);';
  const amber = 'background:rgba(245,158,11,0.15);color:#b45309;';
  const grey = 'background:rgba(100,116,139,0.14);color:var(--text-secondary);';
  const green = 'background:rgba(34,197,94,0.12);color:#16a34a;';
  const badge = (style, text) => `<span class="defect-badge" style="${style}">${text}</span>`;
  switch (c?.stage) {
    case 'needs-mechanic': return badge(blue, 'Needs mechanic sign-off');
    case 'checking': return badge(grey, 'Signed off · checking repairs');
    case 'repair-unknown': return badge(amber, 'Signed off · repair status unknown');
    case 'repair-open': return badge(amber, 'Signed off · repair open');
    case 'awaiting-next-driver': return badge(blue, 'Signed off · awaiting next driver');
    case 'closed': return badge(green, 'Signed off');
    default: return '';
  }
}

// ============================================
// TASK-9691 — THE DVIR SIGN-OFF AND ITS REPAIR WORK ORDER
// ============================================
// A defect DVIR spawns a CRITICAL work order; Dispatch holds the unit until it
// is completed. Signing the DVIR used to write only the mechanic signature, so
// the repair was certified while the hold stayed on with nothing saying why
// (TITS trailer 109, 2 weeks). Both sign-off dialogs (this file's
// submitDvirSignoff, maintenance.js submitMaintDvirSignoff) and both
// next-driver ack sites now read the SAME helpers below — maintenance.js via
// window.DvirRepair (Pitfall #1: never a cross-import).
//
// COD folds that shape this: the work order is NEVER completed silently by
// the signature — the signer ticks "Repair done — also complete the work
// order" and says what was done; otherwise the dialog states the unit stays on
// hold and links to the work order. Completion goes through the
// completeDvirRepairSignoff callable (module-gated, driver sessions refused,
// completedBy derived server-side). No third copy of clearSafetyHoldsForDvir.

/** Pure. The linked-task summary every reader uses. `open` = CRITICAL, not completed. */
function summarizeDvirLinkedTasks(dvirId, tasks) {
  const linked = (tasks || []).filter(t => t && t.source === 'pretrip' && t.sourceId === dvirId);
  const critical = linked.filter(t => t.priority === 'critical');
  const openTasks = critical.filter(t => t.status !== 'completed')
    .map(t => ({ id: t.id, description: t.description || '', status: t.status || 'open' }));
  const doneTasks = critical.filter(t => t.status === 'completed')
    .map(t => ({ id: t.id, description: t.description || '', completionNotes: t.completionNotes || '' }));
  return { state: 'ok', total: linked.length, open: openTasks.length, openTasks, doneTasks };
}

function _dvirTenant() {
  const t = window.AppState?.tenant || 'demo';
  return t === 'demo-tenant' ? 'demo' : t;
}

/** A FRESH read of one DVIR's linked work orders. Never throws: {state:'error'} on failure. */
async function fetchDvirLinkedState(tenant, dvirId) {
  if (!tenant || !dvirId) return { state: 'error' };
  try {
    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'maintenance'),
      where('source', '==', 'pretrip'),
      where('sourceId', '==', dvirId)
    ));
    return summarizeDvirLinkedTasks(dvirId, snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) })));
  } catch (e) {
    console.error('[DVIR] Could not read the work orders for DVIR', dvirId, e);
    return { state: 'error' };
  }
}

function _isDriverOrPublicSession() {
  const st = window.AppState || {};
  return !!(st.driverModePin || st.driverMode || st.isPublicAccess);
}

/**
 * Client mirror of the completeDvirRepairSignoff gate (_claimBoundTenantAuthorize
 * with modules:['maintenance'] + view-only/complianceOnly refusals). The UI may
 * offer LESS than the server allows, never more. Demo is refused server-side
 * (no members doc carries modules), so it is refused here too — a demo
 * sign-off falls back to the plain path and the hold message.
 */
function canCompleteDvirRepair() {
  const st = window.AppState || {};
  if (_isDriverOrPublicSession()) return false;
  if (st.isDemo || st.tenant === 'demo' || st.tenant === 'demo-tenant') return false;
  if (typeof window.isComplianceOnlyTenant === 'function' && window.isComplianceOnlyTenant()) return false;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma) return false;
  if (['revoked', 'inactive', 'pending', 'suspended', 'removed'].includes(ma.status)) return false;
  if (ma.readOnly === true || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('maintenance');
}

/**
 * Client mirror of firestore.rules dvirSignerOk(): may this login write the
 * mechanic sign-off on a DVIR? Demo, the owner (tenants.ownerId), or a
 * `maintenance` member who is not view-only. Never a driver/public session.
 */
function canSignDvirRepair() {
  const st = window.AppState || {};
  if (_isDriverOrPublicSession()) return false;
  if (st.isDemo || st.tenant === 'demo' || st.tenant === 'demo-tenant') return true;
  const uid = st.user?.uid || null;
  const ownerId = st.tenantData?.ownerId || null;
  if (uid && ownerId && uid === ownerId) return true;
  const ma = st.memberAccess;
  if (!ma || ma.readOnly === true || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('maintenance');
}

// TASK-10208 — the Export button: owner, owner/admin role, or a member holding
// Maintenance (mirrors requireDvirExportAccess, the real gate). Never a driver.
function _canExportInspections() {
  if (_isDriverOrPublicSession()) return false;
  const st = window.AppState || {};
  if (st.user?.uid && st.user.uid === st.tenantData?.ownerId) return true;
  const ma = st.memberAccess;
  if (!ma || ma.readOnly === true) return false;
  return ma.role === 'owner' || ma.role === 'admin' || (Array.isArray(ma.modules) && ma.modules.includes('maintenance'));
}

async function openInspectionExport() {
  const tenantId = window.AppState?.tenant;
  if (!tenantId) return;
  try {
    (await __ddChunkWrap(import('../utils/dvirExportUi.js?v=2'))).openDvirExport({
      esc: escapeHtml,
      state: PretripState,
      // 300 s = the function's own limit (the per-inspection PDFs outlast the 70 s default).
      call: async (p) => (await httpsCallable(functions, 'exportCarrierDvirs', { timeout: 300000 })({ tenantId, ...p }))?.data,
    });
  } catch (e) {
    console.error('[Pretrip] export UI failed to load', e);
    window.showToast?.('Could not open the export. Reload and try again.', 'error');
  }
}

/** May this session open a work order in Maintenance (the link)? */
function canOpenWorkOrders() {
  if (_isDriverOrPublicSession()) return false;
  try { return typeof window.checkModuleAccess === 'function' ? !!window.checkModuleAccess('maintenance', true) : false; }
  catch (_) { return false; }
}

function _woLinkHtml(taskId, label = 'Open work order', color = 'var(--brand-blue)') {
  if (!canOpenWorkOrders()) return '';
  return `<button type="button" class="dvir-wo-link" onclick="event.stopPropagation();window.DvirRepair.openWorkOrder('${escJsAttr(taskId)}')" style="background:none;border:none;padding:0 0 0 6px;color:${color};text-decoration:underline;cursor:pointer;font-size:inherit;font-weight:700;">${escapeHtml(label)}</button>`;
}

/**
 * The detail-header line (fold 7): "On hold — work order open" + a link, only
 * while a critical work order spawned by this DVIR is open. Empty otherwise.
 * `onDark`: the pretrip detail header is a red gradient with white text; the
 * maintenance source-DVIR modal header is on the light modal background.
 */
function dvirHoldLineHtml(state, onDark = false) {
  const base = onDark
    ? 'display:inline-flex;align-items:center;flex-wrap:wrap;margin-top:6px;padding:3px 8px;border-radius:6px;background:rgba(0,0,0,0.28);color:#fff;'
    : 'display:flex;align-items:center;flex-wrap:wrap;margin-top:4px;color:#b91c1c;';
  if (!state || state.state !== 'ok' || !(state.open > 0)) {
    if (state && state.state === 'error') {
      return `<div class="dvir-hold-line" style="${base}font-size:0.78rem;font-weight:600;">Couldn't check this unit's work orders</div>`;
    }
    return '';
  }
  const first = state.openTasks[0];
  const more = state.open > 1 ? ` (${state.open})` : '';
  return `<div class="dvir-hold-line" style="${base}font-size:0.8rem;font-weight:700;">
    <span class="material-icons" style="font-size:15px;margin-right:4px;">block</span>On hold — work order open${more}${_woLinkHtml(first.id, 'Open work order', onDark ? '#fff' : 'var(--brand-blue)')}
  </div>`;
}

/**
 * The sign-off panel's work-order block. `prefix` keys the DOM ids so the two
 * dialogs (pretrip 'dvir', maintenance 'maint-dvir') never collide.
 */
function dvirRepairPanelHtml(prefix, state, unitLabel) {
  const unit = escapeHtml(unitLabel || 'This unit');
  if (!state || state.state === 'loading') {
    return `<div style="font-size:0.78rem;color:var(--text-secondary);">Checking this unit's work orders…</div>`;
  }
  if (state.state !== 'ok') {
    return `<div style="font-size:0.8rem;color:#b45309;font-weight:600;">Couldn't check this unit's work orders. Signing off does not clear a safety hold — check Maintenance.</div>`;
  }
  if (!(state.open > 0)) return '';
  const list = state.openTasks.map(t => `<li style="margin:2px 0;">${escapeHtml(t.description || 'Safety defect')}${_woLinkHtml(t.id, 'Open')}</li>`).join('');
  const woWord = state.open === 1 ? 'work order' : 'work orders';
  const holdNote = `<div id="${prefix}-repair-hold-note" style="font-size:0.8rem;color:#b91c1c;font-weight:600;margin-top:6px;">Signing off without this keeps ${unit} on a safety hold until its ${woWord} ${state.open === 1 ? 'is' : 'are'} completed.</div>`;
  const option = canCompleteDvirRepair()
    ? `<label style="display:flex;align-items:center;gap:8px;margin-top:8px;min-height:44px;font-size:0.85rem;font-weight:600;color:var(--text-primary);cursor:pointer;">
         <input type="checkbox" id="${prefix}-repair-done" onchange="window.DvirRepair.toggle('${prefix}')" style="margin-top:3px;width:18px;height:18px;flex-shrink:0;">
         <span>Repair done — also complete the ${woWord}</span>
       </label>
       <div id="${prefix}-repair-notes-wrap" class="hidden" style="margin-top:6px;">
         <label for="${prefix}-repair-notes" style="font-size:0.8rem;font-weight:600;color:var(--text-primary);display:block;margin-bottom:4px;">What was done (required)</label>
         <textarea id="${prefix}-repair-notes" rows="2" maxlength="2000" placeholder="e.g. Replaced the spider valve on the first tank" style="width:100%;box-sizing:border-box;padding:8px;border:1px solid var(--border-light);border-radius:var(--radius-sm);font-size:0.85rem;background:var(--bg-elevated);color:var(--text-primary);"></textarea>
       </div>
       ${holdNote}`
    : `<div style="font-size:0.8rem;color:#b91c1c;font-weight:600;margin-top:6px;">Signing off records the review only. ${unit} stays on a safety hold until its ${woWord} ${state.open === 1 ? 'is' : 'are'} completed in Maintenance.</div>`;
  return `<div style="padding:8px 10px;border-radius:var(--radius-sm);background:rgba(239,68,68,0.06);border:1px solid rgba(239,68,68,0.3);">
    <div style="font-size:0.82rem;font-weight:700;color:#b91c1c;display:flex;align-items:center;gap:4px;"><span class="material-icons" style="font-size:16px;">block</span>${unit} is on a safety hold — open ${woWord}:</div>
    <ul style="margin:4px 0 0 18px;padding:0;font-size:0.82rem;color:var(--text-primary);">${list}</ul>
    ${option}
  </div>`;
}

// prefix -> { dvirId, state, unitLabel } for the panel currently on screen.
const _repairCtx = new Map();

/** Fetch fresh, paint the sign-off panel + the header hold line. */
async function hydrateDvirRepair(prefix, dvirId, unitLabel, holdOnDark = false) {
  _repairCtx.set(prefix, { dvirId, state: { state: 'loading' }, unitLabel });
  const state = await fetchDvirLinkedState(_dvirTenant(), dvirId);
  const ctx = _repairCtx.get(prefix);
  if (!ctx || ctx.dvirId !== dvirId) return state; // a different DVIR was opened meanwhile
  ctx.state = state;
  const panel = document.getElementById(`${prefix}-repair-panel`);
  if (panel) panel.innerHTML = dvirRepairPanelHtml(prefix, state, unitLabel);
  const hold = document.getElementById(`${prefix}-hold-line`);
  if (hold) hold.innerHTML = dvirHoldLineHtml(state, holdOnDark);
  return state;
}

function toggleRepairOption(prefix) {
  const on = !!document.getElementById(`${prefix}-repair-done`)?.checked;
  document.getElementById(`${prefix}-repair-notes-wrap`)?.classList.toggle('hidden', !on);
  const note = document.getElementById(`${prefix}-repair-hold-note`);
  if (note) note.style.display = on ? 'none' : '';
  if (on) document.getElementById(`${prefix}-repair-notes`)?.focus();
}

/**
 * Read the signer's choice BEFORE the button is disabled. Pure DOM read.
 * @returns {{complete:boolean, notes?:string, taskIds?:string[], error?:string, openCount:number, unitLabel?:string}}
 */
function readRepairChoice(prefix, dvirId) {
  const ctx = _repairCtx.get(prefix);
  const state = ctx && ctx.dvirId === dvirId ? ctx.state : null;
  const known = !!(state && state.state === 'ok');
  const openCount = known ? state.open : 0;
  const box = document.getElementById(`${prefix}-repair-done`);
  if (!box || !box.checked) return { complete: false, openCount, known, unitLabel: ctx?.unitLabel };
  const notes = (document.getElementById(`${prefix}-repair-notes`)?.value || '').trim();
  if (notes.length < 3) return { complete: true, error: 'Say what was done before completing the work order.', openCount };
  if (!state || state.state !== 'ok' || !state.openTasks.length) {
    return { complete: true, error: "Couldn't confirm the open work order. Close the DVIR and open it again.", openCount };
  }
  return { complete: true, notes, taskIds: state.openTasks.map(t => t.id), openCount, unitLabel: ctx?.unitLabel };
}

/** The callable. Throws with the server's plain-English message on refusal. */
async function completeDvirRepairOnServer({ dvirId, taskIds, notes, signerName, signature }) {
  const fn = httpsCallable(functions, 'completeDvirRepairSignoff');
  const res = await fn({ dvirId, taskIds, notes, signerName, signature });
  return res?.data || {};
}

/**
 * After the plain (signature-only) path. Returns true when it showed the
 * toast — the caller shows its own green "signed off" only when this is false.
 * Never a silent green while the unit is held, or while we could not tell.
 */
function warnHoldStillOn(choice) {
  const unit = choice?.unitLabel || 'This unit';
  if (choice && choice.openCount > 0) {
    window.showToast?.(`Signed off. ${unit} stays on a safety hold until its work order is completed.`, 'warning');
    return true;
  }
  if (choice && choice.known === false) {
    window.showToast?.(`Signed off. Couldn't check ${unit}'s work orders — check Maintenance for an open safety hold.`, 'warning');
    return true;
  }
  return false;
}

/** The link target: close the DVIR modals and open the work order in Maintenance. */
function openWorkOrder(taskId) {
  if (!taskId) return;
  document.getElementById('modal-dvir-detail')?.classList.add('hidden');
  document.getElementById('modal-source-dvir')?.classList.add('hidden');
  if (typeof window.MaintenanceModule?.openTaskById === 'function') {
    window.MaintenanceModule.openTaskById(taskId);
  } else {
    window.showToast?.('Open Maintenance to complete this work order.', 'info');
  }
}

window.DvirRepair = {
  summarize: summarizeDvirLinkedTasks,
  fetch: fetchDvirLinkedState,
  canComplete: canCompleteDvirRepair,
  canSign: canSignDvirRepair,
  hydrate: hydrateDvirRepair,
  toggle: toggleRepairOption,
  readChoice: readRepairChoice,
  complete: completeDvirRepairOnServer,
  warnHoldStillOn,
  openWorkOrder,
};

// TASK-9691 (fold 6) — which prior DVIR on this unit may the next driver
// acknowledge? ONLY one at stage 'awaiting-next-driver': mechanic signed AND
// every critical work order it spawned completed (fresh read, never the
// list's cache — the ack sites run on the inspection FORM, where no linked-task
// listener exists and the cached stage would read 'checking'). A DVIR whose
// repair is still open is skipped: "has been repaired" must be true when shown.
// Returns { doc, data, linked } | null, or { error:true } when a candidate's
// repair state could not be read.
async function findAckableDvir(tenant, docs) {
  for (const d of docs || []) {
    const data = d.data() || {};
    if (!((data.status === 'defects' || data.hasSafetyDefect === true) && data.mechanicSignature && !data.nextDriverSignature)) continue;
    const linked = await fetchDvirLinkedState(tenant, d.id);
    if (linked.state !== 'ok') return { error: true };
    // hasSafetyDefect-only records are defect DVIRs for this purpose.
    const stage = dvirDefectClosure({ ...data, status: 'defects' }, linked).stage;
    if (stage === 'awaiting-next-driver') return { doc: d, data, linked };
  }
  return null;
}

// One live listener per loaded defect DVIR that has a mechanic signature (the
// only ones whose closure depends on task state). Equality-only query — the
// same shape clearSafetyHoldsForDvir() already runs, so no composite index —
// and scoped to that DVIR's own tasks, so a PIN driver never receives other
// trucks' work orders. Re-synced whenever the loaded set changes.
function _syncLinkedTaskWatchers() {
  const tenant = (window.AppState?.tenant === 'demo-tenant') ? 'demo' : window.AppState?.tenant;
  const want = new Set();
  if (tenant) {
    getAllInspections().forEach(i => {
      if (i?.id && i.status === 'defects' && i.mechanicSignature) want.add(i.id);
    });
  }
  PretripState._linkedTaskUnsubs.forEach((unsub, id) => {
    if (!want.has(id)) {
      try { unsub(); } catch (_) {}
      PretripState._linkedTaskUnsubs.delete(id);
      PretripState._linkedTasks.delete(id);
    }
  });
  want.forEach(id => {
    if (PretripState._linkedTaskUnsubs.has(id)) return;
    PretripState._linkedTasks.set(id, { state: 'loading' });
    let unsub = null;
    try {
      unsub = onSnapshot(query(
        collection(db, 'tenants', tenant, 'maintenance'),
        where('source', '==', 'pretrip'),
        where('sourceId', '==', id)
      ), (snap) => {
        // TASK-9691 — `open` counts the CRITICAL safety work orders only (see
        // summarizeDvirLinkedTasks); a normal "Pretrip Note" task no longer
        // holds a defect DVIR open forever.
        PretripState._linkedTasks.set(id, summarizeDvirLinkedTasks(id, snap.docs.map(d => ({ id: d.id, ...(d.data() || {}) }))));
        _scheduleListRefresh();
      }, (err) => {
        console.error('[Pretrip] linked-task listener failed for DVIR', id, err);
        PretripState._linkedTasks.set(id, { state: 'error' });
        _scheduleListRefresh();
      });
    } catch (err) {
      console.error('[Pretrip] could not watch linked tasks for DVIR', id, err);
      PretripState._linkedTasks.set(id, { state: 'error' });
    }
    PretripState._linkedTaskUnsubs.set(id, unsub || (() => {}));
  });
}

// COD — N listeners each deliver a first snapshot within the same few ms on
// view open; coalesce them into ONE repaint instead of N full-list renders.
function _scheduleListRefresh() {
  if (PretripState._refreshTimer) return;
  PretripState._refreshTimer = setTimeout(() => {
    PretripState._refreshTimer = null;
    _refreshInspectionList();
  }, 30);
}

function _stopLinkedTaskWatchers() {
  if (PretripState._refreshTimer) { clearTimeout(PretripState._refreshTimer); PretripState._refreshTimer = null; }
  PretripState._linkedTaskUnsubs.forEach(unsub => { try { unsub(); } catch (_) {} });
  PretripState._linkedTaskUnsubs.clear();
  PretripState._linkedTasks.clear();
}

// Re-render the list on the chip the user has open, and the counts with it.
// Safe when the list is not on screen (renderInspectionsList returns early).
function _refreshInspectionList() {
  renderInspectionsList(_activeFilter());
  updateCounts();
}

// The set the user can actually SEE — never the unloaded pagination tail.
function _visibleInspections() {
  return visibleInspectionsFor(_activeFilter());
}

function toggleSelected(id) {
  if (PretripState._bulkBusy) return;
  const sel = PretripState._selected;
  if (sel.has(id)) sel.delete(id); else sel.add(id);
  const box = document.querySelector(`.pretrip-card[data-id="${CSS.escape(id)}"] .pretrip-select-box`);
  if (box) box.checked = sel.has(id);
  _syncBulkBar();
}

function toggleSelectAll(checked) {
  if (PretripState._bulkBusy) return;
  PretripState._selected.clear();
  if (checked) _visibleInspections().forEach(i => PretripState._selected.add(i.id));
  renderInspectionsList(_activeFilter());
  _syncBulkBar();
}

// 🔴 SAY "LOADED", NEVER "ALL". The live subscription is a 30-DAY window and
// paging is 50 at a time, so on an 870-record tenant "Select all" reaches a
// fraction of the fleet's history. Both COD's UX and Domain lanes flagged that
// the honest failure here is not compliance, it is an operator who believes he
// cleaned up and did not.
function _syncBulkBar(filter) {
  const f = filter || _activeFilter();
  const n = PretripState._selected.size;
  const visible = visibleInspectionsFor(f).length;
  const archivedTab = f === 'archived';
  const btn = document.getElementById('pretrip-bulk-action');
  if (btn) {
    btn.disabled = n === 0 || PretripState._bulkBusy;
    btn.textContent = PretripState._bulkBusy
      ? 'Working…'
      : `${archivedTab ? 'Restore' : 'Archive'}${n ? ` ${n}` : ''}`;
    btn.onclick = () => archivedTab ? bulkSetArchivedSelected(false) : bulkSetArchivedSelected(true);
  }
  const lbl = document.getElementById('pretrip-select-all-label');
  if (lbl) lbl.textContent = `Select all ${visible} loaded`;
  const all = document.getElementById('pretrip-select-all');
  if (all) all.checked = visible > 0 && n === visible;
  const cnt = document.getElementById('pretrip-bulk-count');
  if (cnt) cnt.textContent = n ? `${n} selected` : '';
  const scope = document.getElementById('pretrip-bulk-scope');
  if (scope) {
    scope.textContent = PretripState._olderExhausted
      ? `This is every inspection on file (${visible} shown).`
      : `Only the ${visible} inspection${visible === 1 ? '' : 's'} loaded here can be selected — use "Load older inspections" at the bottom to reach the rest.`;
  }
}

// Chunked, and Promise.allSettled — NEVER Promise.all. A single refused write
// inside Promise.all rejects the batch and abandons every record behind it,
// which is exactly how resetDemoData silently stranded 14 of 18 collections
// (TASK-4700). One refusal must cost one record and be NAMED, not swallowed.
async function bulkSetArchivedSelected(archived) {
  if (PretripState._bulkBusy) return;
  if (window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    window.showToast?.('Only dispatchers can archive inspections', 'error');
    return;
  }
  const ids = Array.from(PretripState._selected);
  if (!ids.length) return;

  const verb = archived ? 'Archive' : 'Restore';
  const msg = archived
    ? `Archive ${ids.length} inspection${ids.length === 1 ? '' : 's'}? They move to the Archived tab and stay findable. ` +
      `Nothing is deleted, safety holds and compliance rollups are unaffected, and you can restore them at any time.`
    : `Restore ${ids.length} inspection${ids.length === 1 ? '' : 's'} back into the working list?`;
  const ok = typeof window.showConfirm === 'function'
    ? await window.showConfirm(`${verb} ${ids.length}?`, msg, { confirmText: verb, cancelText: 'Cancel' })
    : window.confirm(msg);
  if (!ok) return;

  const tenant = window.AppState?.tenant || 'demo';
  PretripState._bulkBusy = true;
  _syncBulkBar();

  let done = 0; const failed = [];
  const CHUNK = 25;
  try {
    for (let i = 0; i < ids.length; i += CHUNK) {
      const slice = ids.slice(i, i + CHUNK);
      const results = await Promise.allSettled(slice.map(id =>
        updateDoc(doc(db, 'tenants', tenant, 'pretrips', id), {
          archived: !!archived,
          archivedAt: archived ? Timestamp.now() : null,
          archivedBy: archived ? (window.AppState?.user?.uid || null) : null,
        })
      ));
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          done++;
          [PretripState.inspections, PretripState.olderInspections].forEach(list => {
            const hit = (list || []).find(x => x.id === slice[idx]);
            if (hit) hit.archived = !!archived;
          });
        } else {
          failed.push(slice[idx]);
          console.error('[DVIR] Bulk archive refused for', slice[idx], r.reason);
        }
      });
    }
  } finally {
    PretripState._bulkBusy = false;
  }

  PretripState._selected.clear();
  PretripState._selectMode = false;
  document.getElementById('pretrip-bulk-bar')?.classList.add('hidden');
  document.getElementById('pretrip-select-toggle')?.classList.remove('active');
  renderInspectionsList(_activeFilter());
  updateCounts();

  // Name the failures. A partial run reported as success is the silent-failure
  // shape this module has been bitten by before.
  if (failed.length) {
    window.showToast?.(
      `${done} ${archived ? 'archived' : 'restored'}, ${failed.length} could not be — they are still in the list. Check the console for which.`,
      'error');
  } else {
    window.showToast?.(`${done} inspection${done === 1 ? '' : 's'} ${archived ? 'archived' : 'restored'}`, 'success');
  }
}

// TASK-4700 — soft, REVERSIBLE archive. Deliberately a list-visibility flag and
// nothing more: archived DVIRs still feed safety holds, compliance status and
// every defect rollup, so archiving can never silently remove a capability the
// way archiving a ticket once removed its Invoice button.
async function setInspectionArchived(inspectionId, archived) {
  if (window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    window.showToast?.('Only dispatchers can archive inspections', 'error');
    return;
  }
  const tenant = window.AppState?.tenant || 'demo';
  try {
    await updateDoc(doc(db, 'tenants', tenant, 'pretrips', inspectionId), {
      archived: !!archived,
      archivedAt: archived ? Timestamp.now() : null,
      archivedBy: archived ? (window.AppState?.user?.uid || null) : null,
    });
    // Patch local state — `olderInspections` is a manual array the live
    // subscription never refreshes.
    [PretripState.inspections, PretripState.olderInspections].forEach(list => {
      const hit = (list || []).find(i => i.id === inspectionId);
      if (hit) { hit.archived = !!archived; }
    });
    window.showToast?.(archived ? 'Archived — find it under the Archived tab' : 'Restored', 'success');
    document.getElementById('modal-dvir-detail')?.classList.add('hidden');
    renderInspectionsList(
      document.querySelector('.pretrip-filter.active')?.dataset?.filter || PRETRIP_DEFAULT_FILTER
    );
    updateCounts();
  } catch (e) {
    console.error('[DVIR] Archive error:', e);
    window.showToast?.('Could not update this inspection — ' + (e?.message || 'try again'), 'error');
  }
}

async function deleteInspection(inspectionId) {
  if (window.AppState?.driverModePin || window.AppState?.driverMode || window.AppState?.isPublicAccess) {
    window.showToast?.('Only dispatchers can delete inspections', 'error');
    return;
  }
  const inspection = getAllInspections().find(i => i.id === inspectionId);
  if (!inspection) return;
  const label = inspection.driverName ? `${inspection.driverName}'s DVIR` : 'this DVIR';
  const tenant = window.AppState?.tenant || 'demo';

  // TASK-4700 — REFUSE BEFORE THE ROUND TRIP when the record is a defect report
  // still inside the federal retention window. firestore.rules is the actual
  // boundary; this exists so an admin is told WHY and WHEN instead of being
  // handed "Missing or insufficient permissions" and filing a bug.
  if (inspectionHasDefects(inspection)) {
    const deletableOn = inspectionDeletableOn(inspection);
    if (!deletableOn || deletableOn > new Date()) {
      const when = deletableOn
        ? deletableOn.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
        : null;
      // NOTE: showConfirm sets message via textContent and the stylesheet has no
      // white-space rule, so newlines would collapse — keep this flowing prose.
      const msg = 'This DVIR reports a defect, so 49 CFR 396.11 requires keeping it for three months, ' +
        'along with the repair and driver-review certifications. ' +
        (when ? `It can be deleted on ${when}. ` : 'It has no recorded date, so it stays held until one is confirmed. ') +
        'Archive it instead — that clears it from the list, keeps the record, and can be undone.';
      const ok = typeof window.showConfirm === 'function'
        ? await window.showConfirm('Kept by federal rule', msg, { confirmText: 'Archive instead', cancelText: 'Cancel' })
        : window.confirm(msg);
      if (ok) await setInspectionArchived(inspectionId, true);
      return;
    }
  }
  // Warn if deleting this DVIR will also drop a live safety hold (unrepaired defect).
  const holdCount = await getOpenDvirSafetyHoldCount(tenant, inspectionId);
  const unit = inspection.truckUnit || inspection.trailerUnit || '';
  // TASK-4700 — a clean DVIR is an OPTIONAL record for a freight carrier (FMCSA
  // rescinded the no-defect DVIR requirement in 2014), so deleting one is the
  // carrier's call and is NOT blocked. But the pre-trip INSPECTION is still
  // required, and this report is often the only evidence it happened — so say
  // that plainly and offer archive first. Inform, don't block.
  let msg = `Delete ${label}? This cannot be undone. Archiving keeps the record ` +
    `(often your only proof the pre-trip inspection was done) and can be undone — ` +
    `deleting removes it for good.`;
  if (holdCount > 0) {
    msg = `${label}${unit ? ` (Unit ${unit})` : ''} has ${holdCount} open safety defect${holdCount > 1 ? 's' : ''}. ` +
      `Deleting it will ALSO clear the safety hold on this vehicle${unit ? ` (Unit ${unit})` : ''}, allowing it back into dispatch. ` +
      `Only do this if the DVIR was logged in error (wrong vehicle/duplicate) — not to skip a repair. Continue?`;
  }
  const proceed = typeof window.showConfirm === 'function'
    ? await window.showConfirm('Delete this DVIR?', msg, { confirmText: 'Delete permanently', cancelText: 'Keep it' })
    : confirm(msg);
  if (!proceed) return;
  try {
    await deleteDoc(doc(db, 'tenants', tenant, 'pretrips', inspectionId));
    // Clear any safety hold this DVIR's defect(s) created, or the vehicle stays held forever.
    const cleared = await clearSafetyHoldsForDvir(tenant, inspectionId);
    window.showToast?.(cleared > 0 ? 'DVIR deleted — safety hold cleared' : 'DVIR deleted', 'success');
    document.getElementById('modal-dvir-detail')?.classList.add('hidden');
  } catch (e) {
    console.error('[DVIR] Delete error:', e);
    // TASK-4700 — firestore.rules is the real boundary, so a refusal can still
    // land here (stale local doc, a defect added after this list loaded).
    // Translate it instead of surfacing "Missing or insufficient permissions".
    if (e?.code === 'permission-denied') {
      window.showToast?.('This DVIR reports a defect and is kept for three months under 49 CFR 396.11. Archive it instead — the record stays and it can be undone.', 'error');
    } else {
      window.showToast?.('Error deleting: ' + e.message, 'error');
    }
  }
}

function printInspection(inspectionId) {
  const inspection = _findInspectionById(inspectionId);
  if (!inspection) return;
  const date = inspectionDisplayDate(inspection); // TASK-1289 — shared resolver
  const w = window.open('', '_blank');
  if (!w) { window.showToast?.('Popup blocked. Allow popups for this site to print the DVIR.', 'error'); return; }
  const _pl = _inspectionLocCache.get(inspection.id) || null; const s = (_isDriverSessionView() || (inspection.locationStatus === 'captured' && !_pl)) ? null : inspectionLocationSummary(inspection, _pl);
  // TASK-10208 — the print body is the lazy utils/dvirPrint.js (cold-load budget).
  __ddChunkWrap(import('../utils/dvirPrint.js?v=1')).then((m) => m.writeDvirPrint(w, inspection, { date, loc: s })).catch((e) => {
    console.error('[Pretrip] print view failed to load', e);
    try { w.close(); } catch (_) {}
    window.showToast?.('Could not open the print view. Reload and try again.', 'error');
  });
}

function updateCounts() {
  const base = applySearch(getAllInspections());
  // TASK-4700 — the working tabs count LIVE records only, so the Defects badge
  // matches what that tab actually renders. Archived gets its own count.
  const live = base.filter(i => i.archived !== true);
  const all = live.length;
  const pass = live.filter(i => i.status === 'pass').length;
  // TASK-9405 — OPEN defects only, via the same predicate the tab renders.
  const defects = live.filter(isOpenDefectInspection).length;
  const archived = base.filter(i => i.archived === true).length;

  const countAll = document.getElementById('count-all');
  const countPass = document.getElementById('count-pass');
  const countDefects = document.getElementById('count-defects');
  const countArchived = document.getElementById('count-archived');

  if (window.setCountBadge) {
    window.setCountBadge(countAll, all);
    window.setCountBadge(countPass, pass);
    window.setCountBadge(countDefects, defects);
    window.setCountBadge(countArchived, archived);
  } else {
    if (countAll) countAll.textContent = all;
    if (countPass) countPass.textContent = pass;
    if (countDefects) countDefects.textContent = defects;
    if (countArchived) countArchived.textContent = archived;
  }
}

// TASK-9507 — in a driver PIN session the DVIR names the session driver, and
// only them (49 CFR 396.11: the report is the inspecting driver's). Office /
// member sessions return null and keep the picker. Gated on driverModePin, NOT
// on driverPersonId alone: clock.js sets driverPersonId on the office kiosk's
// expense path, and that session must still be able to pick any driver.
function _sessionDriverPersonId() {
  const a = window.AppState || {};
  if (a.driverModePin !== true) return null;
  return a.driverPersonId ? String(a.driverPersonId) : null;
}

// Select + lock the session driver on #inspection-driver. Returns true when a
// driver session is locked. The person may be missing from the active list
// (inactive, or people not loaded yet) — add their option rather than leave
// the field blank, because a blank required field is the "Driver is required"
// dead end this exists to remove.
function _applySessionDriverLock(select) {
  if (!select) return false;
  const pid = _sessionDriverPersonId();
  if (!pid) {
    if (select.dataset && select.dataset.sessionLocked === '1') {
      select.disabled = false;
      delete select.dataset.sessionLocked;
    }
    return false;
  }
  const has = Array.from(select.options || []).some(o => o.value === pid);
  if (!has) {
    const person = (PretripState.people || []).find(p => String(p.id) === pid);
    const name = person?.name || window.AppState?.driverPersonName || 'You';
    select.insertAdjacentHTML('beforeend',
      `<option value="${escapeHtml(pid)}" data-name="${escapeHtml(name)}" data-signature="${person?.signature ? '1' : ''}">${escapeHtml(name)}</option>`);
  }
  select.value = pid;
  select.disabled = true;
  if (select.dataset) select.dataset.sessionLocked = '1';
  return select.value === pid;
}

function updateDriverDropdown() {
  const select = document.getElementById('inspection-driver');
  if (!select) return;
  // TASK-9507 (Edge) — a people snapshot re-fires this and rebuilds the
  // options. Keep the pick already made instead of resetting it to blank.
  const _keep = select.value;
  // COD (Code Reviewer) — also notice when the kept driver's signature-on-file
  // changed in this snapshot; otherwise the pad / on-file image goes stale and
  // a locked driver cannot re-pick to recover.
  const _sigOf = () => select.options?.[select.selectedIndex]?.dataset?.signature || '';
  const _keepSig = _sigOf();

  // Any active person can fill out vehicle inspections
  const drivers = PretripState.people.filter(p => p.active !== false);

  select.innerHTML = '<option value="">Select driver</option>' +
    drivers.map(d => `<option value="${escapeHtml(d.id)}" data-name="${escapeHtml(d.name)}" data-signature="${d.signature ? '1' : ''}">${escapeHtml(d.name)}</option>`).join('') +
    '<option value="unknown" data-name="">-- Other (Not in roster) --</option>';

  // Add change listener to check for signature on file
  select.onchange = onDriverChange;

  if (_applySessionDriverLock(select)) {
    // The session driver is the only possible value; refresh the signature
    // section only when this rebuild changed the selection or its signature.
    if (_keep !== select.value || _keepSig !== _sigOf()) onDriverChange();
    return;
  }
  if (_keep && Array.from(select.options || []).some(o => o.value === _keep)) {
    select.value = _keep;
    if (_keep !== 'unknown' && _keepSig !== _sigOf()) onDriverChange();
  }
}

function onDriverChange() {
  const select = document.getElementById('inspection-driver');
  const selectedOption = select.options[select.selectedIndex];
  const driverId = select.value;
  const unknownNameGroup = document.getElementById('unknown-driver-group');
  const signatureSection = document.querySelector('.dvir-signature-section');
  const signatureOnFileSection = document.getElementById('signature-on-file-section');

  // TASK-9559 — the driver's yard leads the equipment list; re-sort on every
  // change (before the 'unknown' early return, so "Other" drops the old yard).
  if (document.getElementById('inspection-asset-type')?.value === 'equipment' && typeof updateAssetDropdown === 'function') updateAssetDropdown('equipment');

  // Show/hide unknown driver name input
  if (driverId === 'unknown') {
    if (unknownNameGroup) unknownNameGroup.classList.remove('hidden');
    // Always show signature capture for unknown drivers
    if (signatureSection) signatureSection.classList.remove('hidden');
    if (signatureOnFileSection) signatureOnFileSection.classList.add('hidden');
    return;
  } else {
    if (unknownNameGroup) unknownNameGroup.classList.add('hidden');
  }

  // Check if driver has signature on file
  const hasSignature = selectedOption?.dataset?.signature === '1';
  const driver = PretripState.people.find(p => p.id === driverId);

  if (hasSignature && driver?.signature) {
    // Show signature on file, hide capture pad
    if (signatureSection) signatureSection.classList.add('hidden');
    if (signatureOnFileSection) {
      signatureOnFileSection.classList.remove('hidden');
      const sigImg = document.getElementById('signature-on-file-img');
      if (sigImg) sigImg.src = driver.signature;
    }
  } else {
    // No signature on file, show capture pad
    if (signatureSection) signatureSection.classList.remove('hidden');
    if (signatureOnFileSection) signatureOnFileSection.classList.add('hidden');
  }
}

function updateTruckDropdown() {
  // Use asset type from dropdown, default to truck
  const assetTypeSelect = document.getElementById('inspection-asset-type');
  const assetType = assetTypeSelect?.value || 'truck';
  updateAssetDropdown(assetType);
}

function renderChecklist(assetType = 'truck') {
  const container = document.getElementById('checklist-items');
  if (!container) return;

  const items = getInspectionItems(assetType);

  // Group items by category
  const groups = {};
  items.forEach(item => {
    const g = item.group || 'Other';
    if (!groups[g]) groups[g] = [];
    groups[g].push(item);
  });

  let html = `
    <div style="display:flex;justify-content:flex-end;margin-bottom:8px;">
      <button type="button" class="btn btn-sm btn-secondary" onclick="window.PretripModule.markAllChecklistOk()" style="font-size:0.75rem;padding:4px 10px;">
        <span class="material-icons" style="font-size:14px;">done_all</span> All OK
      </button>
    </div>
  `;

  for (const [groupName, groupItems] of Object.entries(groups)) {
    html += `
      <div class="checklist-group" style="margin-bottom:8px;">
        <div style="font-size:0.75rem;font-weight:700;color:var(--text-secondary);text-transform:uppercase;letter-spacing:0.5px;padding:4px 0;border-bottom:1px solid var(--border-light);margin-bottom:4px;">
          ${groupName}
        </div>
        <div class="checklist-grid">
          ${groupItems.map(item => `
            <label class="checklist-item" data-item="${item.id}">
              <input type="checkbox" checked data-item="${item.id}" onchange="window.PretripModule.toggleItem('${item.id}')">
              <span class="checklist-icon"><span class="material-icons">${item.icon}</span></span>
              <span class="checklist-label">${item.label}</span>
            </label>
          `).join('')}
        </div>
      </div>
    `;
  }

  container.innerHTML = html;
}

function markAllChecklistOk() {
  document.querySelectorAll('#checklist-items input[type="checkbox"]').forEach(cb => {
    cb.checked = true;
    const item = cb.closest('.checklist-item');
    if (item) item.classList.remove('failed');
  });
  window.showToast?.('All items marked OK', 'success');
}

function onAssetTypeChange() {
  const assetType = document.getElementById('inspection-asset-type').value;
  const label = document.getElementById('label-inspection-asset');

  // Update label
  if (label) {
    if (assetType === 'trailer') label.textContent = 'Trailer *';
    else if (assetType === 'equipment') label.textContent = 'Equipment *';
    else label.textContent = 'Vehicle *';
  }

  // Toggle mileage visibility (hide for equipment — they use hours)
  const mileageGroup = document.getElementById('inspection-mileage-group') || document.getElementById('inspection-mileage')?.closest('.form-group');
  if (mileageGroup) mileageGroup.style.display = assetType === 'equipment' ? 'none' : '';

  // TASK-9559 — a search typed for one asset type never filters another.
  const filterEl = document.getElementById('inspection-asset-filter');
  if (filterEl) filterEl.value = '';

  // Update dropdown options
  updateAssetDropdown(assetType);

  // Re-render checklist with correct items
  _resetUnitCheckAnswers(); // TASK-10135 — new asset type = fresh answers (base list redrawn too)
  renderChecklist(assetType);
  renderUnitChecks(assetType, ''); // TASK-9741 — no unit picked yet
}

// TASK-9507 — an asset's yard/branch for display. Live label from the yards
// list first (a renamed yard reads current), then the name stamped on the
// asset (a PIN session may not have settings.yards), then the raw slug.
function _assetYardLabel(a) {
  if (!a || (!a.yardId && !a.yardName)) return '';
  const yards = window.RosterModule?.getYardsList?.() || window.AppState?.settings?.yards || [];
  const live = a.yardId && Array.isArray(yards) ? yards.find(y => y && y.slug === a.yardId) : null;
  return String((live && (live.label || live.slug)) || a.yardName || a.yardId || '');
}

// TASK-9559 — Equipment picker: searchable + grouped by branch.
// Chris (Guy demo, PIN driver): one long A–Z dropdown; items with no yard had
// no label, the same SKU in three branches was scattered, no search.
// Groups: the home branch first (a CONVENIENCE default only — members.yardId
// is never a boundary, TASK-9369/9375; every branch is still listed), then the
// other branches A–Z, then "No branch". Native <select> + <optgroup>: branch
// names are yard names, not item names, so a header can't be mistaken for a
// pickable row (the TASK-9609 job-picker P0 was project headers named like jobs).
// Constants stay inline: the unit tests run these functions as vm slices.

// The branch to list first: the signed-in member's home branch, else the
// inspecting driver's assigned yard (a PIN session has no member doc).
function _equipmentHomeYardId() {
  const ma = window.AppState?.memberAccess;
  if (ma && typeof ma.yardId === 'string' && ma.yardId && ma.yardId !== 'all') return ma.yardId;
  const pid = document.getElementById('inspection-driver')?.value;
  if (!pid) return '';
  const p = (PretripState.people || []).find(x => x && String(x.id) === String(pid));
  return (p && typeof p.yardId === 'string') ? p.yardId : '';
}

// PURE apart from _assetYardLabel. Every whitespace-separated word must appear
// in the name, serial, part number or branch. keepId (the current pick) is
// always kept so a search never drops — and never silently changes — it.
function _equipmentPickerGroups(assets, homeYardId, query, keepId) {
  const words = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  const cmp = (x, y) => String(x || '').localeCompare(String(y || ''), undefined, { numeric: true, sensitivity: 'base' });
  const byKey = new Map();
  for (const a of (Array.isArray(assets) ? assets : [])) {
    if (!a) continue;
    const label = _assetYardLabel(a);
    if (words.length && !(keepId && String(a.id) === String(keepId))) {
      const hay = [a.name, a.serialNumber, a.partNumber, label].filter(v => v != null && v !== '').join('\n').toLowerCase();
      if (!words.every(w => hay.includes(w))) continue;
    }
    // Group by the label shown, so a yardId-only and a yardName-only asset in
    // the same branch land together. Home is matched on yardId (a slug).
    const key = label ? label.toLowerCase() : '';
    if (!byKey.has(key)) byKey.set(key, { label: label || 'No branch', home: false, none: !label, items: [] });
    const g = byKey.get(key);
    if (homeYardId && a.yardId === homeYardId) g.home = true;
    g.items.push(a);
  }
  const groups = [...byKey.values()];
  groups.forEach(g => g.items.sort((x, y) => cmp(x.name, y.name)));
  groups.sort((x, y) => (x.none - y.none) || (y.home - x.home) || cmp(x.label, y.label));
  return groups;
}

function onAssetFilterInput() {
  const type = document.getElementById('inspection-asset-type')?.value || 'truck';
  if (type === 'equipment') updateAssetDropdown('equipment');
}

function updateAssetDropdown(assetType = 'truck') {
  const select = document.getElementById('inspection-truck');
  if (!select) return;
  // TASK-9372 — a trucks/trailers/assets snapshot re-fires this and rebuilds
  // the options; keep the pick the driver (or the job-modal prefill) already
  // made instead of silently resetting it to the placeholder.
  const _keep = select.value;
  const _restore = () => {
    if (_keep && Array.from(select.options || []).some(o => o.value === _keep)) select.value = _keep;
  };

  // TASK-9559 — the search box only exists for a long equipment list.
  const filterEl = document.getElementById('inspection-asset-filter');
  const showFilter = assetType === 'equipment' && (PretripState.assets || []).length > 8; // TASK-9559 — short lists need no search
  if (filterEl) {
    if (!showFilter) filterEl.value = '';
    filterEl.classList?.toggle?.('hidden', !showFilter);
  }

  let items;
  if (assetType === 'equipment') {
    // TASK-9559 — grouped by branch (home branch first, "No branch" last) and
    // filtered by the search box. VALUES NEVER CHANGE (asset id) and the
    // current pick is always rendered, so filtering never re-points it.
    const query = showFilter && filterEl ? String(filterEl.value || '') : '';
    const groups = _equipmentPickerGroups(PretripState.assets, _equipmentHomeYardId(), query, _keep);
    const opt = (a) => {
      // TASK-9507 — the yard/branch tells identical rows apart ("Composite
      // Mat 8x14" ×4); without it a defect lands on the wrong yard's mat.
      // Display only: data-unit (what the record stores) stays the name.
      // The closed <select> shows only this text, never the group header, so
      // the branch stays on every row.
      const yard = _assetYardLabel(a);
      return `<option value="${escapeHtml(a.id)}" data-unit="${escapeHtml(a.name)}">${escapeHtml(a.name)}${yard ? ' — ' + escapeHtml(yard) : ''}${a.serialNumber ? ' (SN: ' + escapeHtml(a.serialNumber) + ')' : ''}</option>`;
    };
    // One group only (a tenant with no branches) → flat, no lone header.
    const body = groups.length > 1
      ? groups.map(g => `<optgroup label="${escapeHtml(g.label)}">${g.items.map(opt).join('')}</optgroup>`).join('')
      : groups.map(g => g.items.map(opt).join('')).join('');
    const none = (!body && query.trim()) ? '<option value="" disabled>No equipment matches your search</option>' : '';
    select.innerHTML = `<option value="">Select equipment</option>${none}${body}`;
    _restore();
    return;
  } else if (assetType === 'trailer') {
    items = PretripState.trailers;
  } else {
    items = PretripState.trucks.filter(t => {
      const typeStr = (t.type || '').toLowerCase();
      return !typeStr.includes('trailer');
    });
  }

  const placeholder = assetType === 'trailer' ? 'Select trailer' : 'Select vehicle';
  select.innerHTML = `<option value="">${placeholder}</option>` +
    items.map(t => `<option value="${t.id}" data-unit="${escapeHtml(t.unit)}">${escapeHtml(window.getUnitDisplay ? window.getUnitDisplay(t) : t.unit)}</option>`).join('');
  _restore();
}

// ============================================
// RETURN INSPECTION DETECTION & HANDLING
// ============================================
function onAssetSelected() {
  const assetType = document.getElementById('inspection-asset-type')?.value;
  const assetId = document.getElementById('inspection-truck')?.value;
  renderUnitChecks(assetType || 'truck', assetId); // TASK-9741
  const section = document.getElementById('return-inspection-section');
  if (!section) return;

  // Reset
  PretripState.activeReturnInspection = null;
  section.classList.add('hidden');

  // Show recent truck inspections in driver mode (all asset types)
  _loadTruckRecentInspections(assetId);

  if (assetType !== 'equipment' || !assetId) return;

  // Check if this asset has a pending return inspection
  const ri = PretripState.returnInspections.find(r => r.assetId === assetId);
  if (!ri) return;

  const asset = PretripState.assets.find(a => a.id === assetId);
  if (!asset) return;

  PretripState.activeReturnInspection = ri;
  section.classList.remove('hidden');

  // Show/hide qty field based on tracking mode
  const qtyGroup = document.getElementById('ri-qty-group');
  const isQty = asset.trackingMode === 'quantity';
  if (qtyGroup) qtyGroup.style.display = isQty ? '' : 'none';

  // Pre-fill expected qty
  const expectedQty = ri.expectedQty || (isQty ? (asset.deployedQty || 0) : 1);
  const badge = document.getElementById('ri-expected-badge');
  if (badge) badge.textContent = isQty ? `Expected: ${expectedQty}` : 'Individual asset';
  const qtyInput = document.getElementById('ri-qty-returned');
  if (qtyInput && isQty) qtyInput.value = expectedQty;

  // Show hours field if hours tracking enabled
  const hoursGroup = document.getElementById('ri-hours-group');
  if (hoursGroup) {
    hoursGroup.classList.toggle('hidden', !asset.hoursTracking?.enabled);
    if (asset.hoursTracking?.enabled) {
      document.getElementById('ri-hours-reading').placeholder = `Last: ${asset.hoursTracking.currentHours || 0} hrs`;
    }
  }

  // Populate yard dropdown
  const yardSelect = document.getElementById('ri-yard');
  if (yardSelect) {
    // TASK-1142 drift fix — canonical yard shape is {slug, label, address?,
    // type?}; the old y.id/y.name reads never matched anything (and
    // getYardsList wasn't exported on RosterModule until TASK-1142, so this
    // list was always empty). Falls back to AppState settings just in case.
    const yards = window.RosterModule?.getYardsList?.() || window.AppState?.settings?.yards || [];
    yardSelect.innerHTML = '<option value="">Select yard</option>' +
      yards.map(y => `<option value="${escapeHtml(y.slug)}" ${y.slug === asset.yardId ? 'selected' : ''}>${escapeHtml(y.label || y.slug)}</option>`).join('');
  }

  // Clear condition notes
  const conditionEl = document.getElementById('ri-condition');
  if (conditionEl) conditionEl.value = '';
  const warningEl = document.getElementById('ri-qty-warning');
  if (warningEl) warningEl.classList.add('hidden');
}

async function _loadTruckRecentInspections(assetId) {
  const container = document.getElementById('truck-recent-inspections');
  if (!container) return;

  const isDriverMode = window.AppState?.driverMode || window.AppState?.isPublicAccess || (window.AppState?.driverModePin === true);
  if (!isDriverMode || !assetId) {
    container.classList.add('hidden');
    container.innerHTML = '';
    return;
  }

  container.classList.remove('hidden');
  container.innerHTML = `<div style="padding:10px 0 4px;color:var(--text-secondary);font-size:0.78rem;display:flex;align-items:center;gap:6px;"><span class="btn-spinner" style="width:12px;height:12px;"></span> Loading previous inspections…</div>`;

  try {
    const tenant = window.AppState?.tenant;
    if (!db || !tenant) { container.classList.add('hidden'); return; }

    const snap = await getDocs(query(
      collection(db, 'tenants', tenant, 'pretrips'),
      where('truckId', '==', assetId),
      orderBy('createdAt', 'desc'),
      limit(3)
    ));

    if (snap.empty) { container.classList.add('hidden'); return; }

    // TASK-1289 — same sort-vs-display mismatch as the main list (COD Blast
    // caught this fourth site): the query orders by createdAt but each row
    // renders submittedAt, so a late-synced pre-trip shows an older time above
    // newer ones. Re-sort the fetched window on the SAME key it displays.
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      .sort((a, b) => inspectionDisplayDate(b) - inspectionDisplayDate(a));
    const hasDefects = rows.some(r => r.status === 'defects');

    let html = `
      <div style="margin-bottom:var(--spacing-md);border:1px solid ${hasDefects ? 'rgba(245,158,11,0.35)' : 'var(--border-light)'};border-radius:var(--radius-md);overflow:hidden;">
        <button type="button"
          onclick="this.nextElementSibling.classList.toggle('hidden');this.querySelector('.tri').textContent=this.nextElementSibling.classList.contains('hidden')?'▶':'▼';"
          style="width:100%;display:flex;align-items:center;gap:8px;padding:10px 12px;background:${hasDefects ? 'rgba(245,158,11,0.06)' : 'var(--bg-secondary)'};border:none;cursor:pointer;text-align:left;">
          <span class="material-icons" style="font-size:16px;color:${hasDefects ? '#d97706' : 'var(--text-secondary)'};">${hasDefects ? 'warning' : 'history'}</span>
          <span style="font-size:0.8rem;font-weight:600;color:${hasDefects ? '#d97706' : 'var(--text-secondary)'};">Previous inspections on this vehicle</span>
          ${hasDefects ? `<span style="font-size:0.72rem;background:rgba(245,158,11,0.15);color:#d97706;padding:2px 7px;border-radius:10px;font-weight:700;">Defects noted</span>` : ''}
          <span class="tri" style="margin-left:auto;font-size:0.7rem;color:var(--text-muted);">▶</span>
        </button>
        <div class="hidden" style="border-top:1px solid var(--border-light);">
    `;

    rows.forEach(r => {
      const dt = inspectionDisplayDate(r); // TASK-1289 — shared resolver
      const dateStr = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      const timeStr = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const bad = r.status === 'defects';
      const defectList = (r.defects || []).slice(0, 3).map(d => `<span style="display:inline-block;font-size:0.7rem;background:rgba(239,68,68,0.1);color:#dc2626;padding:1px 6px;border-radius:8px;margin:2px 2px 0 0;">${escapeHtml(d)}</span>`).join('');
      html += `
        <div onclick="window.PretripModule.viewInspection('${r.id}')" style="display:flex;align-items:flex-start;gap:10px;padding:9px 12px;border-bottom:1px solid var(--border-light);cursor:pointer;background:transparent;"
          onmouseenter="this.style.background='var(--bg-hover)'" onmouseleave="this.style.background='transparent'">
          <span class="material-icons" style="font-size:18px;margin-top:2px;color:${bad ? '#f59e0b' : '#16a34a'};">${bad ? 'warning' : 'check_circle'}</span>
          <div style="flex:1;min-width:0;">
            <div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);">${escapeHtml(r.driverName || 'Unknown')} · ${r.type === 'pre' ? 'Pre-Trip' : 'Post-Trip'}</div>
            <div style="font-size:0.75rem;color:var(--text-secondary);">${dateStr} at ${timeStr}</div>
            ${bad && defectList ? `<div style="margin-top:3px;">${defectList}</div>` : ''}
          </div>
          <span class="material-icons" style="font-size:16px;color:var(--text-muted);margin-top:3px;">chevron_right</span>
        </div>
      `;
    });

    html += `</div></div>`;
    container.innerHTML = html;
  } catch (e) {
    console.warn('[Pretrip] Could not load truck history:', e);
    container.classList.add('hidden');
  }
}

function checkReturnQtyWarning() {
  const ri = PretripState.activeReturnInspection;
  if (!ri) return;

  const asset = PretripState.assets.find(a => a.id === ri.assetId);
  if (!asset || asset.trackingMode !== 'quantity') return;

  const expectedQty = ri.expectedQty || asset.deployedQty || 0;
  const returnedQty = parseInt(document.getElementById('ri-qty-returned')?.value) || 0;
  const warningEl = document.getElementById('ri-qty-warning');
  if (!warningEl) return;

  if (returnedQty === expectedQty) {
    warningEl.classList.add('hidden');
  } else if (returnedQty < expectedQty) {
    const remaining = expectedQty - returnedQty;
    warningEl.classList.remove('hidden');
    warningEl.style.background = '#FFFBEB';
    warningEl.style.color = '#92400E';
    warningEl.style.border = '1px solid #FDE68A';
    warningEl.innerHTML = `<span class="material-icons" style="font-size:14px;vertical-align:middle;">warning</span> ${returnedQty} of ${expectedQty} returned — ${remaining} still deployed`;
  } else {
    warningEl.classList.remove('hidden');
    warningEl.style.background = '#FEF2F2';
    warningEl.style.color = '#DC2626';
    warningEl.style.border = '1px solid #FECACA';
    warningEl.innerHTML = `<span class="material-icons" style="font-size:14px;vertical-align:middle;">error</span> Returning ${returnedQty} but only ${expectedQty} expected`;
  }
}

// TASK-1265 — the standalone decrementDeployedQtyFloored helper was removed: the
// floored decrement now lives INSIDE processReturnInspection's single idempotent
// transaction (which also flips the return-inspection status), so a double-submit
// can't re-run it. See the transaction in processReturnInspection below.

async function processReturnInspection(tenant, inspectionId, inspectionData) {
  const ri = PretripState.activeReturnInspection;
  if (!ri) return;

  const asset = PretripState.assets.find(a => a.id === ri.assetId);
  if (!asset) return;

  const isQty = asset.trackingMode === 'quantity';
  const returnedQty = isQty ? (parseInt(document.getElementById('ri-qty-returned')?.value) || 0) : 1;
  const receiver = document.getElementById('ri-receiver')?.value?.trim() || inspectionData.driverName || 'Unknown';
  const yardSelect = document.getElementById('ri-yard');
  const toYardId = yardSelect?.value || '';
  const toYardName = yardSelect?.selectedOptions?.[0]?.textContent || '';
  const condition = document.getElementById('ri-condition')?.value?.trim() || '';
  const hoursReading = parseFloat(document.getElementById('ri-hours-reading')?.value) || null;

  try {
    // Update asset inventory. Non-deployedQty fields (yard/hours/status) go in
    // assetUpdate; for a quantity asset they ride the SAME atomic write as the
    // floored deployedQty decrement (TASK-1255) so nothing is lost on retry.
    const assetUpdate = {};
    if (!isQty) {
      assetUpdate.deploymentStatus = 'available';
      assetUpdate.deployedToJobId = null;
    }
    // Update yard if selected
    if (toYardId) {
      assetUpdate.yardId = toYardId;
      assetUpdate.yardName = toYardName;
    }
    // Update hours if provided
    if (hoursReading && asset.hoursTracking?.enabled) {
      assetUpdate['hoursTracking.currentHours'] = hoursReading;
    }
    // TASK-1265 — ATOMIC + IDEMPOTENT. Fold the asset update AND the return-
    // inspection status→'completed' flip into ONE transaction, gated on the RI
    // not already being completed. The old code did the decrement and the status
    // flip as two separate writes with no RI-status check, so a double-tap /
    // reopened-return-modal / offline-retry re-fired processReturnInspection and
    // decremented deployedQty TWICE — cumulative double-decrements drove Split
    // Rock's Megadeck HD to -444 (TASK-1255 floored a single hit but couldn't stop
    // the repeat). Now a re-fire sees status==='completed' and no-ops: no second
    // decrement, no duplicate assetTransaction/maintenance rows.
    const riRef = doc(db, 'tenants', tenant, 'returnInspections', ri.id);
    const assetRef = doc(db, 'tenants', tenant, 'assets', ri.assetId);
    const riFields = {
      status: 'completed', inspectionId, returnedQty, receiver,
      toYardId, toYardName, condition, hoursReading, completedAt: Timestamp.now(),
    };
    const txResult = await runTransaction(db, async (tx) => {
      const riSnap = await tx.get(riRef);
      // Terminal in EITHER direction — a return completed here OR dismissed via
      // Maintenance is already applied; re-submitting must not decrement again
      // (COD Code-Reviewer: matching Maintenance's completed||dismissed guard).
      const _riStatus = riSnap.exists() ? riSnap.data().status : null;
      if (_riStatus === 'completed' || _riStatus === 'dismissed') return { alreadyDone: true };
      let committed = null;
      if (isQty) {
        const aSnap = await tx.get(assetRef);
        const current = (aSnap.exists() ? aSnap.data().deployedQty : 0) || 0;
        committed = Math.max(0, current - returnedQty);
        if (current - returnedQty < 0) console.warn(`[Rental] deployedQty floor hit on asset ${ri.assetId}: ${current} - ${returnedQty} < 0 -> clamped to 0.`);
        tx.update(assetRef, { deployedQty: committed, updatedAt: Timestamp.now(), ...assetUpdate });
      } else {
        tx.update(assetRef, { ...assetUpdate, updatedAt: Timestamp.now() });
      }
      tx.update(riRef, riFields);
      return { alreadyDone: false, committedDeployedQty: committed };
    });

    if (txResult.alreadyDone) {
      // A concurrent/retry re-fire — inventory + status already applied. Do NOT
      // log a second assetTransaction or open a duplicate maintenance task.
      PretripState.activeReturnInspection = null;
      window.showToast?.('This return was already recorded.', 'info');
      console.log(`[Pretrip] Return inspection already completed (idempotent no-op): ${asset.name}`);
      return;
    }
    const committedDeployedQty = txResult.committedDeployedQty;

    // Log transaction
    await addDoc(collection(db, 'tenants', tenant, 'assetTransactions'), {
      assetId: ri.assetId, assetName: asset.name, partNumber: asset.partNumber || '',
      type: 'return', direction: 'return', quantity: returnedQty,
      jobId: ri.jobId || null,
      toYardId, toYardName,
      fromYardId: asset.yardId || '', fromYardName: asset.yardName || '',
      receiver,
      note: `Returned via inspection${condition ? ': ' + condition.substring(0, 100) : ''}`,
      issuedBy: window.AppState?.user?.email || 'unknown',
      createdAt: Timestamp.now()
    });

    // Create maintenance task if damage noted in condition
    if (condition) {
      await addDoc(collection(db, 'tenants', tenant, 'maintenance'), {
        // TASK-9372 — was truckId/truckUnit with assetType 'asset': invisible
        // under the asset's own filter (matches assetId).
        ..._dvirWorkOrderAssetFields('equipment', ri.assetId, asset.name),
        category: 'Return Condition',
        description: `Return inspection: ${condition}`,
        status: 'open',
        priority: 'normal',
        source: 'return-inspection',
        sourceId: inspectionId,
        createdAt: Timestamp.now(),
        createdBy: receiver
      });
      console.log('[Pretrip] Created maintenance task from return condition');
    }

    // Update local state — mirror from the COMMITTED floored value, never a
    // stale local recompute (TASK-1255).
    if (isQty) {
      asset.deployedQty = committedDeployedQty;
    } else {
      asset.deploymentStatus = 'available';
    }

    PretripState.activeReturnInspection = null;
    console.log(`[Pretrip] Return inspection completed: ${asset.name}, qty: ${returnedQty}, receiver: ${receiver}`);
  } catch (err) {
    console.error('[Pretrip] Return inspection processing error:', err);
    window.showToast?.('Return inspection saved but inventory update failed — check Maintenance', 'warning');
  }
}

// ============================================
// DVIR FLUIDS HELPERS
// ============================================
function toggleDvirOilFields() {
  const type = document.getElementById('dvir-fluid-type')?.value;
  const oilFields = document.getElementById('dvir-oil-fields');
  if (oilFields) oilFields.classList.toggle('hidden', type !== 'Oil');
}

function addDvirFluid() {
  const type = document.getElementById('dvir-fluid-type').value;
  const amount = parseFloat(document.getElementById('dvir-fluid-amount').value);
  const unit = document.getElementById('dvir-fluid-unit').value;

  if (!amount || amount <= 0) {
    window.showToast?.('Enter a valid amount', 'error');
    return;
  }

  const entry = { fluidType: type, amount, unit };

  if (type === 'Oil') {
    entry.oilBrand = document.getElementById('dvir-oil-brand').value.trim();
    entry.oilWeight = document.getElementById('dvir-oil-weight').value;
  }

  PretripState.pendingFluids.push(entry);
  renderDvirFluidsList();

  // Reset form fields
  document.getElementById('dvir-fluid-amount').value = '';
  const oilBrand = document.getElementById('dvir-oil-brand');
  if (oilBrand) oilBrand.value = '';
}

function removeDvirFluid(index) {
  PretripState.pendingFluids.splice(index, 1);
  renderDvirFluidsList();
}

function renderDvirFluidsList() {
  const list = document.getElementById('dvir-fluids-list');
  const badge = document.getElementById('dvir-fluids-badge');
  const count = PretripState.pendingFluids.length;

  if (badge) {
    badge.textContent = count;
    badge.classList.toggle('hidden', count === 0);
  }

  if (!list) return;

  if (count === 0) {
    list.innerHTML = '';
    return;
  }

  list.innerHTML = PretripState.pendingFluids.map((f, i) => `
    <div class="dvir-fluid-entry">
      <span>${f.fluidType} — ${f.amount} ${f.unit}${f.oilBrand ? ` (${f.oilBrand} ${f.oilWeight || ''})` : ''}</span>
      <button type="button" class="btn-icon" onclick="window.PretripModule.removeDvirFluid(${i})">
        <span class="material-icons" style="color:var(--status-error);font-size:18px;">close</span>
      </button>
    </div>
  `).join('');
}

// ============================================
// INSPECTION OPERATIONS
// ============================================
function showNewInspection() {
  // TASK-9372 — a standalone New never inherits a job stamp from an earlier
  // job-modal deep link. showNewInspectionFor re-arms it AFTER this call.
  PretripState.prefillJobId = null;
  PretripState.prefillJobDayId = null;
  PretripState._prefillSeq++;
  PretripState.inspectionFormGen++;
  document.getElementById('modal-inspection-title').textContent = 'New Pre-Trip Inspection';
  document.getElementById('form-inspection').reset();
  document.getElementById('inspection-id').value = '';

  // Set date picker to today, allow backdating up to 7 days
  const dateInput = document.getElementById('inspection-date');
  if (dateInput) {
    // TASK-9923 — the company's calendar day. Was toISOString() (UTC), which
    // after ~7 PM Central stamped the DVIR TOMORROW.
    const today = window.RecordDate.today();
    dateInput.value = today;
    dateInput.max = today;
    dateInput.min = window.RecordDate.addDays(today, -7);
  }

  // Reset asset type to truck
  const assetTypeSelect = document.getElementById('inspection-asset-type');
  if (assetTypeSelect) assetTypeSelect.value = 'truck';

  // Reset label
  const label = document.getElementById('label-inspection-asset');
  if (label) label.textContent = 'Vehicle *';

  // Reset checkbox system toggle
  const checkboxToggle = document.getElementById('use-checkbox-system');
  if (checkboxToggle) checkboxToggle.checked = false;

  // Collapse checklist section
  const checklistSection = document.getElementById('checklist-section');
  if (checklistSection) checklistSection.classList.add('collapsed');

  // Clear pending photos
  PretripState.pendingPhotos.forEach(p => { if (p.thumbUrl) URL.revokeObjectURL(p.thumbUrl); });
  PretripState.pendingPhotos = [];

  // Clear pending fluids
  PretripState.pendingFluids = [];
  renderDvirFluidsList();
  const fluidsSection = document.getElementById('dvir-fluids-section');
  if (fluidsSection) fluidsSection.removeAttribute('open');
  toggleDvirOilFields();

  // Clear signature
  clearSignature();

  // Reset unknown driver field
  const unknownDriverGroup = document.getElementById('unknown-driver-group');
  if (unknownDriverGroup) unknownDriverGroup.classList.add('hidden');
  const unknownDriverInput = document.getElementById('unknown-driver-name');
  if (unknownDriverInput) unknownDriverInput.value = '';

  // Reset printed name toggle
  const printedNameCb = document.getElementById('dvir-use-printed-name');
  if (printedNameCb) printedNameCb.checked = false;
  const printedNameGroup = document.getElementById('dvir-printed-name-group');
  if (printedNameGroup) printedNameGroup.classList.add('hidden');
  const typedNameInput = document.getElementById('dvir-typed-name');
  if (typedNameInput) typedNameInput.value = '';

  // Reset signature sections (show capture, hide on-file)
  const signatureSection = document.querySelector('.dvir-signature-section');
  const signatureOnFileSection = document.getElementById('signature-on-file-section');
  if (signatureSection) signatureSection.classList.remove('hidden');
  if (signatureOnFileSection) signatureOnFileSection.classList.add('hidden');

  // TASK-9507 — a PIN driver files as themselves: preselect + lock the
  // session driver. AFTER form.reset() (which blanks the select) and after the
  // signature-section reset above, so onDriverChange() can show their
  // signature on file. Office sessions keep the picker (no-op there).
  if (_applySessionDriverLock(document.getElementById('inspection-driver'))) onDriverChange();

  // Render checklist for truck (default)
  _resetUnitCheckAnswers(); // TASK-10135 — a new form starts with fresh unit-check answers
  renderChecklist('truck');
  renderUnitChecks('truck', ''); // TASK-9741

  // Update asset dropdown
  updateAssetDropdown('truck');

  // Show modal
  document.getElementById('modal-inspection').classList.remove('hidden');

  // Initialize signature pad after modal is visible
  setTimeout(() => initSignaturePad(), 100);

  // Reset photo grid
  renderPhotoGrid();

  // Dispatch event for tutorial system
  window.dispatchEvent(new CustomEvent('pretrip:modal-opened'));
}

// TASK-9372 — deep link from the Job Board job modal: a NEW inspection with
// the asset type + asset preselected and the job stamped on the record.
// Equipment opens with the checklist ON (the standalone New keeps today's
// collapsed default). The asset list may still be loading — poll it (20×150ms)
// and, if the asset never shows, leave the form open with nothing selected
// rather than hang. A close / standalone New in the meantime cancels the pick.
async function showNewInspectionFor(opts = {}) {
  const assetType = ['truck', 'trailer', 'equipment'].includes(opts.assetType) ? opts.assetType : 'equipment';
  showNewInspection();
  PretripState.prefillJobId = opts.jobId ? String(opts.jobId) : null;
  PretripState.prefillJobDayId = opts.jobDayId ? String(opts.jobDayId) : null;
  const seq = ++PretripState._prefillSeq;

  const typeSel = document.getElementById('inspection-asset-type');
  if (typeSel) {
    typeSel.value = assetType;
    onAssetTypeChange();
  }
  if (assetType === 'equipment') {
    const toggle = document.getElementById('use-checkbox-system');
    if (toggle) {
      toggle.checked = true;
      toggleChecklistSystem();
    }
  }

  const want = opts.assetId ? String(opts.assetId) : '';
  if (!want) return;
  const list = () => (assetType === 'equipment' ? PretripState.assets
    : assetType === 'trailer' ? PretripState.trailers : PretripState.trucks) || [];
  // TASK-9507 — match the job's asset by id. A legacy name ref is only used
  // when exactly ONE asset carries that name: a branch can hold several
  // identical "Composite Mat 8x14" rows, and taking the first one files the
  // defect against the wrong yard's mat. Ambiguous → open unselected and let
  // the driver pick from the yard-labelled list.
  const resolve = () => {
    const l = list();
    const byId = l.find(a => a && String(a.id) === want);
    if (byId) return byId;
    const byName = l.filter(a => a && (a.name === want || a.unit === want || a.nickname === want));
    return byName.length === 1 ? byName[0] : null;
  };
  const ambiguous = () => list().filter(a => a && (a.name === want || a.unit === want || a.nickname === want)).length > 1;
  for (let i = 0; i < 20 && !resolve() && !ambiguous(); i++) {
    await new Promise(r => setTimeout(r, 150));
    if (seq !== PretripState._prefillSeq) return;
  }
  if (seq !== PretripState._prefillSeq) return;
  const modal = document.getElementById('modal-inspection');
  if (!modal || modal.classList.contains('hidden')) return;
  if (typeSel && typeSel.value !== assetType) return; // the driver changed type meanwhile
  const hit = resolve();
  if (!hit) return; // asset not on file / inactive — open unselected
  const sel = document.getElementById('inspection-truck');
  if (!sel || sel.value) return; // never overwrite a pick the driver made
  updateAssetDropdown(assetType);
  sel.value = String(hit.id);
  // TASK-9507 — run the select's handlers directly instead of dispatching a
  // synthetic 'change': app.js's unsaved-work tracker counts every change
  // event, so the prefill made an untouched form ask "Discard your changes?"
  // on Cancel. Same effects as the listeners: return-inspection check
  // (inline onchange) + 3-signature check (setupTutorialEvents).
  if (sel.value === String(hit.id)) {
    onAssetSelected();
    checkVehicle3SigOnSelect(sel.value);
  }
}

// TASK-9658 S1 — record deep link `#/pretrip/dvir/{id}`. app.js parks
// AppState.pendingRecordFocus and mounts this view; we drain it ONCE when the
// inspections list first lands (or after an 8s fallback) and open the normal
// detail modal through openInspectionById (live list first, then a by-id read
// for anything older than the 30-day window; its own "could not be found"
// toast covers a missing id). A DRIVER-scoped session only ever opens its OWN
// inspection — same boundary as the TASK-544 query scope. A crewmate's DVIR
// reads as not found (the by-id path exists for the job modal, not for links).
let _dvirFocusFallbackTimer = null;

function drainPretripRecordFocus() {
  const RL = window.RecordLinks;
  if (!RL || !RL.hasPendingFocus('pretrip')) return;
  const focus = RL.takePendingFocus('pretrip');
  if (_dvirFocusFallbackTimer) { clearTimeout(_dvirFocusFallbackTimer); _dvirFocusFallbackTimer = null; }
  if (!focus || focus.type !== 'dvir') return;
  const gen = PretripState._viewGen;
  _openDvirFromLink(focus.id, gen).catch((e) => {
    console.warn('[Pretrip] DVIR deep link failed', e);
    if (gen === PretripState._viewGen) window.showToast?.('That inspection could not be found.', 'info');
  });
}

async function _openDvirFromLink(id, gen) {
  const driverScoped = window.AppState?.driverMode || window.AppState?.isPublicAccess || (window.AppState?.driverModePin === true);
  if (driverScoped) {
    const pid = window.AppState?.driverPersonId || null;
    let insp = pid ? getAllInspections().find(i => i.id === id) : null;
    if (!insp && pid) {
      try {
        const tenant = window.AppState?.tenant || 'demo';
        const snap = await getDoc(doc(db, 'tenants', tenant, 'pretrips', String(id)));
        insp = snap.exists() ? { id: snap.id, ...snap.data() } : null;
      } catch (e) {
        console.warn('[Pretrip] DVIR link lookup failed:', e?.code || e?.message);
        insp = null;
      }
    }
    if (gen !== PretripState._viewGen) return;
    if (!insp || !pid || insp.driverId !== pid) {
      window.showToast?.('That inspection could not be found.', 'info');
      return;
    }
    // COD (Code Reviewer): open the document we just VALIDATED — never hand off
    // to openInspectionById, which re-reads and would show whatever the second
    // read returns without re-checking driverId.
    if (!getAllInspections().some(i => i.id === insp.id)) {
      _byIdCacheDocs()[insp.id] = { ...insp, _tenant: window.AppState?.tenant || 'demo' };
    }
    viewInspection(insp.id);
    return;
  }
  if (gen !== PretripState._viewGen) return;
  await openInspectionById(id);
}

// TASK-9372 — open one inspection's detail by id. The loaded list is
// driverId-scoped + 30 days for a PIN session, so a crewmate's inspection
// listed on the job modal is not in it: load that doc by id (pretrips read =
// canReadTenant) into a read-only cache, then open the normal detail view.
async function openInspectionById(inspectionId) {
  if (!inspectionId) return;
  // The live list is kept current by its snapshot — use it when it has the doc.
  if (getAllInspections().some(i => i.id === inspectionId)) { viewInspection(inspectionId); return; }
  // Otherwise ALWAYS re-read (a cached copy may predate a sign-off).
  const tenant = window.AppState?.tenant || 'demo';
  _byIdCacheDocs(); // settle the session key before capturing it
  const req = ++PretripState._byIdReq;
  const gen = PretripState._byIdCache.gen;
  const key = PretripState._byIdCache.key;
  const stale = () => req !== PretripState._byIdReq || gen !== PretripState._byIdCache.gen
    || key !== _byIdCacheKey() || tenant !== (window.AppState?.tenant || 'demo');
  try {
    const snap = await getDoc(doc(db, 'tenants', tenant, 'pretrips', String(inspectionId)));
    if (stale()) return; // a newer open, a reset, or a session change won
    if (!snap.exists()) {
      window.showToast?.('That inspection could not be found.', 'error');
      return;
    }
    _byIdCacheDocs()[inspectionId] = { id: snap.id, ...snap.data(), _tenant: tenant };
    viewInspection(inspectionId);
  } catch (e) {
    if (stale()) return;
    console.error('[Pretrip] Error loading inspection by id:', e);
    window.showToast?.('Could not open that inspection. Check your connection and try again.', 'error');
  }
}

function toggleItem(itemId) {
  const checkbox = document.querySelector(`input[data-item="${itemId}"]`);
  const label = document.querySelector(`.checklist-item[data-item="${itemId}"]`);

  if (checkbox && label) {
    // Toggle failed class based on checkbox state (unchecked = defect)
    const isFailed = !checkbox.checked;
    label.classList.toggle('failed', isFailed);
  }
}

function toggleChecklistSystem() {
  const toggle = document.getElementById('use-checkbox-system');
  const checklistSection = document.getElementById('checklist-section');

  if (toggle && checklistSection) {
    checklistSection.classList.toggle('collapsed', !toggle.checked);
  }
}

function showBlankDVIR() {
  document.getElementById('modal-blank-dvir').classList.remove('hidden');
}

function showSafetyHoldModal(truckUnit, openCriticalTasks) {
  const modal = document.getElementById('modal-safety-hold');
  const unitEl = document.getElementById('safety-hold-unit');
  const defectsEl = document.getElementById('safety-hold-defects');

  unitEl.textContent = `Unit ${truckUnit}`;

  // Build defects list from open critical tasks
  const defectsList = openCriticalTasks.map(doc => {
    const task = doc.data();
    const reportedDate = task.createdAt?.toDate?.() || new Date();
    const dateStr = reportedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `
      <div class="safety-hold-defect-item">
        <span class="material-icons">error</span>
        <div>
          <div class="defect-description">${(task.description || 'Safety defect').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</div>
          <div class="defect-date">Reported: ${dateStr}</div>
        </div>
      </div>
    `;
  }).join('');

  defectsEl.innerHTML = defectsList;
  modal.classList.remove('hidden');
}

// --- 3-Signature DVIR (DOT 49 CFR 396.13) ---
// Shows overlay requiring next driver to acknowledge prior defect repairs before operating vehicle
// Module-level state for 3rd signature (avoids onclick string escaping issues on mobile)
let _pending3Sig = null;

// Check for pending 3-sig DVIR when user selects a vehicle — block before they fill out the form
async function checkVehicle3SigOnSelect(truckId) {
  const tenant = window.AppState?.tenant || 'demo';
  const assetType = document.getElementById('inspection-asset-type')?.value || 'truck';
  const driverSelect = document.getElementById('inspection-driver');
  const driverName = driverSelect?.options[driverSelect.selectedIndex]?.dataset?.name || '';
  const driverId = driverSelect?.value || '';

  try {
    const priorSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'pretrips'),
        where('truckId', '==', truckId),
        orderBy('createdAt', 'desc')
      )
    );
    // TASK-9691 — only a DVIR whose repair is CLOSED (stage
    // 'awaiting-next-driver', fresh linked-task read) is offered. A repair
    // still open is skipped here; submit's safety-hold check blocks it.
    const found = await findAckableDvir(tenant, priorSnap.docs);
    const needsAck = found && !found.error ? found.doc : null;

    if (needsAck) {
      const priorData = found.data;
      const truckSelect = document.getElementById('inspection-truck');
      const truckUnit = truckSelect?.options[truckSelect.selectedIndex]?.dataset?.unit || truckId;
      const priorDefects = priorData.defectNotes || priorData.defects?.join?.(', ') || 'See original DVIR';
      const mechanicName = priorData.mechanicSignedBy || 'Unknown';
      const priorDate = priorData.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || '';

      show3rdSignatureModal({
        dvirId: needsAck.id,
        truckUnit,
        defects: priorDefects,
        mechanicName,
        priorDate,
        driverName,
        driverId,
        tenant,
        repairs: found.linked.doneTasks,
      });
    }
  } catch (err) {
    console.error('[Pretrip] Error checking 3-sig on vehicle select:', err);
    // Don't block — they'll get caught at submit time as fallback
  }
}

// TASK-9691 — the caller only gets here at stage 'awaiting-next-driver'
// (findAckableDvir: mechanic signed AND every critical work order completed),
// so "has been repaired" is true when shown. `repairs` = the completed critical
// work orders (description + what was done) so the driver sees WHAT was fixed.
function show3rdSignatureModal({ dvirId, truckUnit, defects, mechanicName, priorDate, driverName, driverId, tenant, repairs = [] }) {
  // Store params in module state — avoids onclick string escaping issues (mobile bug fix)
  _pending3Sig = { dvirId, driverName, driverId, tenant };
  const repairLines = (repairs || []).filter(r => r && (r.description || r.completionNotes)).map(r => `
          <div style="font-size:0.82rem;color:var(--text-primary);margin-top:6px;">
            <span class="material-icons" style="font-size:14px;vertical-align:-2px;color:var(--action-success);">build</span>
            ${escapeHtml(r.description || 'Repair')}${r.completionNotes ? ` — <span style="color:var(--text-secondary);">${escapeHtml(r.completionNotes)}</span>` : ''}
          </div>`).join('');

  // Remove any existing overlay
  document.getElementById('dvir-3sig-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'dvir-3sig-overlay';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.6);z-index:10000;display:flex;align-items:center;justify-content:center;padding:16px;';
  overlay.innerHTML = `
    <div style="background:var(--bg-primary);border-radius:var(--radius-lg);max-width:480px;width:100%;max-height:90dvh;overflow-y:auto;box-shadow:var(--shadow-xl);">
      <div style="padding:20px 24px;border-bottom:1px solid var(--border-light);">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <span class="material-icons" style="font-size:28px;color:#d97706;">verified_user</span>
          <h3 style="margin:0;font-size:1.1rem;">Driver Acknowledgment Required</h3>
        </div>
        <p style="margin:0;font-size:0.85rem;color:var(--text-secondary);line-height:1.5;">
          <strong>Unit ${escapeHtml(truckUnit)}</strong> had a prior DVIR defect that has been repaired and signed off.
          Per DOT 49 CFR 396.13, you must acknowledge the repair before operating this vehicle.
        </p>
      </div>

      <div style="padding:16px 24px;">
        <div style="background:var(--bg-secondary);border-radius:var(--radius-md);padding:12px;margin-bottom:16px;border-left:3px solid #d97706;">
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:4px;">Prior Defect (${escapeHtml(priorDate)})</div>
          <div style="font-size:0.9rem;color:var(--text-primary);font-weight:500;">${escapeHtml(defects)}</div>
          ${repairLines ? `<div style="font-size:0.8rem;color:var(--text-secondary);margin-top:8px;font-weight:600;">What was repaired</div>${repairLines}` : ''}
          <div style="font-size:0.8rem;color:var(--text-secondary);margin-top:6px;">Repaired & signed off by: <strong>${escapeHtml(mechanicName)}</strong></div>
        </div>

        <div style="margin-bottom:12px;">
          <label style="font-size:0.85rem;font-weight:600;display:block;margin-bottom:6px;">Your Signature</label>
          <div style="border:2px solid var(--border-light);border-radius:var(--radius-md);background:#fff;position:relative;">
            <canvas id="dvir-3sig-canvas" width="400" height="120" style="width:100%;height:120px;touch-action:none;cursor:crosshair;"></canvas>
          </div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:4px;">
            <p style="font-size:0.75rem;color:var(--text-secondary);margin:0;">Sign above to acknowledge repairs</p>
            <button type="button" class="btn btn-sm btn-secondary" onclick="window.PretripModule.clear3rdSigCanvas()" style="font-size:0.7rem;padding:2px 8px;">Clear</button>
          </div>
        </div>
      </div>

      <div style="padding:12px 24px 20px;display:flex;gap:12px;">
        <button class="btn btn-secondary" style="flex:1;" onclick="document.getElementById('dvir-3sig-overlay')?.remove()">Cancel</button>
        <button class="btn btn-primary" style="flex:1;" id="btn-3sig-submit" onclick="window.PretripModule.submit3rdSignature()">
          <span class="material-icons" style="font-size:16px;">check</span> Acknowledge & Continue
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  // Init signature canvas
  setTimeout(() => {
    const canvas = document.getElementById('dvir-3sig-canvas');
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // Scale for retina
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    let drawing = false;
    const getPos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = getPos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const p = getPos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); };
    const end = () => { drawing = false; };

    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', end);
    canvas.addEventListener('mouseleave', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
  }, 100);
}

function clear3rdSigCanvas() {
  const canvas = document.getElementById('dvir-3sig-canvas');
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
}

async function submit3rdSignature() {
  if (!_pending3Sig) {
    window.showToast?.('Error: missing signature context', 'error');
    return;
  }
  const { dvirId, driverName, driverId, tenant } = _pending3Sig;

  const canvas = document.getElementById('dvir-3sig-canvas');
  if (!canvas) return;

  // Check canvas is not blank
  const ctx = canvas.getContext('2d');
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
  let hasContent = false;
  for (let i = 3; i < pixels.length; i += 4) {
    if (pixels[i] > 0) { hasContent = true; break; }
  }
  if (!hasContent) {
    window.showToast?.('Please sign to acknowledge the repair', 'error');
    return;
  }

  const btn = document.getElementById('btn-3sig-submit');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Saving...'; }

  const sigData = canvas.toDataURL('image/png');

  // TASK-9691 (COD Code Reviewer) — re-check at SUBMIT, not only when the
  // prompt opened: a work order reopened while the driver was signing must not
  // let them acknowledge a repair that is no longer done.
  const _recheck = await fetchDvirLinkedState(tenant, dvirId);
  if (_recheck.state !== 'ok' || _recheck.open > 0) {
    window.showToast?.(_recheck.state !== 'ok'
      ? 'Could not confirm the repair is still complete. Check your connection and try again.'
      : 'This repair was reopened — it is not done yet, so it can’t be acknowledged.', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">check</span> Acknowledge & Continue'; }
    if (_recheck.state === 'ok') { _pending3Sig = null; document.getElementById('dvir-3sig-overlay')?.remove(); }
    return;
  }

  try {
    try {
      window.logAudit?.('pretrip', 'DVIR_DEFECT_SAVE', {
        entityType: 'dvirDefect',
        entityId: dvirId,
        source: 'pretrip-3sig-acknowledge',
        redFlags: { defectCountDrop: false, photoLost: false, statusReverted: false, ackBy: driverName }
      });
    } catch {}
    await updateDoc(doc(db, 'tenants', tenant, 'pretrips', dvirId), {
      nextDriverSignature: sigData,
      nextDriverName: driverName,
      nextDriverId: driverId || null,
      nextDriverSignedAt: Timestamp.now()
    });
    // TASK-9372 — keep a loaded / by-id-cached copy in step with the write.
    const _acked = _findInspectionById(dvirId);
    if (_acked) {
      _acked.nextDriverSignature = sigData;
      _acked.nextDriverName = driverName;
      _acked.nextDriverId = driverId || null;
    }
    // TASK-9405 — the next-driver signature can CLOSE the defect; repaint the
    // list + counts (an older-page record has no live listener to do it).
    _refreshInspectionList();

    _pending3Sig = null;
    window.showToast?.('Repair acknowledged — you may now proceed with inspection', 'success');
    document.getElementById('dvir-3sig-overlay')?.remove();
  } catch (err) {
    console.error('[Pretrip] Error saving 3rd signature:', err);
    window.showToast?.('Error saving acknowledgment — please try again', 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;">check</span> Acknowledge & Continue'; }
  }
}

// TASK-9372 — the asset identity a DVIR-spawned work order carries, in
// Maintenance's OWN vocabulary ('truck' | 'trailer' | 'asset', maintenance.js
// saveTask). Before this every pretrip writer stamped assetType:'truck' +
// truckId for everything, so a critical defect on a mat was a truck-icon card
// that never showed under that mat's asset filter (which matches assetId).
// The full payload per type — the other two id pairs are nulled exactly the
// way Maintenance's own save does it.
function _dvirWorkOrderAssetFields(assetType, id, unit) {
  const _id = id || null;
  const _unit = unit || '';
  if (assetType === 'equipment' || assetType === 'asset') {
    return { assetType: 'asset', assetId: _id, assetName: _unit, truckId: null, truckUnit: null, trailerId: null, trailerUnit: null };
  }
  if (assetType === 'trailer') {
    return { assetType: 'trailer', trailerId: _id, trailerUnit: _unit, truckId: null, truckUnit: null, assetId: null, assetName: null };
  }
  return { assetType: 'truck', truckId: _id, truckUnit: _unit, trailerId: null, trailerUnit: null, assetId: null, assetName: null };
}

// TASK-9372 — which maintenance fields the pre-submit critical-defect safety
// hold looks up, per inspected asset type. DECISION: equipment is EXEMPT. The
// hold is the 49 CFR 396 vehicle out-of-service gate and its modal is worded
// for a vehicle ("Unit …"); equipment (mats, attachments) is not a CMV, and
// blocking the inspection itself would only stop the crew recording the next
// defect on it. Trailers are looked up by trailerId (new work orders) AND by
// truckId (every trailer work order written before this change).
function _safetyHoldLookupFields(assetType) {
  if (assetType === 'equipment') return [];
  if (assetType === 'trailer') return ['trailerId', 'truckId'];
  return ['truckId'];
}

// TASK-9372 (COD Independent) — every hold lookup runs INDEPENDENTLY
// (allSettled): one failed query can never skip the others. The caller
// FAILS CLOSED when any lookup failed and none found a hold — it cannot say
// the vehicle is clear, so it does not let the inspection through silently.
// (Equality-only `field == x && priority == 'critical'` needs no composite
// index — probed read-only against prod for truckId/trailerId/assetId — so a
// missing index is not the failure mode; offline/permission errors are.)
async function _lookupOpenSafetyHolds(tenant, assetType, assetId) {
  const fields = _safetyHoldLookupFields(assetType);
  if (!fields.length || !assetId) return { tasks: [], failed: [] };
  const results = await Promise.allSettled(fields.map(field => getDocs(query(
    collection(db, 'tenants', tenant, 'maintenance'),
    where(field, '==', assetId),
    where('priority', '==', 'critical')
  ))));
  const seen = new Set();
  const tasks = [];
  const failed = [];
  results.forEach((r, i) => {
    if (r.status !== 'fulfilled') { failed.push({ field: fields[i], error: r.reason }); return; }
    r.value.docs.forEach(d => {
      if (d.data().status !== 'completed' && !seen.has(d.id)) { seen.add(d.id); tasks.push(d); }
    });
  });
  return { tasks, failed };
}

async function saveInspection(e) {
  e.preventDefault();
  if (!window.validateRequiredFields(e.target)) return;
  // TASK-9397 — a photo still converting isn't in pendingPhotos yet; wait.
  // COD: if the inspection form was closed or a new one opened while we
  // waited, this Save belongs to a form that no longer exists — abort.
  const saveFormGen = PretripState.inspectionFormGen;
  if (!(await settlePhotoPrepBeforeSave(_dvirPhotoPrep, 'btn-submit-inspection'))) return;
  if (PretripState.inspectionFormGen !== saveFormGen) return;

  // Prevent duplicate submissions
  const submitBtn = document.getElementById('btn-submit-inspection');
  if (submitBtn?.disabled) {
    console.log('[Pretrip] Submission already in progress');
    return;
  }
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="material-icons rotating">sync</span> Submitting...';
  }

  // TASK-9554 — start the one position fix NOW (the Submit tap), so it runs
  // alongside the safety-hold / 3-signature lookups below. Never on page load.
  // Always resolves (≤ INSPECTION_GPS_HARD_CAP_MS); never blocks the save.
  const locationPromise = _inspectionGpsEnabled(window.AppState?.settings)
    ? captureInspectionLocation(typeof navigator !== 'undefined' ? navigator.geolocation : null)
    : null;

  const driverSelect = document.getElementById('inspection-driver');
  const truckSelect = document.getElementById('inspection-truck');
  const assetTypeSelect = document.getElementById('inspection-asset-type');

  let driverId = driverSelect.value;
  let driverName = driverSelect.options[driverSelect.selectedIndex]?.dataset?.name || '';

  // Handle unknown driver
  if (driverId === 'unknown') {
    const unknownNameInput = document.getElementById('unknown-driver-name');
    driverName = unknownNameInput?.value?.trim() || 'Unknown Driver';
    if (!driverName || driverName === 'Unknown Driver') {
      window.showToast?.('Please enter the driver\'s name', 'error');
      unknownNameInput?.focus();
      return;
    }
    driverId = null; // No roster ID for unknown drivers
  }

  const truckId = truckSelect.value;
  const truckUnit = truckSelect.options[truckSelect.selectedIndex]?.dataset?.unit || '';
  const assetType = assetTypeSelect?.value || 'truck';

  // Check for existing critical safety issues on this vehicle
  // TASK-9372 — keyed per asset type (_safetyHoldLookupFields); equipment exempt.
  const tenant = window.AppState?.tenant || 'demo';
  {
    const { tasks: openCriticalTasks, failed } = await _lookupOpenSafetyHolds(tenant, assetType, truckId);
    if (openCriticalTasks.length > 0) {
      showSafetyHoldModal(truckUnit, openCriticalTasks);
      // The button was disabled above; a blocked submit must not leave it stuck.
      if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = 'Submit Inspection'; }
      return;
    }
    if (failed.length > 0) {
      // FAIL CLOSED: we could not confirm there is no open critical defect.
      failed.forEach(f => console.error(`[Pretrip] Safety-hold lookup failed (${f.field}):`, f.error));
      window.showToast?.('Could not check this unit for open safety defects. Check your connection and submit again.', 'error');
      if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = 'Submit Inspection'; }
      return;
    }
  }

  // --- 3-Signature DVIR Check (DOT 49 CFR 396.13) ---
  // If this vehicle has a prior DVIR defect that was repaired (mechanic signed off)
  // but the next driver hasn't acknowledged the repair, require acknowledgment signature.
  try {
    const priorDvirsSnap = await getDocs(
      query(
        collection(db, 'tenants', tenant, 'pretrips'),
        where('truckId', '==', truckId),
        orderBy('createdAt', 'desc')
      )
    );
    // TASK-9691 — same gate as checkVehicle3SigOnSelect: offer the 396.13
    // acknowledgment only once the repair is closed (fresh read). If a
    // candidate's repair state can't be read, fail CLOSED like the safety-hold
    // lookup above — never skip a required acknowledgment on a read error.
    const found = await findAckableDvir(tenant, priorDvirsSnap.docs);
    if (found && found.error) {
      window.showToast?.('Could not check this unit for a repair acknowledgment. Check your connection and submit again.', 'error');
      if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = 'Submit Inspection'; }
      return;
    }
    const needsAck = found ? found.doc : null;

    if (needsAck) {
      const priorData = found.data;
      const priorDefects = priorData.defectNotes || priorData.defects?.join?.(', ') || 'See original DVIR';
      const mechanicName = priorData.mechanicSignedBy || 'Unknown';
      const priorDate = priorData.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) || '';

      // Show 3rd signature modal — block inspection until signed
      show3rdSignatureModal({
        dvirId: needsAck.id,
        truckUnit,
        defects: priorDefects,
        mechanicName,
        priorDate,
        driverName,
        driverId,
        tenant,
        repairs: found.linked.doneTasks,
      });

      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = 'Submit Inspection';
      }
      return;
    }
  } catch (err) {
    console.error('[Pretrip] Error checking 3-sig DVIR:', err);
    // Don't block on query errors
  }

  // Get DVIR defects text (primary defect reporting method)
  const dvirDefects = document.getElementById('dvir-defects').value.trim();

  // Get failed items from checkbox system (if used)
  const useCheckboxSystem = document.getElementById('use-checkbox-system')?.checked;
  const failedItems = [];
  const inspectionItems = getInspectionItems(assetType);
  if (useCheckboxSystem) {
    document.querySelectorAll('.checklist-item input:not(:checked)').forEach(input => {
      const item = inspectionItems.find(i => i.id === input.dataset.item);
      if (item) failedItems.push(item.label);
    });
  }

  // TASK-9741 — the unit's extra checklist, read from the block that was
  // actually rendered for THIS unit (never re-resolved here, so a settings edit
  // mid-form can't swap the list under the driver). Evaluated whether or not
  // the optional checkbox system is on.
  let tplEval = null;
  const unitChecks = PretripState.unitChecks;
  if (unitChecks && unitChecks.assetType === assetType && unitChecks.assetId === String(truckId) && window.InspectionTemplates) {
    const okByKey = {};
    document.querySelectorAll('#unit-checks-section input[data-tpl-key]').forEach(cb => { okByKey[cb.dataset.tplKey] = cb.checked; });
    tplEval = window.InspectionTemplates.evaluateInspectionTemplate(unitChecks.template, okByKey);
  }
  // A missing photo NEVER stops the DVIR from being filed (49 CFR 396.11: the
  // defect must be reported; a dead camera or no signal can't block that. COD
  // Domain). The driver is asked once; filing anyway stamps templatePhotoMissing.
  let tplPhotoMissing = false;
  if (tplEval && tplEval.photoNeeded && PretripState.pendingPhotos.length === 0) {
    const need = unitChecks.template.items.filter(it => it.photoRequired && tplEval.rows.some(r => r.id === it.id && !r.ok)).map(it => it.label);
    const fileAnyway = window.showConfirm
      ? await window.showConfirm('Photo needed', `Add a photo of: ${need.join(', ')}. Submit without the photo?`, { confirmText: 'Submit without photo', cancelText: 'Add photo' })
      : true;
    if (PretripState.inspectionFormGen !== saveFormGen) {
      const b = document.getElementById('btn-submit-inspection');
      if (b) { b.disabled = false; b.innerHTML = 'Submit Inspection'; }
      return;
    }
    if (!fileAnyway) {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = 'Submit Inspection'; }
      return;
    }
    tplPhotoMissing = true;
  }
  const tplFailedRequired = tplEval ? tplEval.failedRequired : [];
  const tplFailedOptional = tplEval ? tplEval.failedOptional : [];

  // Has defects if text defects OR checkbox defects (TASK-9741: OR a failed
  // must-pass unit check — same hold as any DVIR defect)
  const hasDefects = dvirDefects.length > 0 || failedItems.length > 0 || tplFailedRequired.length > 0;

  // Get signature - either from capture pad OR from driver's signature on file
  let signatureData = getSignatureData();

  // If no captured signature, check if driver has signature on file
  if (!signatureData && driverId && driverId !== 'unknown') {
    const driver = PretripState.people.find(p => p.id === driverId);
    if (driver?.signature) {
      signatureData = driver.signature;
    }
  }

  // Check if using printed name instead of signature
  const usingPrintedName = document.getElementById('dvir-use-printed-name')?.checked || false;
  const typedName = document.getElementById('dvir-typed-name')?.value?.trim() || '';

  if (usingPrintedName) {
    if (typedName.length < 3) {
      window.showToast?.('Please type your full legal name', 'error');
      document.getElementById('dvir-typed-name')?.focus();
      if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = 'Submit Inspection'; }
      return;
    }
  } else if (!signatureData) {
    window.showToast?.('Please sign above or check "Use printed name" to type your name instead', 'error');
    if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = 'Submit Inspection'; }
    return;
  }

  const usingNoSignatureFallback = usingPrintedName;

  const data = {
    driverId,
    driverName,
    truckId,
    truckUnit,
    assetId: truckId, // For consistency with PDF generation
    assetUnit: truckUnit,
    assetType, // truck | trailer | equipment
    // TASK-9372 — set when opened from a Job Board job (null otherwise), so the
    // job modal can list this job's inspections. Additive; no rules allowlist.
    jobId: PretripState.prefillJobId || null,
    jobDayId: PretripState.prefillJobDayId || null,
    type: document.getElementById('inspection-type').value,
    mileage: parseInt(document.getElementById('inspection-mileage').value) || null,
    hours: parseInt(document.getElementById('inspection-hours').value) || null,
    status: hasDefects ? 'defects' : 'pass',
    defects: tplFailedRequired.length ? [...failedItems, ...tplFailedRequired] : failedItems,
    defectNotes: dvirDefects, // Now using the main DVIR defects text field
    maintenanceNotes: document.getElementById('maintenance-notes').value.trim(),
    items: inspectionItems.map(i => i.label), // Save as array of labels for PDF
    useCheckboxSystem: useCheckboxSystem || false,
    driverSignature: signatureData, // Base64 signature image or null
    noSignatureFallback: usingNoSignatureFallback, // True if typed name used instead of signature
    typedNameConfirmation: usingNoSignatureFallback ? typedName : null, // Typed name when pad unavailable
    createdAt: (() => {
      // TASK-9923 — today = now; a backdated day = that day at now's company
      // wall-clock time, so it reads back as the picked day in the company zone.
      const dateVal = document.getElementById('inspection-date')?.value;
      if (dateVal) return Timestamp.fromMillis(window.RecordDate.stampMs(dateVal));
      return Timestamp.now();
    })(),
    submittedAt: Timestamp.now(),
    updatedAt: Timestamp.now()
  };

  // TASK-9659 — the shared meter plausibility rule (window.PmStatus): a reading
  // that is lower than this unit's last believable one, or more than it can run
  // since then, asks BEFORE anything is saved. "Submit anyway" still saves it,
  // but a driver's answer does not mark it confirmed — the office sees it
  // flagged on the Maintenance card with the fix there.
  if ((data.mileage || data.hours) && assetType !== 'equipment' && window.PmStatus) {
    const meterRows = await getMeterRows(tenant, data.truckId);
    const chk = meterRows ? window.PmStatus.pmCheckReading(meterRows, { miles: data.mileage, hours: data.hours, atMs: Date.now() }) : { ok: true };
    if (!chk.ok) {
      const msg = `${chk.problems.map(p => window.PmStatus.pmProblemText(p, data.truckUnit ? `unit ${data.truckUnit}` : '')).join(' ')} Check the number before you submit.`;
      const go = await window.showConfirm('Check this reading', msg, { confirmText: 'Submit anyway', cancelText: 'Fix the number', safeDefault: true, icon: 'error_outline' });
      if (PretripState.inspectionFormGen !== saveFormGen) {
        const stuckBtn = document.getElementById('btn-submit-inspection');
        if (stuckBtn) { stuckBtn.disabled = false; stuckBtn.innerHTML = 'Submit Inspection'; }
        return;
      }
      if (!go) {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = 'Submit Inspection'; }
        document.getElementById(chk.problems[0].meter === 'miles' ? 'inspection-mileage' : 'inspection-hours')?.focus();
        return;
      }
    }
  }

  // TASK-9554 — where it was submitted. Fields only exist when capture is on.
  let capturedLocation = null;
  if (locationPromise) {
    let loc;
    try { loc = await locationPromise; } catch (_) { loc = null; }
    data.locationStatus = (loc && loc.locationStatus) || 'unavailable';
    // Coordinates NEVER ride the pretrip doc (pretrips read = every same-tenant PIN
    // driver). They go to the office-only pretripLocations/{id} after the save.
    if (loc && loc.locationStatus === 'captured' && loc.location) capturedLocation = loc.location;
    // COD r2 (Code Reviewer): the fix can take up to INSPECTION_GPS_HARD_CAP_MS.
    // If the form was closed or a new one opened meanwhile, the checkbox and
    // photo reads below would come from the REPLACEMENT form — abort, same as
    // the TASK-9397 guard at the top of this function.
    if (PretripState.inspectionFormGen !== saveFormGen) {
      console.warn('[Pretrip] Inspection form changed while waiting for location; save aborted');
      // COD r3: this return sits before the try/finally that re-enables Submit.
      // The replacement form shares the button id, so un-stick it here or the
      // next tap dies at the 'already in progress' guard until reload.
      const stuckBtn = document.getElementById('btn-submit-inspection');
      if (stuckBtn) { stuckBtn.disabled = false; stuckBtn.innerHTML = 'Submit Inspection'; }
      return;
    }
  }

  // TASK-9741 — snapshot of the unit checks as answered (only when one applied,
  // so a tenant with no templates writes exactly the old record).
  if (tplEval) {
    data.inspectionTemplate = { id: unitChecks.template.id, name: unitChecks.template.name, matchedBy: unitChecks.matchedBy };
    // TASK-10135 — a unit checklist added to its type's: name the type's too.
    if (unitChecks.template.typeTemplateId) data.inspectionTemplate.typeTemplateId = unitChecks.template.typeTemplateId;
    data.templateItems = tplEval.rows;
    if (tplPhotoMissing) data.templatePhotoMissing = true;
  }

  // Build items status object (only if checkbox system was used)
  if (useCheckboxSystem) {
    data.itemsStatus = {};
    inspectionItems.forEach(item => {
      const checkbox = document.querySelector(`input[data-item="${item.id}"]`);
      data.itemsStatus[item.id] = { ok: checkbox?.checked ?? true };
    });
  }

  // Safety net: reset button if Firestore hangs on slow network (e.g. old Android device)
  const _submitTimeout = setTimeout(() => {
    const btn = document.getElementById('btn-submit-inspection');
    if (btn?.disabled) {
      btn.disabled = false;
      btn.innerHTML = 'Submit Inspection';
      window.showToast?.('Connection is slow — please check your network and try again', 'error');
    }
  }, 60000);

  try {
    const tenant = window.AppState?.tenant || 'demo';

    // Save inspection
    const docRef = await addDoc(collection(db, 'tenants', tenant, 'pretrips'), data);

    // TASK-9554 — coordinates go to the office-only parallel collection (best effort:
    // a failure here never un-files the DVIR; status stays on the pretrip).
    if (capturedLocation) {
      try {
        await setDoc(doc(db, 'tenants', tenant, 'pretripLocations', docRef.id), {
          driverId: data.driverId || null,
          lat: capturedLocation.lat,
          lng: capturedLocation.lng,
          accuracy: capturedLocation.accuracy,
          capturedAt: capturedLocation.capturedAt,
        });
      } catch (locErr) {
        console.warn('[Pretrip] Could not save inspection location:', locErr);
      }
    }

    // Upload photos if any
    if (PretripState.pendingPhotos.length > 0) {
      try {
        const photoMeta = await uploadPhotos(tenant, docRef.id, hasDefects);
        if (photoMeta.length > 0) {
          await updateDoc(doc(db, 'tenants', tenant, 'pretrips', docRef.id), {
            photos: photoMeta,
            photoCount: photoMeta.length
          });
        }
      } catch (photoErr) {
        console.error('[Pretrip] Photo upload error:', photoErr);
        window.showToast?.('Photos queued for upload when online', 'warning');
      }
      // Clean up object URLs
      PretripState.pendingPhotos.forEach(p => { if (p.thumbUrl) URL.revokeObjectURL(p.thumbUrl); });
      PretripState.pendingPhotos = [];
    }

    // If there are maintenance notes, create a maintenance task
    if (data.maintenanceNotes) {
      await addDoc(collection(db, 'tenants', tenant, 'maintenance'), {
        ..._dvirWorkOrderAssetFields(assetType, data.truckId, data.truckUnit),
        category: 'Pretrip Note',
        description: data.maintenanceNotes,
        status: 'open',
        priority: 'normal',
        source: 'pretrip',
        sourceId: docRef.id,
        createdAt: Timestamp.now(),
        createdBy: driverName
      });
      console.log('[Pretrip] Created maintenance task for notes');
    }

    // Save fluid entries to fluidsLog collection
    if (PretripState.pendingFluids.length > 0) {
      for (const fluid of PretripState.pendingFluids) {
        const fluidData = {
          truckId: data.truckId,
          truckUnit: data.truckUnit,
          fluidType: fluid.fluidType,
          amount: fluid.amount,
          unit: fluid.unit,
          mileageAtService: data.mileage || null,
          hoursAtService: data.hours || null,
          notes: '',
          loggedAt: Timestamp.now(),
          loggedBy: data.driverName,
          source: 'dvir',
          pretripId: docRef.id
        };
        if (fluid.fluidType === 'Oil') {
          fluidData.oilBrand = fluid.oilBrand || '';
          fluidData.oilWeight = fluid.oilWeight || '';
          fluidData.filterReplaced = false;
        }
        await addDoc(collection(db, 'tenants', tenant, 'fluidsLog'), fluidData);
      }
      console.log(`[Pretrip] Saved ${PretripState.pendingFluids.length} fluid entries`);
      PretripState.pendingFluids = [];
    }

    // If there are safety defects (text-based), create a critical maintenance task
    if (dvirDefects) {
      await addDoc(collection(db, 'tenants', tenant, 'maintenance'), {
        ..._dvirWorkOrderAssetFields(assetType, data.truckId, data.truckUnit),
        category: 'DVIR Defect',
        description: dvirDefects,
        status: 'open',
        priority: 'critical',
        source: 'pretrip',
        sourceId: docRef.id,
        createdAt: Timestamp.now(),
        createdBy: driverName
      });
      console.log('[Pretrip] Created DVIR defect task from text');
    }

    // If checkbox system was used and items failed, create additional tasks
    if (failedItems.length > 0) {
      for (const defect of failedItems) {
        await addDoc(collection(db, 'tenants', tenant, 'maintenance'), {
          ..._dvirWorkOrderAssetFields(assetType, data.truckId, data.truckUnit),
          category: 'DVIR Defect',
          description: defect,
          status: 'open',
          priority: 'critical',
          source: 'pretrip',
          sourceId: docRef.id,
          createdAt: Timestamp.now(),
          createdBy: driverName
        });
      }
      console.log('[Pretrip] Created', failedItems.length, 'DVIR defect tasks from checklist');
    }

    // TASK-9741 — failed unit checks. Must-pass -> the same CRITICAL 'DVIR
    // Defect' work order as a checklist defect (so the same safety hold, the
    // same mechanic + next-driver sign-off). Optional -> a normal 'Pretrip
    // Note' work order, no hold.
    if (tplEval && (tplFailedRequired.length || tplFailedOptional.length)) {
      const tplName = unitChecks.template.name;
      for (const label of tplFailedRequired) {
        await addDoc(collection(db, 'tenants', tenant, 'maintenance'), {
          ..._dvirWorkOrderAssetFields(assetType, data.truckId, data.truckUnit),
          category: 'DVIR Defect',
          description: `${label} (${tplName})`,
          status: 'open',
          priority: 'critical',
          source: 'pretrip',
          sourceId: docRef.id,
          inspectionTemplateId: unitChecks.template.id,
          createdAt: Timestamp.now(),
          createdBy: driverName
        });
      }
      for (const label of tplFailedOptional) {
        await addDoc(collection(db, 'tenants', tenant, 'maintenance'), {
          ..._dvirWorkOrderAssetFields(assetType, data.truckId, data.truckUnit),
          category: 'Pretrip Note',
          description: `${label} (${tplName})`,
          status: 'open',
          priority: 'normal',
          source: 'pretrip',
          sourceId: docRef.id,
          inspectionTemplateId: unitChecks.template.id,
          createdAt: Timestamp.now(),
          createdBy: driverName
        });
      }
    }

    // Update truck meters if mileage or hours provided
    if (data.mileage || data.hours) {
      // TASK-9659 — plausibility was checked before the save (window.PmStatus);
      // the old "less than last recorded … will be ignored" toasts were wrong
      // (nothing ignored it here) and are gone.
      await addDoc(collection(db, 'tenants', tenant, 'metersLog'), {
        truckId: data.truckId,
        truckUnit: data.truckUnit,
        type: 'reading',
        miles: data.mileage || null,
        hours: data.hours || null,
        timestamp: Timestamp.now(),
        source: 'dvir',
        sourceId: docRef.id,
        createdAt: serverTimestamp() // TASK-9898 follow-up: the 7-day review clock (server time)
      });
      console.log('[Pretrip] Updated truck meters from DVIR');
    }

    // Process return inspection if active
    if (PretripState.activeReturnInspection && assetType === 'equipment') {
      await processReturnInspection(tenant, docRef.id, data);
    }

    window.showToast?.('Inspection submitted!', 'success');
    closeModal('inspection'); // TASK-9372 — also disarms prefillJobId/prefillJobDayId

    // Dispatch event for tutorial system
    window.dispatchEvent(new CustomEvent('pretrip:saved', {
      detail: {
        id: docRef.id,
        hasDefects: hasDefects,
        hasMaintenanceNotes: !!data.maintenanceNotes
      }
    }));

  } catch (error) {
    console.error('[Pretrip] Error saving inspection:', error);
    window.showToast?.('Error saving inspection', 'error');
  } finally {
    clearTimeout(_submitTimeout);
    // Always re-enable submit button
    const btn = document.getElementById('btn-submit-inspection');
    if (btn) {
      btn.disabled = false;
      btn.innerHTML = 'Submit Inspection';
    }
  }
}

// Legacy stub
function toggleNoSignature() {}

function togglePrintedName() {
  const checked = document.getElementById('dvir-use-printed-name')?.checked;
  const nameGroup = document.getElementById('dvir-printed-name-group');
  const sigSection = document.querySelector('.dvir-signature-section');
  if (nameGroup) nameGroup.classList.toggle('hidden', !checked);
  if (sigSection) sigSection.style.opacity = checked ? '0.3' : '1';
  if (checked) document.getElementById('dvir-typed-name')?.focus();
}

// ============================================
// PHOTO HANDLING
// ============================================

// TASK-9397 — pick-time photo work saveInspection waits out.
const _dvirPhotoPrep = createPhotoPrepTracker();

function handlePhotoCapture(event) {
  return _dvirPhotoPrep.track(_handlePhotoCapture(event));
}

async function _handlePhotoCapture(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  event.target.value = ''; // reset input
  const pendingList = PretripState.pendingPhotos;

  const maxPhotos = STARTER_LIMITS.dvir.maxPhotos;
  if (PretripState.pendingPhotos.length >= maxPhotos) {
    window.showToast?.(`Maximum ${maxPhotos} photos per inspection`, 'error');
    return;
  }

  const validation = validateImageFile(file);
  if (!validation.valid) {
    window.showToast?.(validation.error, 'error');
    return;
  }

  try {
    // TASK-9397 — a HEIC this browser can't draw (Windows Chrome/Firefox)
    // used to fail here with "Failed to load image"; convert it first.
    const drawable = await toDrawablePhoto(file);
    const blob = await processPhoto(drawable);
    const thumbBlob = await generateThumbnail(drawable);
    // The inspection form was reset/reopened while we converted — drop it.
    if (PretripState.pendingPhotos !== pendingList) return;
    const thumbUrl = URL.createObjectURL(thumbBlob);
    const id = `photo_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

    PretripState.pendingPhotos.push({ id, blob, thumbBlob, thumbUrl, isDefectPhoto: false });
    renderPhotoGrid();
    window.showToast?.('Photo added', 'success');
  } catch (err) {
    console.error('[Pretrip] Photo processing failed:', err?.cause || err);
    window.showToast?.(photoErrorMessage(err, err?.message || 'Failed to process photo'), 'error');
  }
}

function removePhoto(photoId) {
  const idx = PretripState.pendingPhotos.findIndex(p => p.id === photoId);
  if (idx >= 0) {
    const photo = PretripState.pendingPhotos[idx];
    if (photo.thumbUrl) URL.revokeObjectURL(photo.thumbUrl);
    PretripState.pendingPhotos.splice(idx, 1);
    renderPhotoGrid();
  }
}

function renderPhotoGrid() {
  const grid = document.getElementById('dvir-photo-grid');
  const countEl = document.getElementById('photo-count');
  const addBtn = document.getElementById('btn-add-photo');
  if (!grid) return;

  const count = PretripState.pendingPhotos.length;
  const max = STARTER_LIMITS.dvir.maxPhotos;
  if (countEl) countEl.textContent = `${count} of ${max}`;
  if (addBtn) addBtn.disabled = count >= max;

  if (count === 0) {
    grid.innerHTML = '';
    return;
  }

  grid.innerHTML = PretripState.pendingPhotos.map(p => `
    <div class="dvir-photo-thumb" data-id="${p.id}">
      <img src="${p.thumbUrl}" alt="Photo">
      <button type="button" class="dvir-photo-remove" onclick="window.PretripModule.removePhoto('${p.id}')">
        <span class="material-icons">close</span>
      </button>
    </div>
  `).join('');
}

/**
 * Upload pending photos to Firebase Storage and return photo metadata array
 */
async function uploadPhotos(tenantId, pretripId, isDefectInspection) {
  const photos = PretripState.pendingPhotos;
  if (photos.length === 0) return [];

  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const basePath = `tenants/${tenantId}/photos/dvir/${year}/${month}/${pretripId}`;
  const results = [];

  for (const photo of photos) {
    const photoId = photo.id;
    const fullPath = `${basePath}/${photoId}.jpg`;
    const thumbPath = `${basePath}/${photoId}_thumb.jpg`;

    try {
      // Upload main image
      const fullRef = ref(storage, fullPath);
      await uploadBytes(fullRef, photo.blob, { contentType: 'image/jpeg' });
      const fullUrl = await getDownloadURL(fullRef);

      // Upload thumbnail
      const thumbRef = ref(storage, thumbPath);
      await uploadBytes(thumbRef, photo.thumbBlob, { contentType: 'image/jpeg' });
      const thumbUrl = await getDownloadURL(thumbRef);

      results.push({
        id: photoId,
        thumbUrl,
        fullUrl,
        thumbPath,
        fullPath,
        sizeBytes: photo.blob.size,
        uploadedAt: Timestamp.now(),
        isDefectPhoto: isDefectInspection
      });
    } catch (err) {
      console.error('[Pretrip] Photo upload failed:', err);
      // Queue for offline retry
      try {
        await photoQueue.enqueue({
          id: photoId,
          scope: _pqScope(tenantId),
          pretripId,
          blob: photo.blob,
          thumbBlob: photo.thumbBlob,
          isDefectPhoto: isDefectInspection
        });
      } catch (queueErr) {
        // TASK-4604 — a refused/failed enqueue LOSES this photo: saveInspection
        // clears PretripState.pendingPhotos, so there is no staged copy to fall
        // back on. Console-only made that invisible; logError makes it countable.
        console.error('[Pretrip] Failed to queue photo:', queueErr);
        try {
          window.logError?.(queueErr, {
            module: 'pretrip', operation: 'PHOTO_QUEUE_ENQUEUE_FAILED',
            pretripId, photoId, reason: String(queueErr && queueErr.message || queueErr),
          });
        } catch (_) { /* breadcrumb is best-effort */ }
      }
    }
  }

  return results;
}

/**
 * Process offline photo queue when connectivity returns
 */
// TASK-4604 — the queue is per-ORIGIN, so a shared yard tablet holds every
// driver's blobs. Scope every read to THIS tenant + THIS driver.
//
// 🔴 personId, NOT the Firebase uid. My first cut used `AppState.user.uid` and
// COD killed it in BOTH directions, which is what makes this the load-bearing
// line: a PIN logout (`_teardownDriverPinChrome`) deliberately does NOT sign the
// anonymous user out, and `ensureDriverAnonAuth` REUSES the existing anon user —
// so Driver A and Driver B on the same tablet share a uid and the cross-driver
// leak survived untouched for the same-tenant case, which is the common one. And
// in the other direction, any session where the uid DID rotate would orphan a
// driver from their own queued photos — silent loss, the failure this queue
// exists to prevent. `driverPersonId` is set from `driverSessions/{uid}.personId`
// on every PIN login and is stable per human.
//
// Falls back to the member uid for a signed-in (non-PIN) user, who has no
// driverPersonId; that path is one-human-per-login so a uid is a fine identity
// there. Never falls back to a constant — a shared bucket is the bug.
function _pqScope(tenantId) {
  const who = window.AppState?.driverPersonId
    || window.AppState?.user?.uid
    || null;
  if (!who) return null; // no identity → callers refuse rather than share a bucket
  return `${tenantId || 'nt'}::${who}`;
}

async function processOfflineQueue(tenantId) {
  // includeLegacy: entries queued before the scope existed. They are NOT trusted
  // — each is ownership-checked below against THIS tenant before any upload.
  const _scope = _pqScope(tenantId);
  if (!_scope) {
    // No identity resolved yet (auth still settling). Do NOT drain — draining
    // without a scope is exactly the bug. Silent by design here: the `online`
    // handler re-fires, and a banner is a separate slice.
    console.warn('[Pretrip] offline queue drain skipped — no driver identity yet');
    return;
  }
  const pending = await photoQueue.getPending(_scope, { includeLegacy: true });
  if (pending.length === 0) return;

  console.log(`[Pretrip] Processing ${pending.length} queued photos`);

  for (const item of pending) {
    if (!item.pretripId) continue;
    // TASK-4604 — a legacy (pre-scope) entry may belong to another driver or
    // another TENANT. Before it was scoped, this loop uploaded whatever it found
    // into `tenants/${tenantId}/...` — the tenant of whoever happened to be
    // logged in. Prove the pretrip actually lives in THIS tenant first; if it
    // does not, leave the entry alone rather than misfiling or binning it.
    if (item.legacy) {
      try {
        const _t = tenantId === 'demo-tenant' ? 'demo' : tenantId;
        const _owned = await getDoc(doc(db, 'tenants', _t, 'pretrips', item.pretripId));
        if (!_owned.exists()) continue;
        // 🔴 Existing-in-this-tenant is NOT ownership. On a shared tablet within
        // ONE tenant, Driver B would otherwise drain Driver A's legacy photo into
        // A's DVIR and dequeue it — the same cross-driver leak, just laundered
        // through a tenant check (COD). For a PIN/driver session, require the
        // pretrip's own driver to be THIS driver.
        const _me = window.AppState?.driverPersonId || null;
        if (_me) {
          const _d = _owned.data() || {};
          const _owner = _d.driverId || _d.personId || null;
          if (_owner && String(_owner) !== String(_me)) continue;
        }
      } catch (_) { continue; }
    }
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const basePath = `tenants/${tenantId}/photos/dvir/${year}/${month}/${item.pretripId}`;

    try {
      const fullPath = `${basePath}/${item.id}.jpg`;
      const thumbPath = `${basePath}/${item.id}_thumb.jpg`;

      const fullRef = ref(storage, fullPath);
      await uploadBytes(fullRef, item.blob, { contentType: 'image/jpeg' });
      const fullUrl = await getDownloadURL(fullRef);

      const thumbRef = ref(storage, thumbPath);
      await uploadBytes(thumbRef, item.thumbBlob, { contentType: 'image/jpeg' });
      const thumbUrl = await getDownloadURL(thumbRef);

      // Update Firestore document with the photo
      const tenant = tenantId === 'demo-tenant' ? 'demo' : tenantId;
      const pretripRef = doc(db, 'tenants', tenant, 'pretrips', item.pretripId);
      const { getDoc } = await __ddChunkWrap(import('../firebase-config.js?v=1868'));
      const pretripDoc = await getDoc(pretripRef);
      if (pretripDoc.exists()) {
        const data = pretripDoc.data();
        const photos = data.photos || [];
        const photosBefore = photos.length;
        photos.push({
          id: item.id,
          thumbUrl,
          fullUrl,
          thumbPath,
          fullPath,
          sizeBytes: item.blob.size,
          uploadedAt: Timestamp.now(),
          isDefectPhoto: item.isDefectPhoto
        });
        try {
          window.logAudit?.('pretrip', 'DVIR_DEFECT_SAVE', {
            entityType: 'dvirDefect',
            entityId: item.pretripId,
            source: 'pretrip-photo-queue-flush',
            before: { photoCount: photosBefore },
            after: { photoCount: photos.length },
            redFlags: { defectCountDrop: false, photoLost: photos.length < photosBefore, statusReverted: false, isDefectPhoto: !!item.isDefectPhoto }
          });
        } catch {}
        await updateDoc(pretripRef, { photos, photoCount: photos.length });
      }

      await photoQueue.dequeue(item.id);
      console.log(`[Pretrip] Uploaded queued photo: ${item.id}`);
    } catch (err) {
      console.error(`[Pretrip] Failed to upload queued photo ${item.id}:`, err);
      await photoQueue.incrementRetry(item.id);
    }
  }
}

/**
 * Show full-size photo in a lightbox.
 * TASK-1220 — promoted to the shared window.PhotoLightbox (app.js, same
 * .photo-lightbox CSS). Local name kept for existing pretrip call sites;
 * app.js assigns PhotoLightbox at load, long before any click can land.
 */
function showFullPhoto(url) {
  window.PhotoLightbox?.show(url);
}

// ============================================
// SIGNATURE PAD
// ============================================
let signatureCanvas = null;
let signatureCtx = null;
let isDrawing = false;
let hasSignature = false;

function initSignaturePad() {
  signatureCanvas = document.getElementById('signature-canvas');
  if (!signatureCanvas) return;

  // TASK-4534 — the CSS renders this canvas at width:100%/max-400px while the
  // backing store was the fixed 400x150 attr, and the draw handlers use raw
  // CSS-pixel coords: on any phone narrower than ~432px the stroke landed
  // RIGHT of the finger (offset + squeezed), and on every high-DPI screen it
  // was blurry. Back the store at rect x devicePixelRatio and scale the ctx so
  // CSS-pixel drawing is exact; a driver signs this every morning.
  const _rect = signatureCanvas.getBoundingClientRect();
  const _dpr = window.devicePixelRatio || 1;
  if (_rect.width > 2 && _rect.height > 2) {
    signatureCanvas.width = Math.round(_rect.width * _dpr);
    signatureCanvas.height = Math.round(_rect.height * _dpr);
  }

  signatureCtx = signatureCanvas.getContext('2d');
  if (_rect.width > 2 && _rect.height > 2) signatureCtx.scale(_dpr, _dpr);

  // Set up canvas styling
  signatureCtx.strokeStyle = '#1a237e';
  signatureCtx.lineWidth = 2;
  signatureCtx.lineCap = 'round';
  signatureCtx.lineJoin = 'round';

  // Clear canvas with white background
  signatureCtx.fillStyle = '#ffffff';
  signatureCtx.fillRect(0, 0, signatureCanvas.width, signatureCanvas.height);

  // Mouse events
  signatureCanvas.addEventListener('mousedown', startDrawing);
  signatureCanvas.addEventListener('mousemove', draw);
  signatureCanvas.addEventListener('mouseup', stopDrawing);
  signatureCanvas.addEventListener('mouseout', stopDrawing);

  // Touch events for mobile
  signatureCanvas.addEventListener('touchstart', handleTouchStart, { passive: false });
  signatureCanvas.addEventListener('touchmove', handleTouchMove, { passive: false });
  signatureCanvas.addEventListener('touchend', stopDrawing);
}

function startDrawing(e) {
  isDrawing = true;
  hasSignature = true;
  const rect = signatureCanvas.getBoundingClientRect();
  signatureCtx.beginPath();
  signatureCtx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
}

function draw(e) {
  if (!isDrawing) return;
  const rect = signatureCanvas.getBoundingClientRect();
  signatureCtx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
  signatureCtx.stroke();
}

function stopDrawing() {
  isDrawing = false;
}

function handleTouchStart(e) {
  e.preventDefault();
  const touch = e.touches[0];
  const rect = signatureCanvas.getBoundingClientRect();
  isDrawing = true;
  hasSignature = true;
  signatureCtx.beginPath();
  signatureCtx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
}

function handleTouchMove(e) {
  e.preventDefault();
  if (!isDrawing) return;
  const touch = e.touches[0];
  const rect = signatureCanvas.getBoundingClientRect();
  signatureCtx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
  signatureCtx.stroke();
}

function clearSignature() {
  if (!signatureCanvas || !signatureCtx) {
    initSignaturePad();
    return;
  }
  signatureCtx.fillStyle = '#ffffff';
  signatureCtx.fillRect(0, 0, signatureCanvas.width, signatureCanvas.height);
  hasSignature = false;
}

function getSignatureData() {
  if (!hasSignature || !signatureCanvas) return null;

  // Check if canvas is actually drawn on (not just white)
  const imageData = signatureCtx.getImageData(0, 0, signatureCanvas.width, signatureCanvas.height);
  const pixels = imageData.data;
  let hasContent = false;

  for (let i = 0; i < pixels.length; i += 4) {
    // Check if any pixel is not white (255,255,255)
    if (pixels[i] < 250 || pixels[i+1] < 250 || pixels[i+2] < 250) {
      hasContent = true;
      break;
    }
  }

  if (!hasContent) return null;

  return signatureCanvas.toDataURL('image/png');
}

function filterInspections(filter) {
  // Update active filter
  document.querySelectorAll('.pretrip-filter').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.filter === filter);
  });

  renderInspectionsList(filter);
  updateCounts();
}

function searchInspections(query) {
  PretripState.searchQuery = (query || '').trim().toLowerCase();
  const currentFilter = document.querySelector('.pretrip-filter.active')?.dataset?.filter || PRETRIP_DEFAULT_FILTER;
  renderInspectionsList(currentFilter);
  updateCounts();
}

// TASK-342 — delegate to the safety meeting modal builder which lives in
// jobboard.js. JobBoard module is already loaded by the time the driver
// reaches the Inspections view (driver PIN routes through JobBoard first),
// so window.JobBoardModule.openSafetyMeetingModal is reliably available.
// Defensive guard if for some path it isn't.
function openSafetyMeetingSignIn() {
  if (typeof window.JobBoardModule?.openSafetyMeetingModal === 'function') {
    window.JobBoardModule.openSafetyMeetingModal();
  } else {
    window.showToast?.('Safety meeting sign-in is loading — try again in a moment', 'info');
  }
}

// TASK-437 Slice 2 — PPE sign-off tab handler. Delegates to the jobboard.js
// modal builder (same pattern as openSafetyMeetingSignIn) so the identity
// resolver + signature pad helpers live in one place. The Job Board module is
// loaded as part of the driver portal bundle, so JobBoardModule is present.
function openPpe() {
  if (typeof window.JobBoardModule?.openPpeModal === 'function') {
    window.JobBoardModule.openPpeModal();
  } else {
    window.showToast?.('PPE sign-off is loading — try again in a moment', 'info');
  }
}

function closeModal(type) {
  document.getElementById(`modal-${type}`).classList.add('hidden');
  // TASK-9372 — cancel / close / post-save all land here: disarm the job stamp.
  if (type === 'inspection') {
    PretripState.inspectionFormGen++;
    PretripState.prefillJobId = null;
    PretripState.prefillJobDayId = null;
    PretripState._prefillSeq++;
  }
}

// ============================================
// UTILITIES
// ============================================
// TASK-862 — delegates to shared window.escapeHtml (sharedFormat.js). Escapes the
// identical set (&<>"', '->&#39;) for every string/falsy input; the shared helper
// also String()-coerces a non-string truthy input (former `str.replace` threw) —
// strictly safer, never a rendering regression. Hoisted wrapper, lookup at call time.
function escapeHtml(str) {
  return window.escapeHtml(str);
}

// HOTFIX (stored XSS) — onclick="fn('${…}')" is a JS string inside an attribute;
// the parser decodes escapeHtml's &#39; back to ' before the handler compiles.
// Delegates to sharedFormat.js escJsAttr (TASK-4838), looked up at call time.
function escJsAttr(str) {
  return window.escJsAttr(str);
}

function formatDate(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTime(date) {
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ============================================
// DVIR TAB SWITCHING (Inspections | JSA)
// ============================================
function switchDvirTab(tab) {
  // Update tab buttons
  document.getElementById('dvir-tab-inspections')?.classList.toggle('active', tab === 'inspections');
  document.getElementById('dvir-tab-jsa')?.classList.toggle('active', tab === 'jsa');
  document.getElementById('dvir-tab-jobboard')?.classList.toggle('active', tab === 'jobboard');

  // Update panels
  const inspPanel = document.getElementById('inspections-panel');
  const jsaPanel = document.getElementById('jsa-panel');
  const jbPanel = document.getElementById('jobboard-panel');
  if (inspPanel) inspPanel.classList.toggle('active', tab === 'inspections');
  if (jsaPanel) jsaPanel.classList.toggle('active', tab === 'jsa');
  if (jbPanel) {
    jbPanel.classList.toggle('active', tab === 'jobboard');
    // Init Job Board on first click. jobboard.js is already loaded + evaluated
    // (app.js statically imports initJobBoard at boot), so call it via
    // window.JobBoardModule instead of a dynamic `import('./jobboard.js?v=…')` —
    // the ?v= cross-import spun up a SECOND module instance at a stale buster
    // (Pitfall #1 / TASK-979). No async load now, so no promise/.catch.
    if (tab === 'jobboard' && !PretripState._jobBoardLoaded) {
      PretripState._jobBoardLoaded = true;
      try {
        // Clean up any previous job board cleanup
        if (PretripState._jobBoardCleanup) {
          PretripState._jobBoardCleanup();
        }
        PretripState._jobBoardCleanup = window.JobBoardModule.initJobBoard(jbPanel, window.AppState?.tenant);
        // TASK-587 — for PIN drivers the persistent shell chrome (app.js
        // _syncDriverPinShellChrome) already provides #driver-bottom-nav +
        // #driver-actions-sheet on this view. The embedded board renders its
        // own copies inside the panel — strip them so the ids never
        // double-render (and so they don't vanish with the hidden panel when
        // the driver switches back to the Inspections tab). initJobBoard
        // renders this markup once (snapshot re-renders only touch
        // #jobboard-content), so the removal sticks.
        if (window.AppState?.driverModePin && document.getElementById('driver-pin-shell-chrome')) {
          jbPanel.querySelector('#driver-bottom-nav')?.remove();
          jbPanel.querySelector('#driver-actions-sheet')?.remove();
        }
      } catch (err) {
        console.error('[Pretrip] Failed to load Job Board:', err);
        jbPanel.innerHTML = '<div class="empty-state"><div class="empty-state-text">Failed to load Job Board.</div></div>';
        PretripState._jobBoardLoaded = false; // allow a retry on next tab click
      }
    }
  }
}

// ============================================
// TASK-9741 — UNIT CHECKLISTS EDITOR (office) + BLANK PDF
// ============================================
// Templates live on the tenant ROOT doc at settings.inspectionTemplates (every
// session reads that doc, PIN drivers included). Writers: admin, or a member
// holding the maintenance module (firestore.rules t9337MaintenanceKeys) —
// module access, not roles. The shared module sanitizes every template before
// it is written; the driver form re-normalizes on read.

function canManageInspectionTemplates() {
  const st = window.AppState || {};
  if (st.driverMode || st.isPublicAccess || st.driverModePin === true) return false;
  if (typeof window.hrEffectiveAdmin === 'function' && window.hrEffectiveAdmin()) return true;
  const ma = st.memberAccess;
  if (!ma || ma.viewUploadOnly === true) return false;
  return Array.isArray(ma.modules) && ma.modules.includes('maintenance');
}

function _tplList() {
  return window.InspectionTemplates.normalizeInspectionTemplates(window.AppState?.settings?.inspectionTemplates);
}

function _tplNewId(prefix) {
  return prefix + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

const _TPL_KIND_LABEL = { truck: 'Vehicles', trailer: 'Trailers', equipment: 'Equipment' };
const _TPL_TYPE_LIST_KIND = { truck: 'truckTypes', trailer: 'trailerTypes', equipment: 'assetCategories' };

function _tplUnitsFor(kind) {
  if (kind === 'equipment') return PretripState.assets || [];
  if (kind === 'trailer') return PretripState.trailers || [];
  return (PretripState.trucks || []).filter(t => !String(t.type || '').toLowerCase().includes('trailer'));
}

function _tplUnitLabel(kind, u) {
  if (kind === 'equipment') return String(u.name || u.id);
  return String(window.getUnitDisplay ? window.getUnitDisplay(u) : (u.unit || u.nickname || u.id));
}

// Type choices = the tenant's type list + every type its units actually carry,
// keyed lower-case (the resolver compares lower-case). Falls back to the units
// alone when roster's helper is not loaded.
function _tplTypeChoices(kind) {
  const units = _tplUnitsFor(kind);
  const field = kind === 'equipment' ? 'category' : 'type';
  const seen = new Map();
  if (typeof window.getTypeListWithInUse === 'function') {
    try {
      window.getTypeListWithInUse(_TPL_TYPE_LIST_KIND[kind], units, { caseInsensitive: true }).forEach(t => {
        const key = window.InspectionTemplates.inspectionTypeKey(t.slug);
        // Same wording as roster's type pickers for a value no type-list entry carries.
        if (key && key !== 'other' && !seen.has(key)) seen.set(key, (t.label || t.slug) + (t.unregistered ? ' (not on type list)' : ''));
      });
    } catch (_) { /* fall through to the in-use scan */ }
  }
  units.forEach(u => {
    const key = window.InspectionTemplates.inspectionTypeKey(u && u[field]);
    if (key && !seen.has(key)) seen.set(key, String(u[field]) + ' (not on type list)');
  });
  return [...seen.entries()].map(([key, label]) => ({ key, label }));
}

// TASK-10135 — a stored type key ("bottle_vac") as the office named it
// ("Bottle/Vac"): the type list's label when there is one, else the key made
// readable. Never the raw key.
function _tplTypeLabel(kind, key, choices) {
  const hit = (choices || _tplTypeChoices(kind)).find(t => t.key === key);
  if (hit) return String(hit.label).replace(/ \(not on type list\)$/, '');
  return String(key || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const _TPL_PHASE_LABEL = { both: 'Pre & post', pre: 'Pre-use only', post: 'Post-use only' };

// TASK-10135 — what the resolver will ignore because of this checklist, in
// plain words (first listed wins, the same rule the driver form uses).
function _tplConflictLines(list, id) {
  const IT = window.InspectionTemplates;
  const me = list.find(t => t.id === id);
  if (!me || typeof IT.inspectionTemplateConflicts !== 'function') return [];
  const units = _tplUnitsFor(me.assetKind);
  const choices = _tplTypeChoices(me.assetKind);
  const what = c => c.kind === 'type'
    ? `${_tplTypeLabel(me.assetKind, c.key, choices)} units`
    : _tplUnitLabel(me.assetKind, units.find(u => String(u.id) === c.key) || { id: c.key });
  const hasMustPass = tid => (list.find(t => t.id === tid)?.items || []).some(it => it.required);
  return IT.inspectionTemplateConflicts(list, id).map(c => {
    // COD r1 (Domain): say when the ignored checklist carries must-pass items.
    const mp = hasMustPass(c.loserId) ? ' Its must-pass items will not reach drivers there.' : '';
    if (c.loserId !== id) return `"${c.loserName}" is ignored for ${what(c)}: this checklist is listed first.${mp}`;
    // COD r1 (Code): a checklist still applies to units it names by name,
    // whichever type checklist wins (the unit match is checked first).
    // COD r2: only when it really wins one of its named units (no earlier ON
    // checklist names that unit — the resolver's unit pass, first listed wins).
    const ownUnits = c.kind === 'type' ? me.unitIds.filter(uid => list.find(t => t.active && t.assetKind === me.assetKind && t.unitIds.includes(uid))?.id === me.id).length : 0;
    const still = ownUnits ? ` It still applies to the ${ownUnits === 1 ? 'unit' : ownUnits + ' units'} picked by name.` : '';
    return `${what(c)} already ${c.kind === 'type' ? 'use' : 'uses'} "${c.winnerName}", so this checklist is ignored for ${c.kind === 'type' ? 'them' : 'it'}.${mp}${still}`;
  });
}

function _ensureTplModal() {
  let m = document.getElementById('modal-inspection-templates');
  if (m) return m;
  m = document.createElement('div');
  m.className = 'modal hidden';
  m.id = 'modal-inspection-templates';
  m.innerHTML = `
    <div class="modal-content modal-large" style="max-height:90vh;max-height:90dvh;overflow-y:auto;">
      <div class="modal-header">
        <h3 id="tpl-modal-title">Unit Checklists</h3>
        <button class="btn-icon" onclick="window.PretripModule.closeInspectionTemplates()" aria-label="Close">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" id="tpl-modal-body"></div>
    </div>`;
  document.body.appendChild(m);
  return m;
}

function openInspectionTemplates() {
  if (!canManageInspectionTemplates()) {
    window.showToast?.('You need the Maintenance module to manage unit checklists.', 'error');
    return;
  }
  const m = _ensureTplModal();
  PretripState.tplDraft = null;
  _renderTplList();
  m.classList.remove('hidden');
}

// TASK-10135 (QA edge 10) — Maintenance → Fleet tools → Unit Checklists points
// at the Unit Checklists button on Inspections: go there, then scroll to the
// button, focus it and pulse it briefly. It deliberately does NOT open the
// editor itself (COD r1+r2): the editor reads the unit lists this view
// subscribes to, a first snapshot can come from the local cache, and the editor
// never repaints its pickers — an auto-open could show empty or stale unit
// lists. The user's own tap comes after the lists load, as it does today.
// Cancelled by a company switch, sign-out, leaving Inspections (view cleanup)
// or 6s without the button appearing.
let _tplPointTimer = null;
function pointToInspectionTemplates() {
  if (!canManageInspectionTemplates()) {
    window.showToast?.('You need the Maintenance module to manage unit checklists.', 'error');
    return;
  }
  _cancelTplOpen();
  const tenant = window.AppState?.tenant || null;
  const t0 = Date.now();
  if (typeof window.navigateToView === 'function') window.navigateToView('pretrip');
  const tick = () => {
    _tplPointTimer = null;
    if (!tenant || (window.AppState?.tenant || null) !== tenant || !window.AppState?.user) return;
    const btn = document.getElementById('btn-inspection-templates');
    if (btn) {
      try { btn.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (_) { /* old browsers */ }
      try { btn.focus({ preventScroll: true }); } catch (_) { /* not focusable */ }
      const was = btn.style.boxShadow;
      btn.style.boxShadow = '0 0 0 4px rgba(59,130,246,0.45)'; // brand blue ring, no new CSS
      setTimeout(() => { btn.style.boxShadow = was; }, 2400);
      window.showToast?.('Tap Unit Checklists to set up checklists per vehicle type or unit.', 'info');
      return;
    }
    if (Date.now() - t0 > 6000) return;
    _tplPointTimer = setTimeout(tick, 150);
  };
  _tplPointTimer = setTimeout(tick, 0);
}

function _cancelTplOpen() {
  if (_tplPointTimer) clearTimeout(_tplPointTimer);
  _tplPointTimer = null;
}

function closeInspectionTemplates() {
  PretripState.tplDraft = null;
  document.getElementById('modal-inspection-templates')?.classList.add('hidden');
}

function _renderTplList() {
  const body = document.getElementById('tpl-modal-body');
  const title = document.getElementById('tpl-modal-title');
  if (!body) return;
  if (title) title.textContent = 'Unit Checklists';
  const list = _tplList();
  const L = window.InspectionTemplates.LIMITS;
  const choicesByKind = {};
  const rows = list.map(t => {
    const choices = choicesByKind[t.assetKind] || (choicesByKind[t.assetKind] = _tplTypeChoices(t.assetKind));
    const unitScope = t.unitIds.length
      ? `${t.unitIds.length} specific unit${t.unitIds.length === 1 ? '' : 's'} (${t.unitMode === 'add' ? "added to the type's checklist" : "replaces the type's checklist"})`
      : '';
    const scope = [
      t.vehicleTypes.length ? `Types: ${t.vehicleTypes.map(k => escapeHtml(_tplTypeLabel(t.assetKind, k, choices))).join(', ')}` : '',
      unitScope,
    ].filter(Boolean).join(' · ') || 'Not assigned yet';
    const warn = _tplConflictLines(list, t.id).filter(s => s.includes('this checklist is ignored'));
    return `
      <div class="tpl-row" data-tpl-id="${escapeHtml(t.id)}" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding:10px 12px;border:1px solid var(--border-light);border-radius:10px;margin-bottom:8px;">
        <div style="flex:1;min-width:180px;">
          <div style="font-weight:700;color:var(--text-primary);">${escapeHtml(t.name)}${t.active ? '' : ' <span style="font-size:0.75rem;color:var(--text-secondary);">(off)</span>'}</div>
          <div style="font-size:0.8rem;color:var(--text-secondary);">${_TPL_KIND_LABEL[t.assetKind]} · ${t.items.length} item${t.items.length === 1 ? '' : 's'} · ${scope}</div>
          ${warn.map(s => `<div class="tpl-row-warn" style="font-size:0.8rem;color:var(--text-primary);margin-top:2px;"><span class="material-icons" style="font-size:14px;vertical-align:-2px;color:var(--status-warning);">warning</span> ${escapeHtml(s)}</div>`).join('')}
        </div>
        <button type="button" class="btn btn-secondary btn-sm" style="min-height:40px;" onclick="window.PretripModule.downloadInspectionTemplatePdf('${escJsAttr(t.id)}')">
          <span class="material-icons" style="font-size:16px;vertical-align:-3px;">picture_as_pdf</span> Blank PDF
        </button>
        <button type="button" class="btn btn-secondary btn-sm" style="min-height:40px;" onclick="window.PretripModule.editInspectionTemplate('${escJsAttr(t.id)}')">Edit</button>
      </div>`;
  }).join('');
  body.innerHTML = `
    <p style="margin:0 0 12px;color:var(--text-secondary);font-size:0.875rem;">
      Extra items a driver checks on top of the standard DOT inspection, for a vehicle type or for specific units.
      A checklist for specific units is added to their type's checklist, or replaces it if you choose. The DOT items always stay on the form.
    </p>
    ${rows || '<div style="padding:16px;text-align:center;color:var(--text-secondary);">No unit checklists yet.</div>'}
    <div style="display:flex;flex-wrap:wrap;justify-content:space-between;align-items:center;gap:8px;margin-top:12px;">
      <span id="tpl-count" style="font-size:0.8rem;color:var(--text-secondary);">${list.length} of ${L.maxTemplates} checklists · up to ${L.maxItems} items each</span>
      <button type="button" class="btn btn-primary" id="btn-tpl-new" style="min-height:44px;" ${list.length >= L.maxTemplates ? 'disabled title="You have the most checklists allowed. Delete or reuse one."' : ''} onclick="window.PretripModule.editInspectionTemplate('')">
        <span class="material-icons" style="font-size:18px;vertical-align:-4px;">add</span> New Checklist
      </button>
    </div>`;
}

function editInspectionTemplate(id) {
  const existing = id ? _tplList().find(t => t.id === id) : null;
  PretripState.tplDraft = existing
    ? JSON.parse(JSON.stringify(existing))
    // TASK-10135 — a new item is NOT must-pass (out of service only when the
    // office picks it), shows on both inspections, and a new unit checklist
    // adds to its type's checklist.
    : { id: _tplNewId('t'), name: '', assetKind: 'truck', vehicleTypes: [], unitIds: [], unitMode: 'add', items: [_tplNewItem()], active: true, _isNew: true };
  _renderTplEditor();
}

function _tplNewItem() {
  return { id: _tplNewId('i'), label: '', required: false, photoRequired: false, phase: 'both' };
}

function _renderTplEditor() {
  const d = PretripState.tplDraft;
  const body = document.getElementById('tpl-modal-body');
  const title = document.getElementById('tpl-modal-title');
  if (!d || !body) return;
  if (title) title.textContent = d._isNew ? 'New Unit Checklist' : 'Edit Unit Checklist';
  const types = _tplTypeChoices(d.assetKind);
  // A saved type the fleet no longer carries stays visible (and ticked) so a
  // save never silently drops it.
  d.vehicleTypes.forEach(k => { if (!types.some(t => t.key === k)) types.push({ key: k, label: k }); });
  const units = _tplUnitsFor(d.assetKind);
  const unitIds = new Set(d.unitIds);
  const unitLabel = d.assetKind === 'equipment' ? 'equipment' : (d.assetKind === 'trailer' ? 'trailers' : 'vehicles');
  const L = window.InspectionTemplates.LIMITS;
  const unitMode = d.unitMode === 'add' ? 'add' : 'replace';
  const atMax = d.items.length >= L.maxItems;
  body.innerHTML = `
    <div class="form-group">
      <label for="tpl-name">Checklist name *</label>
      <input type="text" id="tpl-name" maxlength="${L.maxNameLen}" placeholder="e.g. Vac unit" value="${escapeHtml(d.name)}">
      <small style="display:block;margin-top:4px;font-size:0.78rem;color:var(--text-secondary);">Up to ${L.maxNameLen} characters.</small>
    </div>
    <div class="form-row">
      <div class="form-group">
        <label for="tpl-kind">Used on</label>
        <select id="tpl-kind" onchange="window.PretripModule.onInspectionTemplateKindChange(this.value)">
          ${['truck', 'trailer', 'equipment'].map(k => `<option value="${k}" ${d.assetKind === k ? 'selected' : ''}>${_TPL_KIND_LABEL[k]}</option>`).join('')}
        </select>
      </div>
      <div class="form-group" style="display:flex;align-items:flex-end;">
        <label style="display:flex;align-items:center;gap:8px;min-height:44px;cursor:pointer;">
          <input type="checkbox" id="tpl-active" ${d.active ? 'checked' : ''} style="width:18px;height:18px;"> On (drivers see it)
        </label>
      </div>
    </div>

    <div class="form-group">
      <label>Every unit of these types</label>
      <div id="tpl-types" style="display:flex;flex-wrap:wrap;gap:6px 14px;">
        ${types.length ? types.map(t => `
          <label style="display:flex;align-items:center;gap:6px;min-height:36px;cursor:pointer;font-size:0.875rem;">
            <input type="checkbox" data-tpl-type="${escapeHtml(t.key)}" ${d.vehicleTypes.includes(t.key) ? 'checked' : ''} style="width:18px;height:18px;"> ${escapeHtml(t.label)}
          </label>`).join('') : `<span style="font-size:0.85rem;color:var(--text-secondary);">No types on file for ${unitLabel}.</span>`}
      </div>
    </div>

    <div class="form-group">
      <label>Specific units</label>
      <div id="tpl-units" style="display:flex;flex-wrap:wrap;gap:6px 14px;max-height:160px;overflow-y:auto;">
        ${units.length ? units.map(u => `
          <label style="display:flex;align-items:center;gap:6px;min-height:36px;cursor:pointer;font-size:0.875rem;">
            <input type="checkbox" data-tpl-unit="${escapeHtml(u.id)}" ${unitIds.has(String(u.id)) ? 'checked' : ''} onchange="window.PretripModule.onInspectionTemplateUnitsChange()" style="width:18px;height:18px;"> ${escapeHtml(_tplUnitLabel(d.assetKind, u))}
          </label>`).join('') : `<span style="font-size:0.85rem;color:var(--text-secondary);">No ${unitLabel} on file.</span>`}
      </div>
      <div id="tpl-unit-mode" role="radiogroup" aria-label="For these units" style="margin-top:8px;${unitIds.size ? '' : 'display:none;'}">
        <div style="font-size:0.8rem;font-weight:600;color:var(--text-primary);margin-bottom:2px;">For the units picked here:</div>
        <label style="display:flex;align-items:center;gap:6px;min-height:36px;cursor:pointer;font-size:0.875rem;">
          <input type="radio" name="tpl-unit-mode" value="add" ${unitMode === 'add' ? 'checked' : ''} style="width:18px;height:18px;"> Add to their type's checklist
        </label>
        <label style="display:flex;align-items:center;gap:6px;min-height:36px;cursor:pointer;font-size:0.875rem;">
          <input type="radio" name="tpl-unit-mode" value="replace" ${unitMode === 'replace' ? 'checked' : ''} style="width:18px;height:18px;"> Replace their type's checklist
        </label>
      </div>
    </div>

    <div class="form-group">
      <label style="display:flex;justify-content:space-between;gap:8px;">
        <span>Items</span>
        <span id="tpl-item-count" style="font-weight:400;font-size:0.8rem;color:var(--text-secondary);">${d.items.length} of ${L.maxItems} items · up to ${L.maxLabelLen} characters each</span>
      </label>
      <div id="tpl-items">
        ${d.items.map((it, i) => `
          <div class="tpl-item-row" data-tpl-item-id="${escapeHtml(it.id)}" style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding:8px;background:var(--bg-secondary);border-radius:8px;margin-bottom:6px;">
            <input type="text" class="tpl-item-label" maxlength="${L.maxLabelLen}" placeholder="Item to check" aria-label="Item to check" value="${escapeHtml(it.label)}" style="flex:1;min-width:160px;">
            <select class="tpl-item-phase" aria-label="Which inspection" style="min-height:36px;font-size:0.8rem;width:auto;">
              ${['both', 'pre', 'post'].map(p => `<option value="${p}" ${window.InspectionTemplates.inspectionItemPhase(it.phase) === p ? 'selected' : ''}>${_TPL_PHASE_LABEL[p]}</option>`).join('')}
            </select>
            <label style="display:flex;align-items:center;gap:4px;font-size:0.8rem;cursor:pointer;min-height:36px;" title="A failed item puts the unit out of service until repaired and signed off">
              <input type="checkbox" class="tpl-item-required" ${it.required ? 'checked' : ''} style="width:18px;height:18px;"> Must pass <span style="color:var(--text-secondary);">(out of service if failed)</span>
            </label>
            <label style="display:flex;align-items:center;gap:4px;font-size:0.8rem;cursor:pointer;min-height:36px;" title="The driver must add a photo when this item fails">
              <input type="checkbox" class="tpl-item-photo" ${it.photoRequired ? 'checked' : ''} style="width:18px;height:18px;"> Photo if failed
            </label>
            <button type="button" class="btn-icon" aria-label="Remove item" onclick="window.PretripModule.removeInspectionTemplateItem(${i})" style="min-width:36px;min-height:36px;">
              <span class="material-icons" style="font-size:18px;">close</span>
            </button>
          </div>`).join('')}
      </div>
      <button type="button" class="btn btn-secondary btn-sm" id="btn-tpl-add-item" style="min-height:40px;" ${atMax ? `disabled title="A checklist can have at most ${L.maxItems} items."` : ''} onclick="window.PretripModule.addInspectionTemplateItem()">
        <span class="material-icons" style="font-size:16px;vertical-align:-3px;">add</span> Add item
      </button>
      <p style="margin:8px 0 0;font-size:0.8rem;color:var(--text-secondary);">
        Leave Must pass off unless a failed item should take the unit out of service (same as a DVIR defect). Otherwise a failed item makes a regular work order.
        Pre-use only / Post-use only items show on that inspection only.
      </p>
    </div>

    <div style="display:flex;flex-wrap:wrap;justify-content:space-between;gap:8px;margin-top:16px;">
      <div>
        ${d._isNew ? '' : `<button type="button" class="btn btn-secondary" style="min-height:44px;color:var(--status-danger);" onclick="window.PretripModule.deleteInspectionTemplate('${escJsAttr(d.id)}')">Delete</button>`}
      </div>
      <div style="display:flex;gap:8px;">
        <button type="button" class="btn btn-secondary" style="min-height:44px;" onclick="window.PretripModule.openInspectionTemplates()">Cancel</button>
        <button type="button" class="btn btn-primary" id="btn-tpl-save" style="min-height:44px;" onclick="window.PretripModule.saveInspectionTemplate()">Save</button>
      </div>
    </div>`;
}

// Pull what is on screen into the draft (so add/remove/kind change keep typing).
function _readTplForm() {
  const d = PretripState.tplDraft;
  if (!d) return null;
  const name = document.getElementById('tpl-name');
  if (name) d.name = name.value;
  const active = document.getElementById('tpl-active');
  if (active) d.active = active.checked;
  const typeBoxes = document.querySelectorAll('#tpl-types input[data-tpl-type]');
  if (typeBoxes.length) d.vehicleTypes = [...typeBoxes].filter(b => b.checked).map(b => b.dataset.tplType);
  const unitBoxes = document.querySelectorAll('#tpl-units input[data-tpl-unit]');
  if (unitBoxes.length) d.unitIds = [...unitBoxes].filter(b => b.checked).map(b => b.dataset.tplUnit);
  const mode = document.querySelector('#tpl-unit-mode input[name="tpl-unit-mode"]:checked');
  if (mode) d.unitMode = mode.value === 'add' ? 'add' : 'replace';
  const rows = document.querySelectorAll('#tpl-items .tpl-item-row');
  if (rows.length || d.items.length === 0) {
    d.items = [...rows].map(r => ({
      id: r.dataset.tplItemId,
      label: r.querySelector('.tpl-item-label')?.value || '',
      required: !!r.querySelector('.tpl-item-required')?.checked,
      photoRequired: !!r.querySelector('.tpl-item-photo')?.checked,
      phase: window.InspectionTemplates.inspectionItemPhase(r.querySelector('.tpl-item-phase')?.value),
    }));
  }
  return d;
}

// COD r1 (UX): the Add / Replace choice only shows once a unit is ticked.
function onInspectionTemplateUnitsChange() {
  const any = [...document.querySelectorAll('#tpl-units input[data-tpl-unit]')].some(b => b.checked);
  const box = document.getElementById('tpl-unit-mode');
  if (box) box.style.display = any ? '' : 'none';
}

function onInspectionTemplateKindChange(kind) {
  const d = _readTplForm();
  if (!d) return;
  // Types + units belong to one kind; switching kind clears them.
  d.assetKind = window.InspectionTemplates.inspectionAssetKind(kind);
  d.vehicleTypes = [];
  d.unitIds = [];
  _renderTplEditor();
}

function addInspectionTemplateItem() {
  const d = _readTplForm();
  if (!d) return;
  if (d.items.length >= window.InspectionTemplates.LIMITS.maxItems) {
    window.showToast?.(`A checklist can have at most ${window.InspectionTemplates.LIMITS.maxItems} items.`, 'error');
    return;
  }
  d.items.push(_tplNewItem());
  _renderTplEditor();
  const inputs = document.querySelectorAll('#tpl-items .tpl-item-label');
  inputs[inputs.length - 1]?.focus();
}

function removeInspectionTemplateItem(index) {
  const d = _readTplForm();
  if (!d) return;
  d.items.splice(index, 1);
  _renderTplEditor();
}

// One transaction on the tenant doc: replace (or append) this template by id in
// the CURRENT stored list, so two editors never overwrite each other's lists.
async function _writeTemplateList(mutate) {
  const tenant = window.AppState?.tenant;
  if (!tenant) throw new Error('no-tenant');
  const tRef = doc(db, 'tenants', tenant);
  return runTransaction(db, async (tx) => {
    const snap = await tx.get(tRef);
    const cur = snap.exists() ? (snap.data()?.settings?.inspectionTemplates) : null;
    const next = mutate(window.InspectionTemplates.normalizeInspectionTemplates(cur));
    tx.update(tRef, { 'settings.inspectionTemplates': next });
    return next;
  });
}

async function saveInspectionTemplate() {
  const d = _readTplForm();
  if (!d) return;
  const IT = window.InspectionTemplates;
  const blankLabels = d.items.filter(it => !String(it.label || '').trim()).length;
  const res = IT.sanitizeInspectionTemplate(d);
  if (!res.ok) {
    window.showToast?.(res.errors.filter(e => e !== 'missing id')[0] || 'Check the checklist and try again.', 'error');
    return;
  }
  // TASK-10135 — two ON checklists naming the same type (or unit): the one
  // listed first is used and the other is ignored. Say so before saving.
  {
    const cur = _tplList();
    const prospective = cur.some(t => t.id === res.template.id)
      ? cur.map(t => (t.id === res.template.id ? res.template : t))
      : [...cur, res.template];
    const lines = _tplConflictLines(prospective, res.template.id);
    if (lines.length && window.showConfirm) {
      const go = await window.showConfirm('Another checklist covers the same units', lines.join(' '), { confirmText: 'Save anyway', cancelText: 'Go back' });
      if (!go) return;
      // The modal may have closed or moved on while the dialog was up.
      if (PretripState.tplDraft !== d) return;
    }
  }
  const btn = document.getElementById('btn-tpl-save');
  if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
  try {
    const next = await _writeTemplateList(list => {
      if (list.some(t => t.id === res.template.id)) {
        return list.map(t => (t.id === res.template.id ? res.template : t));
      }
      // Edited while someone else deleted it: say so, never a silent no-op (COD Code Reviewer).
      if (!d._isNew) { const e = new Error('gone'); e.code = 'gone'; throw e; }
      if (list.length >= IT.LIMITS.maxTemplates) {
        const e = new Error('too-many'); e.code = 'too-many'; throw e;
      }
      return [...list, res.template];
    });
    // Live settings listeners may lag a beat; reflect the write locally now.
    if (window.AppState) window.AppState.settings = { ...(window.AppState.settings || {}), inspectionTemplates: next };
    PretripState.tplDraft = null;
    _renderTplList();
    window.showToast?.(blankLabels ? `Checklist saved (${blankLabels} empty item${blankLabels === 1 ? '' : 's'} left out)` : 'Checklist saved', 'success');
  } catch (err) {
    console.error('[Pretrip] Could not save unit checklist:', err);
    const msg = err?.code === 'gone' ? 'Someone deleted this checklist while you were editing it. Close and start a new one.'
      : err?.code === 'too-many' ? `You can have at most ${IT.LIMITS.maxTemplates} unit checklists.`
      : (err?.code === 'permission-denied' ? 'You need the Maintenance module to change unit checklists.' : 'Could not save the checklist. Check your connection and try again.');
    window.showToast?.(msg, 'error');
  } finally {
    const b = document.getElementById('btn-tpl-save');
    if (b) { b.disabled = false; b.textContent = 'Save'; }
  }
}

async function deleteInspectionTemplate(id) {
  const t = _tplList().find(x => x.id === id);
  if (!t) return;
  const ok = window.showConfirm
    ? await window.showConfirm('Delete this checklist?', `"${t.name}" will no longer show on driver inspections. Inspections already filed keep their answers.`, { confirmText: 'Delete', cancelText: 'Keep it', danger: true })
    : false;
  if (!ok) return;
  try {
    const next = await _writeTemplateList(list => list.filter(x => x.id !== id));
    if (window.AppState) window.AppState.settings = { ...(window.AppState.settings || {}), inspectionTemplates: next };
    PretripState.tplDraft = null;
    _renderTplList();
    window.showToast?.('Checklist deleted', 'success');
  } catch (err) {
    console.error('[Pretrip] Could not delete unit checklist:', err);
    window.showToast?.('Could not delete the checklist. Try again.', 'error');
  }
}

// Server-rendered blank form (company letterhead + DOT base items + this
// checklist's items + signature lines). The server reads the SAVED template.
async function downloadInspectionTemplatePdf(id) {
  const tenant = window.AppState?.tenant;
  if (!tenant || !id) return;
  window.showToast?.('Building the blank form...', 'info');
  try {
    const call = httpsCallable(functions, 'downloadInspectionTemplatePdf');
    const res = await call({ tenantId: tenant, templateId: id });
    const b64 = res?.data?.pdfBase64;
    if (!b64) throw new Error('empty');
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = res.data.fileName || 'inspection-checklist.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
    // TASK-10208 — no logo printed: say where one is added (the uploader shows with Clock & Billing).
    if (res.data.logoDrawn === false) {
      window.showToast?.(window.hasAddOnPackage?.('clockbilling')
        ? 'Tip: add your logo in Settings > Company Logo and it prints on your inspection forms.'
        : 'Your company name prints on this form. To print your logo instead, contact DispatchDeck support.', 'info');
    }
  } catch (err) {
    console.error('[Pretrip] Blank checklist PDF failed:', err);
    window.showToast?.(err?.code === 'functions/not-found' ? 'That checklist is not saved yet.' : 'Could not build the PDF. Try again.', 'error');
  }
}


// ============================================
// EXPOSE TO WINDOW
// ============================================
window.PretripModule = {
  toggleSelectMode,
  toggleSelected,
  toggleSelectAll,
  bulkArchiveSelected: () => bulkSetArchivedSelected(true),
  bulkRestoreSelected: () => bulkSetArchivedSelected(false),
  showNewInspection,
  showNewInspectionFor, // TASK-9372 — Job Board job modal deep link
  onAssetFilterInput,   // TASK-9559 — equipment picker search box
  openInspectionById,   // TASK-9372 — open a listed (possibly crewmate's) inspection
  saveInspection,
  toggleItem,
  markAllChecklistOk,
  toggleChecklistSystem,
  togglePrintedName,
  clear3rdSigCanvas,
  submit3rdSignature,
  showBlankDVIR,
  // TASK-9741 — unit checklists editor + blank PDF
  openInspectionTemplates,
  pointToInspectionTemplates, // TASK-10135 — Maintenance → Fleet tools → Unit Checklists
  canManageInspectionTemplates,    // TASK-10135 — Maintenance shows that menu item only to editors
  editInspectionTemplate,
  closeInspectionTemplates,
  addInspectionTemplateItem,
  removeInspectionTemplateItem,
  onInspectionTemplateKindChange,
  saveInspectionTemplate,
  deleteInspectionTemplate,
  downloadInspectionTemplatePdf,
  onInspectionTypeChange, // TASK-10135 — Pre-Trip / Post-Trip re-picks the unit checks
  onInspectionTemplateUnitsChange, // TASK-10135 — show Add / Replace once a unit is ticked
  filterInspections,
  searchInspections,
  loadOlderInspections,
  viewInspection,
  printInspection,
  openInspectionExport, // TASK-10208
  deleteInspection,
  setInspectionArchived,
  closeModal,
  handlePhotoCapture,
  removePhoto,
  showFullPhoto,
  onAssetTypeChange,
  onAssetSelected,
  checkReturnQtyWarning,
  clearSignature,
  toggleNoSignature,
  switchDvirTab,
  openSafetyMeetingSignIn,
  openPpe,
  toggleDvirOilFields,
  addDvirFluid,
  removeDvirFluid,
  clearSignoffCanvas,
  submitDvirSignoff,
  // TASK-9554 — maintenance.js's source-DVIR modal renders the same line (Pitfall #1: via window, not import)
  inspectionLocationLineHTML
};

export default { initPretrip };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
